import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Page =
  | "dashboard"
  | "calendar"
  | "clients"
  | "pets"
  | "records"
  | "billing"
  | "inventory"
  | "tasks"
  | "marketing"
  | "reports"
  | "settings";

type ClientStatus = "פעיל" | "חדש" | "לא פעיל";
type AppointmentStatus = "ממתין" | "הגיע" | "בטיפול" | "הושלם" | "בוטל";
type InvoiceStatus =
  | "טיוטה"
  | "נשלחה"
  | "חוב פתוח"
  | "שולמה"
  | "מבוטלת";
type InvoiceDocumentType =
  | "חשבונית מס"
  | "חשבונית מס קבלה"
  | "קבלה"
  | "דרישת תשלום"
  | "זיכוי";
type TaskStatus = "פתוחה" | "בוצעה";

type ClientRecord = {
  id: string;
  name: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  status: ClientStatus;
  joinDate: string;
  preferredContact: string;
  taxId: string;
  paymentTerms: number;
  notes: string;
};

type PetRecord = {
  id: string;
  ownerId: string;
  name: string;
  species: string;
  breed: string;
  age: number;
  sex: string;
  chip: string;
  weight: number;
  allergies: string;
  status: string;
  lastVisit: string;
  nextVaccine: string;
  notes: string;
};

type Appointment = {
  id: string;
  date: string;
  time: string;
  duration: number;
  type: string;
  petId: string;
  ownerId: string;
  doctor: string;
  room: string;
  status: AppointmentStatus;
  price: number;
  notes: string;
  reminderAt?: string;
  reminderNote?: string;
};

type MedicalRecord = {
  id: string;
  petId: string;
  date: string;
  type: string;
  doctor: string;
  title: string;
  diagnosis: string;
  treatment: string;
  nextSteps: string;
  price: number;
  attachments: MedicalAttachment[];
};

type MedicalAttachment = {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedAt: string;
};

type InvoiceItem = { id?: string; desc: string; qty: number; price: number };

type Invoice = {
  id: string;
  date: string;
  dueDate: string;
  paidDate: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientAddress: string;
  clientTaxId: string;
  appointmentId?: string;
  status: InvoiceStatus;
  documentType: InvoiceDocumentType;
  items: InvoiceItem[];
  vatRate: number;
  vatExempt: boolean;
  paid: number;
  paymentMethod: string;
  notes: string;
};

type InventoryItem = {
  id: string;
  name: string;
  category: string;
  stock: number;
  minStock: number;
  unit: string;
  expiry: string;
  supplier: string;
  price: number;
};

type ClinicTask = {
  id: string;
  text: string;
  dueDate: string;
  priority: "דחופה" | "גבוהה" | "רגילה" | "נמוכה";
  owner: string;
  status: TaskStatus;
  petId: string;
  reminderAt?: string;
  reminderNote?: string;
};

type MarketingCampaignStatus = "טיוטה" | "בבדיקה" | "פעיל" | "מושהה";

type MarketingCampaign = {
  id: string;
  name: string;
  platform: string;
  objective: string;
  audience: string;
  offer: string;
  landingPage: string;
  keywords: string[];
  budget: number;
  spent: number;
  results: number;
  status: MarketingCampaignStatus;
  roas: number;
  startDate: string;
  endDate: string;
  leads?: number;
  appointments?: number;
  revenue?: number;
  nextAction?: string;
  nextActionDate?: string;
};

type MarketingAsset = {
  id: string;
  campaignId: string;
  type: string;
  title: string;
  format: string;
  status: "טיוטה" | "בבדיקה" | "מוכן" | "פורסם";
  tone: "mint" | "coral" | "purple" | "amber";
  headline: string;
  body: string;
  cta: string;
};

type MarketingAudience = {
  id: string;
  name: string;
  description: string;
  size: number;
  channel: string;
  criteria: string[];
  opportunity: string;
};

type MarketingConnection = {
  name: string;
  icon: string;
  connected: boolean;
  account: string;
  lastSync: string;
};

type Clinic = {
  name: string;
  doctor: string;
  phone: string;
  email: string;
  address: string;
  defaultDoctor: string;
  reminderDays: number;
  vat: number;
  taxId: string;
  bank: string;
  invoicePrefix: string;
  nextInvoiceNumber: number;
};

type AppData = {
  clinic: Clinic;
  clients: ClientRecord[];
  pets: PetRecord[];
  appointments: Appointment[];
  records: MedicalRecord[];
  invoices: Invoice[];
  inventory: InventoryItem[];
  tasks: ClinicTask[];
  marketingCampaigns: MarketingCampaign[];
  marketingAssets: MarketingAsset[];
  marketingAudiences: MarketingAudience[];
  marketingConnections: MarketingConnection[];
};

type ModalState =
  | { type: "client"; id?: string }
  | { type: "pet"; id?: string }
  | { type: "appointment"; id?: string; date?: string }
  | { type: "record"; id?: string; petId?: string }
  | { type: "invoice"; id?: string; clientId?: string; appointmentId?: string }
  | { type: "inventory"; id?: string }
  | { type: "task"; id?: string }
  | { type: "client-detail"; id: string }
  | { type: "pet-detail"; id: string }
  | { type: "invoice-detail"; id: string; autoPrint?: boolean }
  | null;

const STORE_KEY = "vetmanage-pro-v3";
const VAT_18_MIGRATION_KEY = "vetmanage-vat-18-applied";

async function loadAppState() {
  const response = await fetch("/api/app-data");
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(
      payload?.error || `לא ניתן לטעון נתונים מהשרת (${response.status})`,
    );
  }
  return response.json() as Promise<{
    data?: AppData | null;
    loggedIn?: boolean;
    theme?: "light" | "dark";
  }>;
}

async function saveAppState(payload: {
  data: AppData;
  loggedIn: boolean;
  theme: "light" | "dark";
}) {
  const response = await fetch("/api/app-data", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(
      payload?.error || `לא ניתן לשמור נתונים (${response.status})`,
    );
  }
  return response.json();
}

function localDateISO(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function localDateTimeInput(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function friendlyDateTime(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("he-IL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function addDays(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateISO(date);
}

function addDaysFrom(value: string, days: number) {
  const date = new Date(`${value || localDateISO()}T12:00:00`);
  date.setDate(date.getDate() + Number(days || 0));
  return localDateISO(date);
}

function uid(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 6)}`;
}

function money(value: number) {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function friendlyDate(value: string) {
  if (!value) return "לא נקבע";
  return new Intl.DateTimeFormat("he-IL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00`));
}

function roundMoney(value: number) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function seedState(): AppData {
  return {
    clinic: {
      name: "VetManage Pro",
      doctor: "ד״ר פרידה כהן",
      phone: "03-555-0199",
      email: "clinic@example.co.il",
      address: "הברזל 12, תל אביב",
      defaultDoctor: "ד״ר פרידה כהן",
      reminderDays: 14,
      vat: 18,
      taxId: "515555555",
      bank: "בנק 12 · סניף 345 · חשבון 678901",
      invoicePrefix: "INV",
      nextInvoiceNumber: 1043,
    },
    clients: [
      {
        id: "c1",
        name: "ישראל ישראלי",
        phone: "050-1112233",
        email: "israel@example.com",
        city: "תל אביב",
        address: "דיזנגוף 120",
        status: "פעיל",
        joinDate: addDays(-190),
        preferredContact: "WhatsApp",
        taxId: "012345678",
        paymentTerms: 14,
        notes: "לקוח קבוע, מעדיף הודעות בשעות הערב",
      },
      {
        id: "c2",
        name: "דוד כהן",
        phone: "052-2223344",
        email: "david@example.com",
        city: "רמת גן",
        address: "ביאליק 17",
        status: "פעיל",
        joinDate: addDays(-130),
        preferredContact: "טלפון",
        taxId: "023456789",
        paymentTerms: 30,
        notes: "רגיש לשעות בוקר",
      },
      {
        id: "c3",
        name: "שרה לוי",
        phone: "054-3334455",
        email: "sara@example.com",
        city: "גבעתיים",
        address: "ויצמן 6",
        status: "חדש",
        joinDate: addDays(-14),
        preferredContact: "SMS",
        taxId: "034567890",
        paymentTerms: 14,
        notes: "הגיעה מהמלצה",
      },
      {
        id: "c4",
        name: "רחל אלי",
        phone: "058-4445566",
        email: "rachel@example.com",
        city: "חולון",
        address: "סוקולוב 22",
        status: "פעיל",
        joinDate: addDays(-250),
        preferredContact: "WhatsApp",
        taxId: "045678901",
        paymentTerms: 30,
        notes: "בעלת שני כלבים",
      },
    ],
    pets: [
      {
        id: "p1",
        ownerId: "c1",
        name: "מיצי",
        species: "חתול",
        breed: "מעורב",
        age: 4,
        sex: "נקבה",
        chip: "985141000001",
        weight: 4.2,
        allergies: "אין",
        status: "פעיל",
        lastVisit: addDays(-20),
        nextVaccine: addDays(15),
        notes: "מפחדת מכלובים",
      },
      {
        id: "p2",
        ownerId: "c2",
        name: "רקס",
        species: "כלב",
        breed: "לברדור",
        age: 7,
        sex: "זכר",
        chip: "985141000002",
        weight: 28,
        allergies: "פניצילין",
        status: "פעיל",
        lastVisit: addDays(-4),
        nextVaccine: addDays(45),
        notes: "בדיקת מעבדה פתוחה",
      },
      {
        id: "p3",
        ownerId: "c3",
        name: "בלה",
        species: "כלב",
        breed: "שיצו",
        age: 3,
        sex: "נקבה",
        chip: "985141000003",
        weight: 6.4,
        allergies: "אין",
        status: "פעיל",
        lastVisit: addDays(-40),
        nextVaccine: addDays(3),
        notes: "טיפול שיניים מתוכנן",
      },
      {
        id: "p4",
        ownerId: "c1",
        name: "שוקו",
        species: "ארנב",
        breed: "ננסי",
        age: 2,
        sex: "זכר",
        chip: "",
        weight: 1.7,
        allergies: "אין",
        status: "פעיל",
        lastVisit: addDays(-70),
        nextVaccine: addDays(80),
        notes: "בדיקות ציפורניים",
      },
      {
        id: "p5",
        ownerId: "c4",
        name: "לאקי",
        species: "כלב",
        breed: "פודל",
        age: 8,
        sex: "זכר",
        chip: "985141000004",
        weight: 9.3,
        allergies: "אבק",
        status: "פעיל",
        lastVisit: addDays(-10),
        nextVaccine: addDays(-2),
        notes: "חיסון באיחור",
      },
    ],
    appointments: [
      {
        id: "a1",
        date: localDateISO(),
        time: "08:00",
        duration: 30,
        type: "חיסון",
        petId: "p1",
        ownerId: "c1",
        doctor: "ד״ר פרידה כהן",
        room: "חדר 1",
        status: "הגיע",
        price: 180,
        notes: "חיסון מרובע",
      },
      {
        id: "a2",
        date: localDateISO(),
        time: "09:30",
        duration: 45,
        type: "בדיקה כללית",
        petId: "p2",
        ownerId: "c2",
        doctor: "ד״ר פרידה כהן",
        room: "חדר 2",
        status: "ממתין",
        price: 260,
        notes: "מעקב תוצאות מעבדה",
      },
      {
        id: "a3",
        date: localDateISO(),
        time: "11:00",
        duration: 75,
        type: "טיפול שיניים",
        petId: "p3",
        ownerId: "c3",
        doctor: "ד״ר רועי לוין",
        room: "ניתוח",
        status: "בטיפול",
        price: 850,
        notes: "ניקוי אבנית",
      },
      {
        id: "a4",
        date: localDateISO(),
        time: "13:00",
        duration: 20,
        type: "גזירת ציפורניים",
        petId: "p4",
        ownerId: "c1",
        doctor: "ד״ר רועי לוין",
        room: "חדר 1",
        status: "ממתין",
        price: 90,
        notes: "",
      },
      {
        id: "a5",
        date: addDays(1),
        time: "10:00",
        duration: 35,
        type: "בדיקת דם",
        petId: "p5",
        ownerId: "c4",
        doctor: "ד״ר פרידה כהן",
        room: "מעבדה",
        status: "ממתין",
        price: 320,
        notes: "ספירת דם",
      },
      {
        id: "a6",
        date: addDays(-1),
        time: "16:30",
        duration: 30,
        type: "ייעוץ תזונה",
        petId: "p2",
        ownerId: "c2",
        doctor: "ד״ר פרידה כהן",
        room: "חדר 1",
        status: "הושלם",
        price: 210,
        notes: "סיכום נשלח במייל",
      },
    ],
    records: [
      {
        id: "r1",
        petId: "p2",
        date: addDays(-4),
        type: "מעבדה",
        doctor: "ד״ר פרידה כהן",
        title: "בדיקת דם לרקס",
        diagnosis: "חשד לדלקת קלה",
        treatment: "נלקחה דגימת דם ונשלחה למעבדה",
        nextSteps: "לחזור ללקוח עם תוצאות",
        price: 320,
        attachments: [],
      },
      {
        id: "r2",
        petId: "p1",
        date: addDays(-20),
        type: "חיסון",
        doctor: "ד״ר פרידה כהן",
        title: "חיסון תקופתי למיצי",
        diagnosis: "בריאה",
        treatment: "חיסון מרובע",
        nextSteps: "תזכורת בעוד שנה",
        price: 180,
        attachments: [],
      },
      {
        id: "r3",
        petId: "p3",
        date: addDays(-40),
        type: "שיניים",
        doctor: "ד״ר רועי לוין",
        title: "אבחון שיניים לבלה",
        diagnosis: "אבנית בינונית",
        treatment: "הומלץ ניקוי",
        nextSteps: "קביעת טיפול",
        price: 230,
        attachments: [],
      },
    ],
    invoices: [
      {
        id: "INV-1042",
        date: localDateISO(),
        dueDate: addDays(14),
        paidDate: localDateISO(),
        clientId: "c1",
        clientName: "ישראל ישראלי",
        clientEmail: "israel@example.com",
        clientAddress: "דיזנגוף 120, תל אביב",
        clientTaxId: "012345678",
        status: "שולמה",
        documentType: "חשבונית מס קבלה",
        items: [{ desc: "חיסון מרובע", qty: 1, price: 180 }],
        vatRate: 0,
        vatExempt: true,
        paid: 180,
        paymentMethod: "אשראי",
        notes: "",
      },
      {
        id: "INV-1041",
        date: addDays(-4),
        dueDate: addDays(10),
        paidDate: "",
        clientId: "c2",
        clientName: "דוד כהן",
        clientEmail: "david@example.com",
        clientAddress: "ביאליק 17, רמת גן",
        clientTaxId: "023456789",
        status: "חוב פתוח",
        documentType: "חשבונית מס",
        items: [
          { desc: "בדיקת דם", qty: 1, price: 320 },
          { desc: "ייעוץ", qty: 1, price: 210 },
        ],
        vatRate: 0,
        vatExempt: true,
        paid: 210,
        paymentMethod: "אשראי",
        notes: "יתרה לתשלום בביקור הבא",
      },
      {
        id: "INV-1040",
        date: addDays(-12),
        dueDate: addDays(2),
        paidDate: addDays(-12),
        clientId: "c3",
        clientName: "שרה לוי",
        clientEmail: "sara@example.com",
        clientAddress: "ויצמן 6, גבעתיים",
        clientTaxId: "034567890",
        status: "שולמה",
        documentType: "קבלה",
        items: [{ desc: "אבחון שיניים", qty: 1, price: 230 }],
        vatRate: 0,
        vatExempt: true,
        paid: 230,
        paymentMethod: "ביט",
        notes: "",
      },
    ],
    inventory: [
      {
        id: "m1",
        name: "חיסון כלבת",
        category: "חיסונים",
        stock: 18,
        minStock: 10,
        unit: "מנות",
        expiry: addDays(120),
        supplier: "VetSupply",
        price: 62,
      },
      {
        id: "m2",
        name: "חיסון מרובע חתולים",
        category: "חיסונים",
        stock: 6,
        minStock: 12,
        unit: "מנות",
        expiry: addDays(90),
        supplier: "BioVet",
        price: 74,
      },
      {
        id: "m3",
        name: "כפפות ניטריל",
        category: "ציוד מתכלה",
        stock: 280,
        minStock: 120,
        unit: "יחידות",
        expiry: "",
        supplier: "MediPack",
        price: 0.7,
      },
      {
        id: "m4",
        name: "מזון רפואי כליות",
        category: "מזון רפואי",
        stock: 4,
        minStock: 5,
        unit: "שקים",
        expiry: addDays(220),
        supplier: "PetFood Pro",
        price: 145,
      },
    ],
    tasks: [
      {
        id: "t1",
        text: "לחזור למשפחת כהן לגבי תוצאות המעבדה של רקס",
        dueDate: localDateISO(),
        priority: "דחופה",
        owner: "ד״ר פרידה",
        status: "פתוחה",
        petId: "p2",
      },
      {
        id: "t2",
        text: "להזמין מלאי חיסון מרובע חתולים",
        dueDate: addDays(1),
        priority: "גבוהה",
        owner: "מנהלת המרפאה",
        status: "פתוחה",
        petId: "",
      },
      {
        id: "t3",
        text: "לשלוח תזכורות תילוע לחודש הבא",
        dueDate: addDays(-1),
        priority: "רגילה",
        owner: "מזכירות",
        status: "בוצעה",
        petId: "",
      },
    ],
    marketingCampaigns: [
      {
        id: "mc1",
        name: "חיסוני קיץ — תזכורת וקביעת תור",
        platform: "Meta",
        objective: "קביעת תורים",
        audience: "בעלי כלבים וחתולים עד 12 ק״מ מהמרפאה",
        offer: "בדיקת בריאות קצרה ללא עלות עם החיסון",
        landingPage: "/campaigns/summer-vaccines",
        keywords: ["חיסון לכלב", "חיסון לחתול", "וטרינר בתל אביב"],
        budget: 120,
        spent: 1940,
        results: 96,
        status: "פעיל",
        roas: 5.2,
        startDate: addDays(-18),
        endDate: addDays(24),
        leads: 96,
        appointments: 43,
        revenue: 10088,
        nextAction: "לאשר גרסת מודעה למשפחות צעירות",
        nextActionDate: addDays(1),
      },
      {
        id: "mc2",
        name: "בדיקות סניורים — חיפוש בכוונה גבוהה",
        platform: "Google",
        objective: "לידים איכותיים",
        audience: "בעלי כלבים וחתולים מעל גיל 8 באזור המרכז",
        offer: "חבילת בדיקות סניור במחיר היכרות",
        landingPage: "/campaigns/senior-check",
        keywords: ["בדיקות לכלב מבוגר", "וטרינר לחתול מבוגר", "בדיקות דם לחיות"],
        budget: 95,
        spent: 1620,
        results: 71,
        status: "פעיל",
        roas: 4.8,
        startDate: addDays(-30),
        endDate: addDays(14),
        leads: 71,
        appointments: 38,
        revenue: 7776,
        nextAction: "להרחיב מילות מפתח לבדיקות דם",
        nextActionDate: addDays(2),
      },
      {
        id: "mc3",
        name: "חודש בריאות השיניים",
        platform: "Instagram",
        objective: "מודעות וקביעת תורים",
        audience: "לקוחות קיימים וקהל דומה של בעלי חיות",
        offer: "בדיקת שיניים ראשונית ב־50% הנחה",
        landingPage: "/campaigns/dental-month",
        keywords: ["בריאות הפה", "ניקוי שיניים", "ריח פה"],
        budget: 80,
        spent: 1260,
        results: 51,
        status: "פעיל",
        roas: 3.9,
        startDate: addDays(-12),
        endDate: addDays(18),
        leads: 51,
        appointments: 19,
        revenue: 4914,
        nextAction: "לפרסם סרטון לפני ואחרי טיפול",
        nextActionDate: addDays(4),
      },
      {
        id: "mc4",
        name: "חוזרים לביקורת",
        platform: "WhatsApp",
        objective: "שימור לקוחות",
        audience: "לקוחות שלא ביקרו במרפאה מעל 6 חודשים",
        offer: "ייעוץ מעקב קצר ללא חיוב",
        landingPage: "",
        keywords: ["שימור", "חזרה למרפאה"],
        budget: 35,
        spent: 420,
        results: 34,
        status: "מושהה",
        roas: 2.7,
        startDate: addDays(-42),
        endDate: addDays(7),
        leads: 34,
        appointments: 12,
        revenue: 1134,
        nextAction: "לעבור על תגובות ולחדש את הקהל",
        nextActionDate: localDateISO(),
      },
    ],
    marketingAssets: [
      {
        id: "ma1",
        campaignId: "mc2",
        type: "דף נחיתה",
        title: "בדיקת סניור בראש שקט",
        format: "Desktop + Mobile",
        status: "פורסם",
        tone: "purple",
        headline: "הם מתבגרים. אנחנו נשארים צעד אחד קדימה.",
        body: "חבילת בדיקות מקיפה לכלבים וחתולים מגיל 8, עם תשובות ברורות וליווי אישי.",
        cta: "קביעת בדיקה",
      },
      {
        id: "ma2",
        campaignId: "mc3",
        type: "פוסט Instagram",
        title: "חיוך בריא מתחיל בבדיקה",
        format: "1080×1080",
        status: "מוכן",
        tone: "coral",
        headline: "גם לחיוך שלהם מגיע טיפול",
        body: "חודש בריאות השיניים ב־VetManage: בדיקה ראשונית בחצי מחיר.",
        cta: "לפרטים וקביעת תור",
      },
      {
        id: "ma3",
        campaignId: "mc1",
        type: "הודעת WhatsApp",
        title: "תזכורת חיסון אישית",
        format: "הודעה קצרה",
        status: "בבדיקה",
        tone: "mint",
        headline: "הגיע הזמן לחיסון השנתי של {{pet_name}}",
        body: "שמנו לב שהחיסון מתקרב. אפשר לבחור זמן נוח בקישור ונסגור הכול בדקה.",
        cta: "בחירת תור",
      },
      {
        id: "ma4",
        campaignId: "mc2",
        type: "מודעת Google",
        title: "בדיקות לחיות מבוגרות",
        format: "Responsive Search",
        status: "פורסם",
        tone: "amber",
        headline: "בדיקות סניור לכלבים וחתולים",
        body: "אבחון מוקדם, צוות מנוסה ותוצאות מוסברות בגובה העיניים.",
        cta: "קביעת בדיקה היום",
      },
    ],
    marketingAudiences: [
      {
        id: "aud1",
        name: "חיסונים קרובים",
        description: "לקוחות עם חיסון צפוי ב־45 הימים הקרובים",
        size: 138,
        channel: "WhatsApp + Meta",
        criteria: ["מטופל פעיל", "חיסון תוך 45 יום", "פרטי קשר תקינים"],
        opportunity: "פוטנציאל ל־62 תורים",
      },
      {
        id: "aud2",
        name: "בעלי חיות סניור",
        description: "כלבים וחתולים מגיל 8 ללא בדיקות דם בשנה האחרונה",
        size: 84,
        channel: "Google + Email",
        criteria: ["גיל 8+", "לא בוצעה בדיקת דם", "אזור המרכז"],
        opportunity: "קהל בעל צורך רפואי ברור",
      },
      {
        id: "aud3",
        name: "לקוחות לחידוש קשר",
        description: "לקוחות שלא ביקרו במרפאה מעל 6 חודשים",
        size: 216,
        channel: "WhatsApp",
        criteria: ["לקוח פעיל בעבר", "180+ ימים מביקור", "ללא תור עתידי"],
        opportunity: "34 לקוחות הגיבו בסבב הקודם",
      },
      {
        id: "aud4",
        name: "קהילה חדשה באזור",
        description: "קהל דומה ללקוחות המרוצים ביותר של המרפאה",
        size: 4300,
        channel: "Meta + Instagram",
        criteria: ["עד 12 ק״מ", "עניין בחיות מחמד", "דומה ללקוחות חוזרים"],
        opportunity: "מתאים להגדלת חשיפה",
      },
    ],
    marketingConnections: [
      {
        name: "Meta Ads",
        icon: "M",
        connected: true,
        account: "VetManage Clinic",
        lastSync: "לפני 8 דקות",
      },
      {
        name: "Google Ads",
        icon: "G",
        connected: true,
        account: "VetManage Search",
        lastSync: "לפני 12 דקות",
      },
      {
        name: "Instagram",
        icon: "I",
        connected: true,
        account: "@vetmanage_clinic",
        lastSync: "לפני 18 דקות",
      },
      {
        name: "WhatsApp Business",
        icon: "W",
        connected: true,
        account: "03-555-0199",
        lastSync: "לפני 5 דקות",
      },
      {
        name: "Email",
        icon: "@",
        connected: false,
        account: "",
        lastSync: "טרם חובר",
      },
    ],
  };
}

function normalizeData(value: AppData): AppData {
  const defaults = seedState();
  const clinic = { ...defaults.clinic, ...(value.clinic || {}) };
  const clients = (value.clients || defaults.clients).map((client) => ({
    ...client,
    taxId: client.taxId || "",
    paymentTerms: Number(client.paymentTerms ?? 30),
  }));
  const invoices = (value.invoices || defaults.invoices).map((invoice) => {
    const client = clients.find((item) => item.id === invoice.clientId);
    return {
      ...invoice,
      dueDate: invoice.dueDate || invoice.date || localDateISO(),
      paidDate:
        invoice.paidDate ||
        (invoice.status === "שולמה" ? invoice.date || localDateISO() : ""),
      clientName: invoice.clientName || client?.name || "",
      clientEmail: invoice.clientEmail || client?.email || "",
      clientAddress:
        invoice.clientAddress ||
        [client?.address, client?.city].filter(Boolean).join(", "),
      clientTaxId: invoice.clientTaxId || client?.taxId || "",
      documentType: invoice.documentType || "חשבונית מס",
      vatRate:
        typeof invoice.vatRate === "number" ? invoice.vatRate : 0,
      vatExempt:
        typeof invoice.vatExempt === "boolean" ? invoice.vatExempt : true,
      items: (invoice.items || []).map((item) => ({
        ...item,
        id: item.id || uid("line"),
      })),
    };
  });
  const records = (value.records || defaults.records).map((record) => ({
    ...record,
    attachments: record.attachments || [],
  }));
  const appointments = (value.appointments || defaults.appointments).map(
    (appointment) => ({
      ...appointment,
      reminderAt: appointment.reminderAt || "",
      reminderNote: appointment.reminderNote || "",
    }),
  );
  const tasks = (value.tasks || defaults.tasks).map((task) => ({
    ...task,
    reminderAt: task.reminderAt || "",
    reminderNote: task.reminderNote || "",
  }));
  const marketingCampaigns = (
    value.marketingCampaigns ?? defaults.marketingCampaigns
  ).map((campaign) => ({
    ...campaign,
    leads: Number(campaign.leads ?? campaign.results ?? 0),
    appointments: Number(
      campaign.appointments ?? Math.round(Number(campaign.results || 0) * 0.45),
    ),
    revenue: Number(
      campaign.revenue ??
        Number(campaign.spent || 0) * Number(campaign.roas || 0),
    ),
    nextAction: campaign.nextAction || "",
    nextActionDate: campaign.nextActionDate || "",
  }));
  return {
    ...defaults,
    ...value,
    clinic,
    clients,
    invoices,
    records,
    appointments,
    tasks,
    marketingCampaigns,
    marketingAssets: value.marketingAssets ?? defaults.marketingAssets,
    marketingAudiences: value.marketingAudiences ?? defaults.marketingAudiences,
    marketingConnections:
      value.marketingConnections ?? defaults.marketingConnections,
  };
}

const navItems: Array<{
  id: Page;
  label: string;
  icon: string;
  hint: string;
}> = [
  { id: "dashboard", label: "לוח בקרה", icon: "⌂", hint: "תמונת מצב" },
  { id: "calendar", label: "יומן תורים", icon: "◫", hint: "תורים וחדרים" },
  { id: "clients", label: "לקוחות", icon: "◎", hint: "בעלים ופרטי קשר" },
  { id: "pets", label: "בעלי חיים", icon: "♣", hint: "מטופלים ותיקים" },
  { id: "records", label: "רפואה", icon: "+", hint: "אבחנות וטיפולים" },
  { id: "billing", label: "חיובים", icon: "₪", hint: "חשבוניות וגבייה" },
  { id: "inventory", label: "מלאי", icon: "□", hint: "ציוד ותרופות" },
  { id: "tasks", label: "משימות", icon: "✓", hint: "צוות ומעקב" },
  { id: "marketing", label: "שיווק", icon: "✦", hint: "קמפיינים ותוכן" },
  { id: "reports", label: "דוחות", icon: "↗", hint: "נתונים ותובנות" },
  { id: "settings", label: "הגדרות", icon: "⚙", hint: "מרפאה ונתונים" },
];

const pageIds = new Set<Page>(navItems.map((item) => item.id));

function pagePath(page: Page) {
  return page === "dashboard" ? "/" : `/${page}`;
}

function pageFromPath(pathname = window.location.pathname): Page {
  const segment = pathname.replace(/\/+$/, "").split("/").filter(Boolean)[0] || "";
  if (!segment || segment === "index.php" || segment === "index.html") {
    return "dashboard";
  }
  return pageIds.has(segment as Page) ? (segment as Page) : "dashboard";
}

const statusTone: Record<string, string> = {
  פעיל: "success",
  חדש: "info",
  "לא פעיל": "neutral",
  ממתין: "warning",
  הגיע: "info",
  בטיפול: "purple",
  הושלם: "success",
  בוטל: "neutral",
  טיוטה: "neutral",
  נשלחה: "info",
  "חוב פתוח": "danger",
  שולמה: "success",
  פתוחה: "warning",
  בוצעה: "success",
  דחופה: "danger",
  גבוהה: "warning",
  רגילה: "info",
  נמוכה: "neutral",
  מושהה: "warning",
  בבדיקה: "info",
  מוכן: "purple",
  פורסם: "success",
  מבוטלת: "danger",
  באיחור: "danger",
};

function invoiceSubtotal(invoice: Invoice) {
  return roundMoney(
    invoice.items.reduce(
    (sum, item) => sum + Number(item.qty) * Number(item.price),
    0,
    ),
  );
}

function invoiceVat(invoice: Invoice) {
  if (invoice.vatExempt) return 0;
  return roundMoney(invoiceSubtotal(invoice) * Number(invoice.vatRate || 0));
}

function invoiceTotal(invoice: Invoice) {
  return roundMoney(invoiceSubtotal(invoice) + invoiceVat(invoice));
}

function invoiceBalance(invoice: Invoice) {
  if (invoice.status === "מבוטלת") return 0;
  return Math.max(
    0,
    roundMoney(invoiceTotal(invoice) - Number(invoice.paid)),
  );
}

function nextInvoiceIdentity(clinic: Clinic, invoices: Invoice[]) {
  const prefix = clinic.invoicePrefix.trim() || "INV";
  const existingIds = new Set(invoices.map((invoice) => invoice.id));
  let number = Math.max(1, Math.floor(Number(clinic.nextInvoiceNumber) || 1));
  let id = `${prefix}-${number}`;
  while (existingIds.has(id)) {
    number += 1;
    id = `${prefix}-${number}`;
  }
  return { id, nextNumber: number + 1 };
}

function invoiceDisplayStatus(invoice: Invoice) {
  if (
    invoice.status !== "שולמה" &&
    invoice.status !== "מבוטלת" &&
    invoice.dueDate &&
    invoice.dueDate < localDateISO() &&
    invoiceBalance(invoice) > 0
  ) {
    return "באיחור";
  }
  return invoice.status;
}

function printInvoiceDocument(
  elementId: string,
  title: string,
) {
  const source = document.getElementById(elementId);
  if (!source || typeof window.print !== "function") return false;
  const previousTitle = document.title;
  const restorePage = () => {
    document.body.classList.remove("invoice-printing");
    document.title = previousTitle;
  };
  document.title = title.replace(/[<>&"]/g, "");
  document.body.classList.add("invoice-printing");
  window.addEventListener("afterprint", restorePage, { once: true });
  try {
    window.focus();
    window.print();
    window.setTimeout(restorePage, 0);
    return true;
  } catch {
    restorePage();
    return false;
  }
}

async function syncReminder(
  entityType: "appointment" | "task",
  entityId: string,
  remindAt: string,
  message: string,
) {
  const url = new URL("/api/reminders", window.location.origin);
  if (!remindAt) {
    url.searchParams.set("entityType", entityType);
    url.searchParams.set("entityId", entityId);
    const response = await fetch(url, { method: "DELETE" });
    if (!response.ok && response.status !== 404) {
      throw new Error("Reminder removal failed");
    }
    return;
  }
  const response = await fetch(url, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ entityType, entityId, remindAt, message }),
  });
  if (!response.ok) throw new Error("Reminder synchronization failed");
}

function Field({
  label,
  children,
  full,
  hint,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
  hint?: string;
}) {
  return (
    <label className={`field ${full ? "field-full" : ""}`}>
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function Pill({ children, tone = "neutral" }: { children: ReactNode; tone?: string }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

function MedicalThumbnail({
  attachment,
}: {
  attachment: MedicalAttachment;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="medical-thumbnail-fallback" role="img" aria-label="תמונה לא זמינה">
        ▧
      </span>
    );
  }
  return (
    // Private medical uploads are already served through the app's protected image route.
    <img
      src={attachment.url}
      alt={attachment.name}
      width={180}
      height={120}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function EmptyState({
  icon = "◇",
  title,
  text,
  action,
}: {
  icon?: string;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <strong>{title}</strong>
      <p>{text}</p>
      {action}
    </div>
  );
}

function CardHeader({
  title,
  eyebrow,
  action,
}: {
  title: string;
  eyebrow?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card-header">
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h3>{title}</h3>
      </div>
      {action}
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState<AppData>(() => seedState());
  const [loggedIn, setLoggedIn] = useState(false);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState<Page>(() => pageFromPath());
  const [modal, setModal] = useState<ModalState>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [toast, setToast] = useState("");
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [calendarDate, setCalendarDate] = useState(localDateISO());
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [filters, setFilters] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onPopState = () => setPage(pageFromPath());
    window.addEventListener("popstate", onPopState);
    const current = pageFromPath();
    const expected = pagePath(current);
    if (
      window.location.pathname.replace(/\/+$/, "") !==
      expected.replace(/\/+$/, "")
    ) {
      window.history.replaceState({ page: current }, "", expected);
    }
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const parsed = await loadAppState();
        if (cancelled) return;
        const nextTheme = parsed?.theme === "dark" ? "dark" : "light";
        if (parsed?.data) {
          setData(normalizeData(parsed.data));
        } else {
          const initial = seedState();
          setData(initial);
          await saveAppState({
            data: initial,
            loggedIn: false,
            theme: nextTheme,
          });
        }
        setLoggedIn(Boolean(parsed?.loggedIn));
        setTheme(nextTheme);
        setLoadError(null);
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            error instanceof Error
              ? error.message
              : "לא ניתן להתחבר למסד הנתונים",
          );
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || loadError) return;
    const timer = window.setTimeout(() => {
      void saveAppState({ data, loggedIn, theme }).catch((error) => {
        const message =
          error instanceof Error
            ? error.message
            : "שמירת הנתונים נכשלה";
        setLoadError(message);
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [data, loggedIn, ready, theme, loadError]);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    void fetch("/api/reminders")
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as {
          reminders?: Array<{
            entityType: "appointment" | "task";
            entityId: string;
            remindAt: string;
            message: string;
          }>;
        };
      })
      .then((payload) => {
        if (cancelled || !payload?.reminders) return;
        const reminderMap = new Map(
          payload.reminders.map((reminder) => [
            `${reminder.entityType}:${reminder.entityId}`,
            reminder,
          ]),
        );
        setData((current) => ({
          ...current,
          appointments: current.appointments.map((appointment) => {
            const reminder = reminderMap.get(`appointment:${appointment.id}`);
            return {
              ...appointment,
              reminderAt: reminder?.remindAt || "",
              reminderNote: reminder?.message || "",
            };
          }),
          tasks: current.tasks.map((task) => {
            const reminder = reminderMap.get(`task:${task.id}`);
            return {
              ...task,
              reminderAt: reminder?.remindAt || "",
              reminderNote: reminder?.message || "",
            };
          }),
        }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [ready]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") {
        setModal(null);
        setSearchOpen(false);
        setNotificationsOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const clientById = useCallback(
    (id: string) => data.clients.find((client) => client.id === id),
    [data.clients],
  );
  const petById = useCallback(
    (id: string) => data.pets.find((pet) => pet.id === id),
    [data.pets],
  );

  const alerts = useMemo(() => {
    const vaccineCutoff = addDays(data.clinic.reminderDays);
    const vaccineAlerts = data.pets
      .filter((pet) => pet.nextVaccine && pet.nextVaccine <= vaccineCutoff)
      .map((pet) => ({
        id: `v-${pet.id}`,
        tone: pet.nextVaccine < localDateISO() ? "danger" : "warning",
        title:
          pet.nextVaccine < localDateISO()
            ? `החיסון של ${pet.name} באיחור`
            : `חיסון קרוב ל${pet.name}`,
        text: `${clientById(pet.ownerId)?.name || "ללא בעלים"} · ${friendlyDate(
          pet.nextVaccine,
        )}`,
        page: "pets" as Page,
      }));
    const stockAlerts = data.inventory
      .filter((item) => Number(item.stock) <= Number(item.minStock))
      .map((item) => ({
        id: `s-${item.id}`,
        tone: "danger",
        title: `מלאי נמוך: ${item.name}`,
        text: `${item.stock} ${item.unit} במלאי · מינימום ${item.minStock}`,
        page: "inventory" as Page,
      }));
    const taskAlerts = data.tasks
      .filter(
        (task) =>
          task.status === "פתוחה" && task.dueDate < localDateISO(),
      )
      .map((task) => ({
        id: `t-${task.id}`,
        tone: "warning",
        title: "משימה באיחור",
        text: task.text,
        page: "tasks" as Page,
      }));
    const reminderAlerts = [
      ...data.appointments
        .filter(
          (appointment) =>
            appointment.reminderAt &&
            new Date(appointment.reminderAt).getTime() <= nowTick &&
            appointment.status !== "בוטל" &&
            appointment.status !== "הושלם",
        )
        .map((appointment) => ({
          id: `ra-${appointment.id}`,
          tone: "info",
          title: `תזכורת לתור של ${
            petById(appointment.petId)?.name || appointment.type
          }`,
          text:
            appointment.reminderNote ||
            `${friendlyDate(appointment.date)} בשעה ${appointment.time}`,
          page: "calendar" as Page,
        })),
      ...data.tasks
        .filter(
          (task) =>
            task.reminderAt &&
            new Date(task.reminderAt).getTime() <= nowTick &&
            task.status === "פתוחה",
        )
        .map((task) => ({
          id: `rt-${task.id}`,
          tone: "info",
          title: "תזכורת למשימה",
          text: task.reminderNote || task.text,
          page: "tasks" as Page,
        })),
    ];
    return [
      ...reminderAlerts,
      ...vaccineAlerts,
      ...stockAlerts,
      ...taskAlerts,
    ];
  }, [clientById, data, nowTick, petById]);

  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast(message);
    toastTimerRef.current = window.setTimeout(() => {
      setToast("");
      toastTimerRef.current = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
      }
    },
    [],
  );

  const navigate = (next: Page) => {
    setPage(next);
    setSidebarOpen(false);
    setNotificationsOpen(false);
    const path = pagePath(next);
    if (window.location.pathname !== path) {
      window.history.pushState({ page: next }, "", path);
    }
  };

  const updateAppointmentStatus = (
    id: string,
    status: AppointmentStatus,
  ) => {
    const createsCharge =
      status === "הושלם" &&
      !data.invoices.some((invoice) => invoice.appointmentId === id);
    setData((current) => {
      const appointment = current.appointments.find((item) => item.id === id);
      if (!appointment) return current;
      const appointments = current.appointments.map((item) =>
        item.id === id ? { ...item, status } : item,
      );
      let invoices = current.invoices;
      let clinic = current.clinic;
      if (
        status === "הושלם" &&
        !current.invoices.some((invoice) => invoice.appointmentId === id)
      ) {
        const client = current.clients.find(
          (item) => item.id === appointment.ownerId,
        );
        const pet = current.pets.find((item) => item.id === appointment.petId);
        const nextInvoice = nextInvoiceIdentity(clinic, current.invoices);
        invoices = [
          {
            id: nextInvoice.id,
            date: localDateISO(),
            dueDate: addDaysFrom(
              localDateISO(),
              client?.paymentTerms ?? 30,
            ),
            paidDate: "",
            clientId: appointment.ownerId,
            clientName: client?.name || "",
            clientEmail: client?.email || "",
            clientAddress: [client?.address, client?.city]
              .filter(Boolean)
              .join(", "),
            clientTaxId: client?.taxId || "",
            appointmentId: appointment.id,
            status: "חוב פתוח",
            documentType: "דרישת תשלום",
            items: [
              {
                id: uid("line"),
                desc: `${appointment.type}${pet ? ` — ${pet.name}` : ""}`,
                qty: 1,
                price: appointment.price,
              },
            ],
            vatRate: Number(clinic.vat || 0) / 100,
            vatExempt: false,
            paid: 0,
            paymentMethod: "",
            notes: `נוצר אוטומטית מתור בשעה ${appointment.time}`,
          },
          ...current.invoices,
        ];
        clinic = {
          ...clinic,
          nextInvoiceNumber: nextInvoice.nextNumber,
        };
      }
      return { ...current, clinic, appointments, invoices };
    });
    showToast(
      status === "הושלם"
        ? createsCharge
          ? "הביקור הושלם ונוצר חיוב"
          : "הביקור הושלם"
        : "סטטוס התור עודכן",
    );
  };

  const removeItem = (
    collection:
      | "clients"
      | "pets"
      | "appointments"
      | "records"
      | "invoices"
      | "inventory"
      | "tasks",
    id: string,
  ) => {
    if (
      collection === "clients" &&
      data.pets.some((pet) => pet.ownerId === id)
    ) {
      showToast("לא ניתן למחוק לקוח עם בעלי חיים משויכים");
      return;
    }
    if (
      collection === "pets" &&
      (data.appointments.some((appointment) => appointment.petId === id) ||
        data.records.some((record) => record.petId === id) ||
        data.tasks.some((task) => task.petId === id))
    ) {
      showToast("לא ניתן למחוק מטופל עם היסטוריה או משימות משויכות");
      return;
    }
    if (!window.confirm("למחוק את הפריט? הפעולה אינה ניתנת לביטול.")) return;
    if (collection === "records") {
      const record = data.records.find((item) => item.id === id);
      record?.attachments.forEach((attachment) => {
        void fetch(attachment.url, { method: "DELETE" }).catch(() => undefined);
      });
    }
    if (collection === "appointments" || collection === "tasks") {
      void syncReminder(
        collection === "appointments" ? "appointment" : "task",
        id,
        "",
        "",
      ).catch(() => showToast("הפריט נמחק, אך ניקוי התזכורת יושלם בהמשך"));
    }
    setData((current) => ({
      ...current,
      [collection]: current[collection].filter(
        (item: { id: string }) => item.id !== id,
      ),
    }));
    showToast("הפריט נמחק");
  };

  const adjustStock = (id: string, delta: number) => {
    setData((current) => ({
      ...current,
      inventory: current.inventory.map((item) =>
        item.id === id
          ? { ...item, stock: Math.max(0, Number(item.stock) + delta) }
          : item,
      ),
    }));
    showToast("המלאי עודכן");
  };

  const toggleTask = (id: string) => {
    setData((current) => ({
      ...current,
      tasks: current.tasks.map((task) =>
        task.id === id
          ? {
              ...task,
              status: task.status === "בוצעה" ? "פתוחה" : "בוצעה",
            }
          : task,
      ),
    }));
  };

  const markInvoicePaid = (id: string) => {
    setData((current) => ({
      ...current,
      invoices: current.invoices.map((invoice) =>
        invoice.id === id
          ? {
              ...invoice,
              paid: invoiceTotal(invoice),
              status: "שולמה",
              paidDate: localDateISO(),
              paymentMethod: invoice.paymentMethod || "אשראי",
            }
          : invoice,
      ),
    }));
    showToast("החשבונית סומנה כשולמה");
  };

  const sendInvoice = (id: string) => {
    setData((current) => ({
      ...current,
      invoices: current.invoices.map((invoice) =>
        invoice.id === id && invoice.status !== "מבוטלת"
          ? { ...invoice, status: "נשלחה" }
          : invoice,
      ),
    }));
    showToast("החשבונית סומנה כנשלחה");
  };

  const cancelInvoice = (id: string) => {
    setData((current) => ({
      ...current,
      invoices: current.invoices.map((invoice) =>
        invoice.id === id ? { ...invoice, status: "מבוטלת" } : invoice,
      ),
    }));
    showToast("החשבונית בוטלה ונשמרה בהיסטוריה");
  };

  const duplicateInvoice = (id: string) => {
    setData((current) => {
      const source = current.invoices.find((invoice) => invoice.id === id);
      if (!source) return current;
      const nextInvoice = nextInvoiceIdentity(
        current.clinic,
        current.invoices,
      );
      const duplicate: Invoice = {
        ...source,
        id: nextInvoice.id,
        date: localDateISO(),
        dueDate: addDaysFrom(localDateISO(), 30),
        paidDate: "",
        status: "טיוטה",
        paid: 0,
        notes: source.notes
          ? `${source.notes} — שוכפל ממסמך ${source.id}`
          : `שוכפל ממסמך ${source.id}`,
        items: source.items.map((item) => ({ ...item, id: uid("line") })),
      };
      return {
        ...current,
        clinic: {
          ...current.clinic,
          nextInvoiceNumber: nextInvoice.nextNumber,
        },
        invoices: [duplicate, ...current.invoices],
      };
    });
    showToast("עותק חדש נשמר כטיוטה");
  };

  const exportCSV = (
    type:
      | "clients"
      | "pets"
      | "appointments"
      | "records"
      | "invoices"
      | "inventory"
      | "tasks",
  ) => {
    let rows: Array<Record<string, string | number>> = [];
    if (type === "clients") {
      rows = data.clients.map((client) => ({
        name: client.name,
        phone: client.phone,
        email: client.email,
        city: client.city,
        status: client.status,
      }));
    } else if (type === "pets") {
      rows = data.pets.map((pet) => ({
        name: pet.name,
        species: pet.species,
        breed: pet.breed,
        owner: clientById(pet.ownerId)?.name || "",
        chip: pet.chip,
        nextVaccine: pet.nextVaccine,
      }));
    } else if (type === "appointments") {
      rows = data.appointments.map((appointment) => ({
        date: appointment.date,
        time: appointment.time,
        pet: petById(appointment.petId)?.name || "",
        client: clientById(appointment.ownerId)?.name || "",
        treatment: appointment.type,
        status: appointment.status,
        price: appointment.price,
      }));
    } else if (type === "records") {
      rows = data.records.map((record) => ({
        date: record.date,
        pet: petById(record.petId)?.name || "",
        title: record.title,
        diagnosis: record.diagnosis,
        treatment: record.treatment,
      }));
    } else if (type === "invoices") {
      rows = data.invoices.map((invoice) => ({
        id: invoice.id,
        documentType: invoice.documentType,
        date: invoice.date,
        dueDate: invoice.dueDate,
        client: invoice.clientName || clientById(invoice.clientId)?.name || "",
        clientTaxId: invoice.clientTaxId,
        subtotal: invoiceSubtotal(invoice),
        vat: invoiceVat(invoice),
        total: invoiceTotal(invoice),
        paid: invoice.paid,
        balance: invoiceBalance(invoice),
        status: invoiceDisplayStatus(invoice),
        paymentMethod: invoice.paymentMethod,
      }));
    } else if (type === "inventory") {
      rows = data.inventory.map((item) => ({
        name: item.name,
        category: item.category,
        stock: item.stock,
        minimum: item.minStock,
        supplier: item.supplier,
        expiry: item.expiry,
      }));
    } else {
      rows = data.tasks.map((task) => ({
        task: task.text,
        dueDate: task.dueDate,
        owner: task.owner,
        priority: task.priority,
        status: task.status,
      }));
    }
    downloadRows(`vetmanage-${type}.csv`, rows);
    showToast("קובץ הייצוא מוכן");
  };

  const downloadRows = (
    filename: string,
    rows: Array<Record<string, string | number>>,
  ) => {
    const keys = Object.keys(rows[0] || {});
    const csvCell = (value: string | number | undefined) => {
      const raw = String(value ?? "");
      const safe = /^[=+\-@]/.test(raw.trimStart()) ? `'${raw}` : raw;
      return `"${safe.replaceAll('"', '""')}"`;
    };
    const csv =
      "\ufeff" +
      [
        keys.join(","),
        ...rows.map((row) =>
          keys
            .map((key) => csvCell(row[key]))
            .join(","),
        ),
      ].join("\n");
    downloadFile(filename, csv, "text/csv;charset=utf-8");
  };

  const downloadFile = (filename: string, content: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const backup = () => {
    downloadFile(
      `vetmanage-backup-${localDateISO()}.json`,
      JSON.stringify(
        {
          format: "vetmanage-backup",
          version: 2,
          exportedAt: new Date().toISOString(),
          data,
        },
        null,
        2,
      ),
      "application/json",
    );
    showToast("הגיבוי הורד");
  };

  const restore = async (file?: File) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as unknown;
      const candidate =
        parsed && typeof parsed === "object" && "data" in parsed
          ? (parsed as { data?: unknown }).data
          : parsed;
      if (!candidate || typeof candidate !== "object") throw new Error();
      const restored = candidate as Partial<AppData>;
      if (
        !restored.clinic ||
        !Array.isArray(restored.clients) ||
        !Array.isArray(restored.pets) ||
        !Array.isArray(restored.appointments)
      ) {
        throw new Error();
      }
      setData(normalizeData(restored as AppData));
      showToast("הגיבוי שוחזר בהצלחה");
    } catch {
      showToast("הקובץ אינו גיבוי תקין");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const globalResults = useMemo(() => {
    const query = globalSearch.trim().toLowerCase();
    if (!query) return [];
    const results: Array<{
      id: string;
      type: string;
      title: string;
      subtitle: string;
      page: Page;
      modal?: ModalState;
    }> = [];
    data.clients.forEach((client) => {
      if (
        `${client.name} ${client.phone} ${client.email} ${client.city}`
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          id: client.id,
          type: "לקוח",
          title: client.name,
          subtitle: `${client.phone} · ${client.city}`,
          page: "clients",
          modal: { type: "client-detail", id: client.id },
        });
      }
    });
    data.pets.forEach((pet) => {
      const owner = clientById(pet.ownerId);
      if (
        `${pet.name} ${pet.species} ${pet.breed} ${pet.chip} ${owner?.name}`
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          id: pet.id,
          type: "מטופל",
          title: pet.name,
          subtitle: `${pet.species} · ${owner?.name || ""}`,
          page: "pets",
          modal: { type: "pet-detail", id: pet.id },
        });
      }
    });
    data.appointments.forEach((appointment) => {
      if (
        `${appointment.type} ${petById(appointment.petId)?.name} ${
          clientById(appointment.ownerId)?.name
        }`
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          id: appointment.id,
          type: "תור",
          title: `${appointment.type} · ${petById(appointment.petId)?.name}`,
          subtitle: `${appointment.date} · ${appointment.time}`,
          page: "calendar",
        });
      }
    });
    data.invoices.forEach((invoice) => {
      if (
        `${invoice.id} ${clientById(invoice.clientId)?.name}`
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          id: invoice.id,
          type: "חשבונית",
          title: invoice.id,
          subtitle: `${clientById(invoice.clientId)?.name} · ${money(
            invoiceBalance(invoice),
          )}`,
          page: "billing",
          modal: { type: "invoice-detail", id: invoice.id },
        });
      }
    });
    data.marketingCampaigns.forEach((campaign) => {
      if (
        `${campaign.name} ${campaign.platform} ${campaign.objective} ${campaign.audience}`
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          id: campaign.id,
          type: "קמפיין",
          title: campaign.name,
          subtitle: `${campaign.platform} · ${campaign.status}`,
          page: "marketing",
        });
      }
    });
    data.marketingAssets.forEach((asset) => {
      if (
        `${asset.title} ${asset.type} ${asset.headline} ${asset.body}`
          .toLowerCase()
          .includes(query)
      ) {
        results.push({
          id: asset.id,
          type: "תוכן שיווקי",
          title: asset.title,
          subtitle: `${asset.type} · ${asset.status}`,
          page: "marketing",
        });
      }
    });
    return results.slice(0, 12);
  }, [clientById, data, globalSearch, petById]);

  const renderPage = () => {
    switch (page) {
      case "calendar":
        return (
          <CalendarPage
            data={data}
            date={calendarDate}
            setDate={setCalendarDate}
            filters={filters}
            setFilters={setFilters}
            clientById={clientById}
            petById={petById}
            onAdd={(targetDate) =>
              setModal({
                type: "appointment",
                date: targetDate || calendarDate,
              })
            }
            onEdit={(id) => setModal({ type: "appointment", id })}
            onDelete={(id) => removeItem("appointments", id)}
            onStatus={updateAppointmentStatus}
            onExport={() => exportCSV("appointments")}
          />
        );
      case "clients":
        return (
          <ClientsPage
            data={data}
            filters={filters}
            setFilters={setFilters}
            onAdd={() => setModal({ type: "client" })}
            onEdit={(id) => setModal({ type: "client", id })}
            onView={(id) => setModal({ type: "client-detail", id })}
            onInvoice={(id) => setModal({ type: "invoice", clientId: id })}
            onDelete={(id) => removeItem("clients", id)}
            onExport={() => exportCSV("clients")}
          />
        );
      case "pets":
        return (
          <PetsPage
            data={data}
            filters={filters}
            setFilters={setFilters}
            clientById={clientById}
            onAdd={() => setModal({ type: "pet" })}
            onEdit={(id) => setModal({ type: "pet", id })}
            onView={(id) => setModal({ type: "pet-detail", id })}
            onDelete={(id) => removeItem("pets", id)}
            onExport={() => exportCSV("pets")}
          />
        );
      case "records":
        return (
          <RecordsPage
            data={data}
            filters={filters}
            setFilters={setFilters}
            clientById={clientById}
            petById={petById}
            onAdd={() => setModal({ type: "record" })}
            onEdit={(id) => setModal({ type: "record", id })}
            onDelete={(id) => removeItem("records", id)}
            onViewPet={(id) => setModal({ type: "pet-detail", id })}
            onExport={() => exportCSV("records")}
          />
        );
      case "billing":
        return (
          <BillingPage
            data={data}
            filters={filters}
            setFilters={setFilters}
            clientById={clientById}
            onAdd={() => setModal({ type: "invoice" })}
            onEdit={(id) => setModal({ type: "invoice", id })}
            onView={(id) => setModal({ type: "invoice-detail", id })}
            onPrint={(id) =>
              setModal({ type: "invoice-detail", id, autoPrint: true })
            }
            onDelete={(id) => removeItem("invoices", id)}
            onPaid={markInvoicePaid}
            onSend={sendInvoice}
            onDuplicate={duplicateInvoice}
            onCancel={cancelInvoice}
            onExport={() => exportCSV("invoices")}
          />
        );
      case "inventory":
        return (
          <InventoryPage
            data={data}
            filters={filters}
            setFilters={setFilters}
            onAdd={() => setModal({ type: "inventory" })}
            onEdit={(id) => setModal({ type: "inventory", id })}
            onDelete={(id) => removeItem("inventory", id)}
            onAdjust={adjustStock}
            onExport={() => exportCSV("inventory")}
          />
        );
      case "tasks":
        return (
          <TasksPage
            data={data}
            filters={filters}
            setFilters={setFilters}
            petById={petById}
            onAdd={() => setModal({ type: "task" })}
            onEdit={(id) => setModal({ type: "task", id })}
            onDelete={(id) => removeItem("tasks", id)}
            onToggle={toggleTask}
            onExport={() => exportCSV("tasks")}
          />
        );
      case "marketing":
        return (
          <MarketingPage data={data} setData={setData} onToast={showToast} />
        );
      case "reports":
        return (
          <ReportsPage
            data={data}
            petById={petById}
          />
        );
      case "settings":
        return (
          <SettingsPage
            data={data}
            setData={setData}
            onBackup={backup}
            onRestore={() => fileInputRef.current?.click()}
            onReset={() => {
              if (window.confirm("לאפס את כל נתוני ההדגמה?")) {
                setData(seedState());
                showToast("נתוני ההדגמה אופסו");
              }
            }}
            onClear={() => {
              if (window.confirm("למחוק את כל הנתונים המקומיים?")) {
                void saveAppState({ data: seedState(), loggedIn: true, theme }).catch(() => undefined);
                setData(seedState());
                setLoggedIn(false);
              }
            }}
            onToast={showToast}
          />
        );
      default:
        return (
          <DashboardPage
            data={data}
            alerts={alerts}
            clientById={clientById}
            petById={petById}
            onNavigate={navigate}
            onAddAppointment={() => setModal({ type: "appointment" })}
            onAddClient={() => setModal({ type: "client" })}
            onAddPet={() => setModal({ type: "pet" })}
            onAddRecord={() => setModal({ type: "record" })}
            onAddInvoice={() => setModal({ type: "invoice" })}
            onAddTask={() => setModal({ type: "task" })}
            onToggleTask={toggleTask}
            onStatus={updateAppointmentStatus}
            onOpenAlerts={() => setNotificationsOpen(true)}
          />
        );
    }
  };

  if (!ready) {
    return (
      <div className="launch-screen" aria-label="המערכת נטענת">
        <div className="brand-mark large">V+</div>
        <div className="launch-line" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="launch-screen" aria-label="שגיאת חיבור">
        <div className="empty-state db-error-state">
          <span className="empty-icon">!</span>
          <strong>שגיאת חיבור למסד הנתונים</strong>
          <p>{loadError}</p>
          <p className="db-error-hint">
            בדקו את קובץ <code>.env</code> (DB_NAME, DB_USER, DB_PASS) ושהמסד
            MySQL פועל ב-OpenServer.
          </p>
          <button
            type="button"
            className="button button-primary"
            onClick={() => window.location.reload()}
          >
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  if (!loggedIn) {
    return (
      <LoginScreen
        clinic={data.clinic}
        onLogin={() => {
          setLoggedIn(true);
          showToast("ברוכים הבאים למרפאה");
        }}
      />
    );
  }

  const activeNav = navItems.find((item) => item.id === page) || navItems[0];

  return (
    <div className={`app ${theme === "dark" ? "theme-dark" : ""}`} dir="rtl">
      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <span className="brand-mark">V+</span>
          <div>
            <strong>VetManage</strong>
            <small>מרפאה חכמה. טיפול אישי.</small>
          </div>
          <button
            className="icon-button mobile-only"
            onClick={() => setSidebarOpen(false)}
            aria-label="סגירת תפריט"
          >
            ×
          </button>
        </div>
        <div className="clinic-switcher">
          <span className="clinic-avatar">VC</span>
          <div>
            <strong>{data.clinic.name}</strong>
            <small>המרפאה הראשית</small>
          </div>
          <span className="chevron">⌄</span>
        </div>
        <nav className="nav-list" aria-label="ניווט ראשי">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              className={page === item.id ? "active" : ""}
              onClick={() => navigate(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span>
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </span>
              {item.id === "tasks" &&
              data.tasks.filter((task) => task.status === "פתוחה").length ? (
                <em>
                  {data.tasks.filter((task) => task.status === "פתוחה").length}
                </em>
              ) : null}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="staff-card">
            <span className="staff-avatar">פכ</span>
            <div>
              <strong>{data.clinic.doctor}</strong>
              <small>וטרינרית ראשית</small>
            </div>
            <button
              className="more-button"
              aria-label="יציאה"
              title="יציאה"
              onClick={() => setLoggedIn(false)}
            >
              ↪
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen ? (
        <button
          className="sidebar-scrim"
          onClick={() => setSidebarOpen(false)}
          aria-label="סגירת תפריט"
        />
      ) : null}

      <div className="app-main">
        <header className="topbar">
          <div className="topbar-title">
            <button
              className="icon-button mobile-only"
              onClick={() => setSidebarOpen(true)}
              aria-label="פתיחת תפריט"
            >
              ☰
            </button>
            <div>
              <span className="breadcrumb">מרפאה / {activeNav.label}</span>
              <h1>{activeNav.label}</h1>
            </div>
          </div>
          <div className="topbar-actions">
            <button
              className="search-trigger"
              onClick={() => setSearchOpen(true)}
              aria-label="חיפוש במערכת"
            >
              <span>⌕</span>
              <span>חיפוש לקוח, מטופל או חשבונית</span>
              <kbd>Ctrl K</kbd>
            </button>
            <button
              className="icon-button"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              aria-label="החלפת ערכת צבעים"
            >
              {theme === "light" ? "☾" : "☀"}
            </button>
            <button
              className="icon-button notification-button"
              onClick={() => setNotificationsOpen(true)}
              aria-label={`התראות, ${alerts.length} פתוחות`}
            >
              ♢
              {alerts.length ? <span>{alerts.length}</span> : null}
            </button>
          </div>
        </header>

        <main className="page-content">{renderPage()}</main>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json"
        hidden
        onChange={(event) => restore(event.target.files?.[0])}
      />

      {modal ? (
        <EntityModal
          modal={modal}
          data={data}
          setData={setData}
          onClose={() => setModal(null)}
          onToast={showToast}
          clientById={clientById}
          petById={petById}
          onEdit={(next) => setModal(next)}
        />
      ) : null}

      {searchOpen ? (
        <SearchOverlay
          query={globalSearch}
          setQuery={setGlobalSearch}
          results={globalResults}
          onClose={() => {
            setSearchOpen(false);
            setGlobalSearch("");
          }}
          onSelect={(result) => {
            navigate(result.page);
            if (result.modal) setModal(result.modal);
            setSearchOpen(false);
            setGlobalSearch("");
          }}
        />
      ) : null}

      <NotificationDrawer
        open={notificationsOpen}
        alerts={alerts}
        onClose={() => setNotificationsOpen(false)}
        onNavigate={navigate}
      />

      <div
        className={`toast ${toast ? "toast-visible" : ""}`}
        role="status"
        aria-live="polite"
      >
        <span>✓</span>
        {toast}
      </div>
    </div>
  );
}

function LoginScreen({
  clinic,
  onLogin,
}: {
  clinic: Clinic;
  onLogin: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    if (!values.get("user") || !values.get("password")) {
      setError("יש להזין שם משתמש וסיסמה");
      return;
    }
    onLogin();
  };

  return (
    <div className="login-page" dir="rtl">
      <section className="login-story">
        <div className="story-orb story-orb-one" />
        <div className="story-orb story-orb-two" />
        <div className="story-content">
          <div className="story-brand">
            <span className="brand-mark brand-mark-light">V+</span>
            <strong>VetManage</strong>
          </div>
          <div className="story-copy">
            <Pill tone="mint">המרפאה שלך, מסודרת</Pill>
            <h1>
              יותר זמן לטיפול.
              <br />
              פחות זמן לניהול.
            </h1>
            <p>
              תורים, לקוחות, תיקים רפואיים, חיובים ומלאי — סביבת עבודה אחת
              שמחברת את כל צוות המרפאה.
            </p>
            <div className="story-stats">
              <div>
                <strong>360°</strong>
                <span>תמונה מלאה לכל מטופל</span>
              </div>
              <div>
                <strong>24/7</strong>
                <span>גישה למידע מכל מכשיר</span>
              </div>
              <div>
                <strong>1</strong>
                <span>מקור מידע אמין לצוות</span>
              </div>
            </div>
          </div>
          <div className="story-quote">
            <span className="quote-avatar">פכ</span>
            <div>
              <p>
                “בכל בוקר אני יודעת בדיוק מה מחכה לצוות ומה דורש תשומת לב.”
              </p>
              <strong>ד״ר פרידה כהן · {clinic.name}</strong>
            </div>
          </div>
        </div>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div className="login-heading">
            <span className="eyebrow">כניסה מאובטחת</span>
            <h2>טוב לראות אותך שוב</h2>
            <p>היכנסו לסביבת העבודה של {clinic.name}</p>
          </div>
          <Field label="שם משתמש">
            <div className="input-with-icon">
              <span>◎</span>
              <input
                name="user"
                defaultValue="dr_cohen"
                autoComplete="username"
                aria-label="שם משתמש"
              />
            </div>
          </Field>
          <Field label="סיסמה">
            <div className="input-with-icon">
              <span>○</span>
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                defaultValue="password123"
                autoComplete="current-password"
                aria-label="סיסמה"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "הסתרת סיסמה" : "הצגת סיסמה"}
              >
                {showPassword ? "הסתר" : "הצג"}
              </button>
            </div>
          </Field>
          <div className="login-options">
            <label>
              <input type="checkbox" defaultChecked /> זכור אותי
            </label>
            <button type="button">שכחתי סיסמה</button>
          </div>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="button button-primary button-large" type="submit">
            כניסה למערכת
            <span>←</span>
          </button>
          <div className="demo-note">
            <span>i</span>
            <p>
              סביבת הדגמה: פרטי הכניסה כבר הוזנו. הנתונים נשמרים במכשיר הזה.
            </p>
          </div>
        </form>
        <div className="login-help">
          צריכים עזרה? <button>פנייה לתמיכה</button>
        </div>
      </section>
    </div>
  );
}

function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

function DashboardPage({
  data,
  alerts,
  clientById,
  petById,
  onNavigate,
  onAddAppointment,
  onAddClient,
  onAddPet,
  onAddRecord,
  onAddInvoice,
  onAddTask,
  onToggleTask,
  onStatus,
  onOpenAlerts,
}: {
  data: AppData;
  alerts: Array<{
    id: string;
    tone: string;
    title: string;
    text: string;
    page: Page;
  }>;
  clientById: (id: string) => ClientRecord | undefined;
  petById: (id: string) => PetRecord | undefined;
  onNavigate: (page: Page) => void;
  onAddAppointment: () => void;
  onAddClient: () => void;
  onAddPet: () => void;
  onAddRecord: () => void;
  onAddInvoice: () => void;
  onAddTask: () => void;
  onToggleTask: (id: string) => void;
  onStatus: (id: string, status: AppointmentStatus) => void;
  onOpenAlerts: () => void;
}) {
  const todayAppointments = data.appointments
    .filter(
      (appointment) =>
        appointment.date === localDateISO() && appointment.status !== "בוטל",
    )
    .sort((a, b) => a.time.localeCompare(b.time));
  const openTasks = data.tasks.filter((task) => task.status === "פתוחה");
  const lowStock = data.inventory.filter(
    (item) => Number(item.stock) <= Number(item.minStock),
  );
  const month = localDateISO().slice(0, 7);
  const revenue = data.invoices
    .filter((invoice) => invoice.date.startsWith(month))
    .reduce((sum, invoice) => sum + Number(invoice.paid), 0);
  const completed = todayAppointments.filter(
    (appointment) => appointment.status === "הושלם",
  ).length;
  const progress = todayAppointments.length
    ? Math.round((completed / todayAppointments.length) * 100)
    : 0;

  const stats = [
    {
      label: "ביקורים היום",
      value: todayAppointments.length,
      detail: `${completed} הושלמו`,
      icon: "◫",
      tone: "teal",
      trend: "+12%",
    },
    {
      label: "משימות פתוחות",
      value: openTasks.length,
      detail: `${openTasks.filter((task) => task.priority === "דחופה").length} דחופות`,
      icon: "✓",
      tone: "coral",
      trend: "לטיפול",
    },
    {
      label: "פריטי מלאי נמוך",
      value: lowStock.length,
      detail: "נדרשת הזמנה",
      icon: "□",
      tone: "amber",
      trend: `${lowStock.length}`,
    },
    {
      label: "הכנסות החודש",
      value: money(revenue),
      detail: "גבייה בפועל",
      icon: "₪",
      tone: "blue",
      trend: "+8.4%",
    },
  ];

  return (
    <div className="page-stack">
      <section className="welcome-strip">
        <div>
          <span className="eyebrow">יום עבודה חדש</span>
          <h2>
            בוקר טוב, ד״ר פרידה <span>👋</span>
          </h2>
          <p>
            {new Intl.DateTimeFormat("he-IL", {
              weekday: "long",
              day: "numeric",
              month: "long",
            }).format(new Date())}{" "}
            · הנה תמונת המצב של המרפאה
          </p>
        </div>
        <button className="button button-primary" onClick={onAddAppointment}>
          <span>＋</span> קביעת תור חדש
        </button>
      </section>

      <section className="stats-grid">
        {stats.map((stat) => (
          <article className="stat-card" key={stat.label}>
            <div className={`stat-icon stat-${stat.tone}`}>{stat.icon}</div>
            <div className="stat-body">
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <small>{stat.detail}</small>
            </div>
            <Pill tone={stat.tone === "coral" ? "danger" : "success"}>
              {stat.trend}
            </Pill>
          </article>
        ))}
      </section>

      <section className="dashboard-main-grid">
        <article className="card schedule-card">
          <CardHeader
            eyebrow="היום במרפאה"
            title="לוח הביקורים"
            action={
              <button className="text-button" onClick={() => onNavigate("calendar")}>
                לכל היומן ←
              </button>
            }
          />
          <div className="day-progress">
            <div>
              <span>התקדמות היום</span>
              <strong>{progress}%</strong>
            </div>
            <div className="progress-track">
              <span style={{ width: `${Math.max(progress, 4)}%` }} />
            </div>
          </div>
          <div className="appointment-list">
            {todayAppointments.length ? (
              todayAppointments.map((appointment, index) => {
                const pet = petById(appointment.petId);
                const client = clientById(appointment.ownerId);
                return (
                  <div className="appointment-row" key={appointment.id}>
                    <div className="appointment-time">
                      <strong>{appointment.time}</strong>
                      <span>{appointment.duration} דק׳</span>
                    </div>
                    <div
                      className={`timeline-dot ${
                        index === 0 ? "timeline-dot-active" : ""
                      }`}
                    />
                    <div className="pet-avatar">
                      {pet?.species === "חתול"
                        ? "מ"
                        : pet?.species === "ארנב"
                          ? "ש"
                          : pet?.name.slice(0, 1)}
                    </div>
                    <div className="appointment-info">
                      <strong>{pet?.name || "מטופל"}</strong>
                      <span>
                        {appointment.type} · {client?.name}
                      </span>
                    </div>
                    <div className="appointment-room">
                      <span>{appointment.doctor}</span>
                      <small>{appointment.room}</small>
                    </div>
                    <select
                      className={`status-select status-${statusTone[appointment.status]}`}
                      value={appointment.status}
                      onChange={(event) =>
                        onStatus(
                          appointment.id,
                          event.target.value as AppointmentStatus,
                        )
                      }
                      aria-label={`סטטוס התור של ${pet?.name}`}
                    >
                      {["ממתין", "הגיע", "בטיפול", "הושלם", "בוטל"].map(
                        (status) => (
                          <option key={status}>{status}</option>
                        ),
                      )}
                    </select>
                  </div>
                );
              })
            ) : (
              <EmptyState
                icon="◫"
                title="אין תורים להיום"
                text="אפשר לנצל את הזמן למעקב יזום או לקבוע ביקור חדש."
                action={
                  <button className="button button-secondary" onClick={onAddAppointment}>
                    קביעת תור
                  </button>
                }
              />
            )}
          </div>
        </article>

        <aside className="dashboard-side">
          <article className="card quick-card">
            <CardHeader eyebrow="קיצורי דרך" title="פעולות מהירות" />
            <div className="quick-grid">
              <button onClick={onAddClient}>
                <span className="quick-icon mint">◎</span>
                <strong>לקוח חדש</strong>
                <small>הוספת בעלים</small>
              </button>
              <button onClick={onAddPet}>
                <span className="quick-icon coral">♣</span>
                <strong>מטופל חדש</strong>
                <small>פתיחת כרטיס</small>
              </button>
              <button onClick={onAddRecord}>
                <span className="quick-icon purple">+</span>
                <strong>רשומה רפואית</strong>
                <small>תיעוד טיפול</small>
              </button>
              <button onClick={onAddInvoice}>
                <span className="quick-icon amber">₪</span>
                <strong>חשבונית</strong>
                <small>חיוב וגבייה</small>
              </button>
            </div>
          </article>

          <article className="card alerts-card">
            <CardHeader
              eyebrow="דורש תשומת לב"
              title="מרכז התראות"
              action={
                <button className="round-more" onClick={onOpenAlerts}>
                  •••
                </button>
              }
            />
            <div className="compact-alerts">
              {alerts.slice(0, 4).map((alert) => (
                <button
                  key={alert.id}
                  onClick={() => onNavigate(alert.page)}
                  className="compact-alert"
                >
                  <span className={`alert-mark alert-${alert.tone}`}>!</span>
                  <span>
                    <strong>{alert.title}</strong>
                    <small>{alert.text}</small>
                  </span>
                  <b>←</b>
                </button>
              ))}
              {!alerts.length ? (
                <EmptyState
                  icon="✓"
                  title="הכול בשליטה"
                  text="אין התראות פתוחות כרגע."
                />
              ) : null}
            </div>
          </article>
        </aside>
      </section>

      <section className="dashboard-bottom-grid">
        <article className="card tasks-card">
          <CardHeader
            eyebrow="צוות"
            title="משימות קרובות"
            action={
              <button className="button button-small button-secondary" onClick={onAddTask}>
                ＋ משימה
              </button>
            }
          />
          <div className="task-list">
            {openTasks.slice(0, 5).map((task) => (
              <div className="task-row" key={task.id}>
                <button
                  className="task-check"
                  onClick={() => onToggleTask(task.id)}
                  aria-label={`סימון המשימה ${task.text} כבוצעה`}
                />
                <div>
                  <strong>{task.text}</strong>
                  <span>
                    {friendlyDate(task.dueDate)} · {task.owner}
                  </span>
                </div>
                <Pill tone={statusTone[task.priority]}>{task.priority}</Pill>
              </div>
            ))}
          </div>
        </article>

        <article className="card pulse-card">
          <CardHeader eyebrow="7 ימים אחרונים" title="דופק המרפאה" />
          <div className="pulse-summary">
            <div>
              <strong>42</strong>
              <span>ביקורים</span>
            </div>
            <div>
              <strong>88%</strong>
              <span>תפוסה</span>
            </div>
            <div>
              <strong>4.9</strong>
              <span>שביעות רצון</span>
            </div>
          </div>
          <div className="mini-chart" aria-label="תרשים פעילות שבועי">
            {[42, 64, 55, 82, 72, 94, 68].map((height, index) => (
              <div key={index}>
                <span style={{ height: `${height}%` }} />
                <small>{["א", "ב", "ג", "ד", "ה", "ו", "ש"][index]}</small>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}

function CalendarPage({
  data,
  date,
  setDate,
  filters,
  setFilters,
  clientById,
  petById,
  onAdd,
  onEdit,
  onDelete,
  onStatus,
  onExport,
}: {
  data: AppData;
  date: string;
  setDate: (date: string) => void;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  clientById: (id: string) => ClientRecord | undefined;
  petById: (id: string) => PetRecord | undefined;
  onAdd: (date?: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onStatus: (id: string, status: AppointmentStatus) => void;
  onExport: () => void;
}) {
  const query = filters.calendarQuery || "";
  const status = filters.calendarStatus || "הכול";
  const view = filters.calendarView || "month";
  const movePeriod = (amount: number) => {
    const current = new Date(`${date}T12:00:00`);
    if (view === "month") {
      current.setDate(1);
      current.setMonth(current.getMonth() + amount);
    } else {
      current.setDate(current.getDate() + amount);
    }
    setDate(localDateISO(current));
  };
  const filteredAppointments = data.appointments
    .filter((appointment) => status === "הכול" || appointment.status === status)
    .filter((appointment) =>
      `${appointment.type} ${petById(appointment.petId)?.name} ${
        clientById(appointment.ownerId)?.name
      }`
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
    .sort((a, b) => a.time.localeCompare(b.time));
  const rows = filteredAppointments.filter(
    (appointment) => appointment.date === date,
  );
  const monthAnchor = new Date(`${date}T12:00:00`);
  const monthLabel = new Intl.DateTimeFormat("he-IL", {
    month: "long",
    year: "numeric",
  }).format(monthAnchor);
  const monthStart = new Date(
    monthAnchor.getFullYear(),
    monthAnchor.getMonth(),
    1,
    12,
  );
  monthStart.setDate(monthStart.getDate() - monthStart.getDay());
  const calendarDays = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(monthStart);
    day.setDate(monthStart.getDate() + index);
    const key = localDateISO(day);
    return {
      key,
      day,
      appointments: filteredAppointments.filter(
        (appointment) => appointment.date === key,
      ),
    };
  });

  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="ניהול היום"
        title="יומן תורים"
        description="תצוגת חודש מלאה לצד רשימת התורים, החדרים והעומס היומי."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button
              className="button button-primary"
              onClick={() => onAdd(date)}
            >
              ＋ תור חדש
            </button>
          </>
        }
      />
      <div className="calendar-toolbar card">
        <div className="calendar-view-toggle" aria-label="בחירת תצוגת יומן">
          <button
            className={view === "month" ? "active" : ""}
            onClick={() =>
              setFilters({ ...filters, calendarView: "month" })
            }
            aria-pressed={view === "month"}
          >
            ▦ חודש
          </button>
          <button
            className={view === "day" ? "active" : ""}
            onClick={() => setFilters({ ...filters, calendarView: "day" })}
            aria-pressed={view === "day"}
          >
            ☷ יום
          </button>
        </div>
        <div className="date-navigator">
          <button
            onClick={() => movePeriod(-1)}
            aria-label={view === "month" ? "חודש קודם" : "יום קודם"}
          >
            ›
          </button>
          <div>
            <strong>{view === "month" ? monthLabel : friendlyDate(date)}</strong>
            <button onClick={() => setDate(localDateISO())}>חזרה להיום</button>
          </div>
          <button
            onClick={() => movePeriod(1)}
            aria-label={view === "month" ? "חודש הבא" : "יום הבא"}
          >
            ‹
          </button>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            aria-label="בחירת תאריך"
          />
        </div>
        <div className="filter-row">
          <div className="search-box">
            <span>⌕</span>
            <input
              value={query}
              onChange={(event) =>
                setFilters({ ...filters, calendarQuery: event.target.value })
              }
              placeholder="חיפוש תור או מטופל"
              aria-label="חיפוש ביומן"
            />
          </div>
          <select
            value={status}
            onChange={(event) =>
              setFilters({ ...filters, calendarStatus: event.target.value })
            }
            aria-label="סינון סטטוס"
          >
            <option>הכול</option>
            {["ממתין", "הגיע", "בטיפול", "הושלם", "בוטל"].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </div>
      </div>
      {view === "month" ? (
        <section className="card month-calendar" aria-label={`לוח חודש ${monthLabel}`}>
          <div className="calendar-weekdays" aria-hidden="true">
            {["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"].map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>
          <div className="calendar-month-grid">
            {calendarDays.map(({ key, day, appointments }) => {
              const isCurrentMonth =
                day.getMonth() === monthAnchor.getMonth();
              const isSelected = key === date;
              const isToday = key === localDateISO();
              return (
                <button
                  key={key}
                  className={`calendar-day ${
                    isCurrentMonth ? "" : "outside-month"
                  } ${isSelected ? "selected" : ""} ${
                    isToday ? "today" : ""
                  }`}
                  onClick={() => {
                    setDate(key);
                    onAdd(key);
                  }}
                  aria-label={`${friendlyDate(key)}, ${appointments.length} תורים. לחיצה תפתח תור חדש`}
                  aria-pressed={isSelected}
                >
                  <span className="calendar-day-head">
                    <b>{day.getDate()}</b>
                    {appointments.length ? (
                      <small>{appointments.length} תורים</small>
                    ) : null}
                  </span>
                  <span className="calendar-day-events">
                    {appointments.slice(0, 3).map((appointment) => (
                      <span
                        className={`calendar-event calendar-event-${statusTone[appointment.status]}`}
                        key={appointment.id}
                      >
                        <b>{appointment.time}</b>
                        <span>
                          {petById(appointment.petId)?.name || appointment.type}
                        </span>
                      </span>
                    ))}
                    {appointments.length > 3 ? (
                      <small className="calendar-more">
                        +{appointments.length - 3} נוספים
                      </small>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}
      <div className="calendar-selected-day">
        <div>
          <span className="eyebrow">התאריך שנבחר</span>
          <h3>{friendlyDate(date)}</h3>
        </div>
        <strong>{rows.length} תורים</strong>
      </div>
      <div className="day-summary-grid">
        {["ממתין", "הגיע", "בטיפול", "הושלם"].map((item) => (
          <div className="summary-chip" key={item}>
            <span className={`summary-dot summary-${statusTone[item]}`} />
            <span>{item}</span>
            <strong>{rows.filter((row) => row.status === item).length}</strong>
          </div>
        ))}
      </div>
      <article className="card table-card">
        {rows.length ? (
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>שעה</th>
                  <th>מטופל</th>
                  <th>לקוח</th>
                  <th>סיבת ביקור</th>
                  <th>רופא וחדר</th>
                  <th>סטטוס</th>
                  <th>חיוב</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((appointment) => {
                  const pet = petById(appointment.petId);
                  const client = clientById(appointment.ownerId);
                  return (
                    <tr key={appointment.id}>
                      <td>
                        <strong className="table-primary">{appointment.time}</strong>
                        <span className="table-secondary">
                          {appointment.duration} דקות
                        </span>
                      </td>
                      <td>
                        <div className="table-person">
                          <span>{pet?.name.slice(0, 1)}</span>
                          <div>
                            <strong>{pet?.name}</strong>
                            <small>
                              {pet?.species} · {pet?.breed}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <strong>{client?.name}</strong>
                        <span className="table-secondary">{client?.phone}</span>
                      </td>
                      <td>
                        <strong>{appointment.type}</strong>
                        <span className="table-secondary">{appointment.notes || "—"}</span>
                        {appointment.reminderAt ? (
                          <span className="reminder-badge">
                            ◷ {friendlyDateTime(appointment.reminderAt)}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <strong>{appointment.doctor}</strong>
                        <span className="table-secondary">{appointment.room}</span>
                      </td>
                      <td>
                        <select
                          className={`status-select status-${statusTone[appointment.status]}`}
                          value={appointment.status}
                          onChange={(event) =>
                            onStatus(
                              appointment.id,
                              event.target.value as AppointmentStatus,
                            )
                          }
                        >
                          {["ממתין", "הגיע", "בטיפול", "הושלם", "בוטל"].map(
                            (option) => (
                              <option key={option}>{option}</option>
                            ),
                          )}
                        </select>
                      </td>
                      <td>
                        <strong>{money(appointment.price)}</strong>
                      </td>
                      <td>
                        <div className="row-actions">
                          <button onClick={() => onEdit(appointment.id)}>עריכה</button>
                          <button
                            className="danger-link"
                            onClick={() => onDelete(appointment.id)}
                          >
                            מחיקה
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon="◫"
            title="לא נמצאו תורים ביום הזה"
            text="בחרו תאריך אחר או הוסיפו תור חדש ליומן."
            action={
              <button
                className="button button-primary"
                onClick={() => onAdd(date)}
              >
                ＋ תור חדש
              </button>
            }
          />
        )}
      </article>
    </div>
  );
}

function ClientsPage({
  data,
  filters,
  setFilters,
  onAdd,
  onEdit,
  onView,
  onInvoice,
  onDelete,
  onExport,
}: {
  data: AppData;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onInvoice: (id: string) => void;
  onDelete: (id: string) => void;
  onExport: () => void;
}) {
  const query = filters.clientQuery || "";
  const status = filters.clientStatus || "הכול";
  const rows = data.clients.filter(
    (client) =>
      (status === "הכול" || client.status === status) &&
      `${client.name} ${client.phone} ${client.email} ${client.city}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="קשרי לקוחות"
        title="לקוחות ובעלים"
        description="פרטי קשר, מטופלים משויכים, העדפות תקשורת ויתרות."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button className="button button-primary" onClick={onAdd}>
              ＋ לקוח חדש
            </button>
          </>
        }
      />
      <ListToolbar
        query={query}
        onQuery={(value) => setFilters({ ...filters, clientQuery: value })}
        placeholder="חיפוש לפי שם, טלפון, מייל או עיר"
        filter={
          <select
            value={status}
            onChange={(event) =>
              setFilters({ ...filters, clientStatus: event.target.value })
            }
            aria-label="סינון לקוחות"
          >
            <option>הכול</option>
            <option>פעיל</option>
            <option>חדש</option>
            <option>לא פעיל</option>
          </select>
        }
        count={`${rows.length} לקוחות`}
      />
      <article className="card table-card">
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>לקוח</th>
                <th>פרטי קשר</th>
                <th>עיר</th>
                <th>מטופלים</th>
                <th>יתרה פתוחה</th>
                <th>תקשורת</th>
                <th>סטטוס</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((client) => {
                const pets = data.pets.filter((pet) => pet.ownerId === client.id);
                const balance = data.invoices
                  .filter((invoice) => invoice.clientId === client.id)
                  .reduce((sum, invoice) => sum + invoiceBalance(invoice), 0);
                return (
                  <tr key={client.id}>
                    <td>
                      <button
                        className="table-person person-button"
                        onClick={() => onView(client.id)}
                      >
                        <span>{client.name.slice(0, 2)}</span>
                        <div>
                          <strong>{client.name}</strong>
                          <small>מאז {friendlyDate(client.joinDate)}</small>
                        </div>
                      </button>
                    </td>
                    <td>
                      <strong>{client.phone}</strong>
                      <span className="table-secondary">{client.email}</span>
                    </td>
                    <td>{client.city}</td>
                    <td>
                      <div className="pet-pill-row">
                        {pets.map((pet) => (
                          <Pill key={pet.id} tone="mint">
                            {pet.name}
                          </Pill>
                        ))}
                        {!pets.length ? <span className="muted">ללא</span> : null}
                      </div>
                    </td>
                    <td>
                      <strong className={balance ? "text-danger" : ""}>
                        {money(balance)}
                      </strong>
                    </td>
                    <td>{client.preferredContact}</td>
                    <td>
                      <Pill tone={statusTone[client.status]}>{client.status}</Pill>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="invoice-client-action"
                          onClick={() => onInvoice(client.id)}
                        >
                          ＋ חשבונית
                        </button>
                        <button onClick={() => onView(client.id)}>כרטיס</button>
                        <button onClick={() => onEdit(client.id)}>עריכה</button>
                        <button
                          className="danger-link"
                          onClick={() => onDelete(client.id)}
                        >
                          מחיקה
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}

function PetsPage({
  data,
  filters,
  setFilters,
  clientById,
  onAdd,
  onEdit,
  onView,
  onDelete,
  onExport,
}: {
  data: AppData;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  clientById: (id: string) => ClientRecord | undefined;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
  onExport: () => void;
}) {
  const query = filters.petQuery || "";
  const species = filters.petSpecies || "הכול";
  const speciesOptions = [...new Set(data.pets.map((pet) => pet.species))];
  const rows = data.pets.filter(
    (pet) =>
      (species === "הכול" || pet.species === species) &&
      `${pet.name} ${pet.species} ${pet.breed} ${pet.chip} ${
        clientById(pet.ownerId)?.name
      }`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="המטופלים שלנו"
        title="בעלי חיים"
        description="כרטיס רפואי מרכזי עם חיסונים, רגישויות, ביקורים ובעלים."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button className="button button-primary" onClick={onAdd}>
              ＋ מטופל חדש
            </button>
          </>
        }
      />
      <ListToolbar
        query={query}
        onQuery={(value) => setFilters({ ...filters, petQuery: value })}
        placeholder="חיפוש לפי שם, שבב, גזע או בעלים"
        filter={
          <select
            value={species}
            onChange={(event) =>
              setFilters({ ...filters, petSpecies: event.target.value })
            }
            aria-label="סינון סוג בעל חיים"
          >
            <option>הכול</option>
            {speciesOptions.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        }
        count={`${rows.length} מטופלים`}
      />
      <div className="pet-card-grid">
        {rows.map((pet) => {
          const owner = clientById(pet.ownerId);
          const overdue = pet.nextVaccine && pet.nextVaccine < localDateISO();
          const near =
            pet.nextVaccine &&
            pet.nextVaccine <= addDays(data.clinic.reminderDays);
          return (
            <article className="card pet-card" key={pet.id}>
              <div className="pet-card-top">
                <div className="large-pet-avatar">{pet.name.slice(0, 1)}</div>
                <Pill tone={overdue ? "danger" : near ? "warning" : "success"}>
                  {overdue ? "חיסון באיחור" : near ? "חיסון קרוב" : "מעקב תקין"}
                </Pill>
                <button
                  className="round-more"
                  onClick={() => onEdit(pet.id)}
                  aria-label={`עריכת ${pet.name}`}
                >
                  •••
                </button>
              </div>
              <button className="pet-name-button" onClick={() => onView(pet.id)}>
                <strong>{pet.name}</strong>
                <span>
                  {pet.species} · {pet.breed} · {pet.age} שנים
                </span>
              </button>
              <div className="pet-owner">
                <span className="mini-avatar">{owner?.name.slice(0, 2)}</span>
                <div>
                  <small>בעלים</small>
                  <strong>{owner?.name}</strong>
                </div>
                <span>{owner?.phone}</span>
              </div>
              <div className="pet-metrics">
                <div>
                  <span>משקל</span>
                  <strong>{pet.weight} ק״ג</strong>
                </div>
                <div>
                  <span>שבב</span>
                  <strong>{pet.chip ? `••${pet.chip.slice(-4)}` : "ללא"}</strong>
                </div>
                <div>
                  <span>חיסון הבא</span>
                  <strong>{friendlyDate(pet.nextVaccine)}</strong>
                </div>
              </div>
              {pet.allergies !== "אין" ? (
                <div className="allergy-note">
                  <span>!</span> רגישות: {pet.allergies}
                </div>
              ) : null}
              <div className="pet-card-actions">
                <button onClick={() => onView(pet.id)}>פתיחת תיק רפואי</button>
                <button onClick={() => onEdit(pet.id)}>עריכה</button>
                <button className="danger-link" onClick={() => onDelete(pet.id)}>
                  מחיקה
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function RecordsPage({
  data,
  filters,
  setFilters,
  clientById,
  petById,
  onAdd,
  onEdit,
  onDelete,
  onViewPet,
  onExport,
}: {
  data: AppData;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  clientById: (id: string) => ClientRecord | undefined;
  petById: (id: string) => PetRecord | undefined;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onViewPet: (id: string) => void;
  onExport: () => void;
}) {
  const query = filters.recordQuery || "";
  const petFilter = filters.recordPet || "הכול";
  const rows = data.records
    .filter(
      (record) =>
        (petFilter === "הכול" || record.petId === petFilter) &&
        `${record.title} ${record.diagnosis} ${record.treatment} ${
          petById(record.petId)?.name
        }`
          .toLowerCase()
          .includes(query.toLowerCase()),
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="רצף טיפולי"
        title="רשומות רפואיות"
        description="אבחנות, טיפולים, תכניות מעקב והיסטוריה קלינית במקום אחד."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button className="button button-primary" onClick={onAdd}>
              ＋ רשומה רפואית
            </button>
          </>
        }
      />
      <ListToolbar
        query={query}
        onQuery={(value) => setFilters({ ...filters, recordQuery: value })}
        placeholder="חיפוש אבחנה, טיפול, רופא או מטופל"
        filter={
          <select
            value={petFilter}
            onChange={(event) =>
              setFilters({ ...filters, recordPet: event.target.value })
            }
            aria-label="סינון לפי מטופל"
          >
            <option>הכול</option>
            {data.pets.map((pet) => (
              <option key={pet.id} value={pet.id}>
                {pet.name} · {clientById(pet.ownerId)?.name}
              </option>
            ))}
          </select>
        }
        count={`${rows.length} רשומות`}
      />
      <article className="card records-card">
        <div className="records-list">
          {rows.map((record) => {
            const pet = petById(record.petId);
            const owner = pet ? clientById(pet.ownerId) : undefined;
            return (
              <div className="record-row" key={record.id}>
                <div className="record-date">
                  <strong>{new Date(`${record.date}T12:00:00`).getDate()}</strong>
                  <span>
                    {new Intl.DateTimeFormat("he-IL", { month: "short" }).format(
                      new Date(`${record.date}T12:00:00`),
                    )}
                  </span>
                </div>
                <div className="record-line">
                  <span />
                </div>
                <div className="record-body">
                  <div className="record-title">
                    <div>
                      <Pill tone="purple">{record.type}</Pill>
                      <button onClick={() => onViewPet(record.petId)}>
                        {record.title}
                      </button>
                    </div>
                    <div className="row-actions">
                      <button onClick={() => onEdit(record.id)}>עריכה</button>
                      <button
                        className="danger-link"
                        onClick={() => onDelete(record.id)}
                      >
                        מחיקה
                      </button>
                    </div>
                  </div>
                  <p>
                    <strong>{pet?.name}</strong> · {owner?.name} · {record.doctor}
                  </p>
                  <div className="clinical-grid">
                    <div>
                      <span>אבחנה</span>
                      <strong>{record.diagnosis}</strong>
                    </div>
                    <div>
                      <span>טיפול</span>
                      <strong>{record.treatment}</strong>
                    </div>
                    <div>
                      <span>המשך מעקב</span>
                      <strong>{record.nextSteps}</strong>
                    </div>
                  </div>
                  {record.attachments.length ? (
                    <div className="record-attachment-strip">
                      <span>{record.attachments.length} תמונות</span>
                      {record.attachments.slice(0, 5).map((attachment) => (
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noreferrer"
                          key={attachment.id}
                          title={attachment.name}
                        >
                          <MedicalThumbnail attachment={attachment} />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </article>
    </div>
  );
}

function BillingPage({
  data,
  filters,
  setFilters,
  clientById,
  onAdd,
  onEdit,
  onView,
  onPrint,
  onDelete,
  onPaid,
  onSend,
  onDuplicate,
  onCancel,
  onExport,
}: {
  data: AppData;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  clientById: (id: string) => ClientRecord | undefined;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onPrint: (id: string) => void;
  onDelete: (id: string) => void;
  onPaid: (id: string) => void;
  onSend: (id: string) => void;
  onDuplicate: (id: string) => void;
  onCancel: (id: string) => void;
  onExport: () => void;
}) {
  const query = filters.invoiceQuery || "";
  const status = filters.invoiceStatus || "הכול";
  const rows = data.invoices
    .filter(
      (invoice) =>
        (status === "הכול" ||
          invoice.status === status ||
          invoiceDisplayStatus(invoice) === status) &&
        `${invoice.id} ${invoice.documentType} ${invoice.clientName} ${
          clientById(invoice.clientId)?.name
        } ${invoice.notes}`
          .toLowerCase()
          .includes(query.toLowerCase()),
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  const totalOpen = data.invoices.reduce(
    (sum, invoice) => sum + invoiceBalance(invoice),
    0,
  );
  const collected = data.invoices.reduce(
    (sum, invoice) =>
      sum + (invoice.status === "מבוטלת" ? 0 : Number(invoice.paid)),
    0,
  );
  const vatToReport = data.invoices.reduce(
    (sum, invoice) =>
      sum + (invoice.status === "מבוטלת" ? 0 : invoiceVat(invoice)),
    0,
  );
  const overdueCount = data.invoices.filter(
    (invoice) => invoiceDisplayStatus(invoice) === "באיחור",
  ).length;
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="כספים"
        title="חיובים וחשבוניות"
        description="הפקת מסמכים ממוספרים, מע״מ, גבייה, תזכורות והדפסה ל־PDF."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button className="button button-primary" onClick={onAdd}>
              ＋ הפקת חשבונית
            </button>
          </>
        }
      />
      <section className="card invoice-howto">
        <div className="invoice-howto-copy">
          <span className="quick-icon mint">₪</span>
          <div>
            <span className="eyebrow">כך מפיקים חשבונית ללקוח</span>
            <strong>בוחרים לקוח, מוסיפים טיפול ומפיקים מסמך</strong>
            <p>
              אפשר להתחיל כאן, או ללחוץ על “＋ חשבונית” ליד כל לקוח בעמוד
              הלקוחות.
            </p>
          </div>
        </div>
        <ol>
          <li><b>1</b><span>בחירת לקוח או תור</span></li>
          <li><b>2</b><span>הוספת פריטי חיוב</span></li>
          <li><b>3</b><span>הפקה והדפסה</span></li>
        </ol>
        <button className="button button-primary" onClick={onAdd}>
          ＋ הפקת חשבונית ללקוח
        </button>
      </section>
      <div className="billing-summary">
        <div className="card">
          <span>חוב פתוח</span>
          <strong className="text-danger">{money(totalOpen)}</strong>
          <small>{data.invoices.filter((item) => invoiceBalance(item) > 0).length} חשבוניות</small>
        </div>
        <div className="card">
          <span>נגבה עד היום</span>
          <strong>{money(collected)}</strong>
          <small>מכל אמצעי התשלום</small>
        </div>
        <div className="card">
          <span>אחוז גבייה</span>
          <strong>
            {Math.round(
              (collected /
                Math.max(
                  1,
                  data.invoices.reduce(
                    (sum, invoice) => sum + invoiceTotal(invoice),
                    0,
                  ),
                )) *
                100,
            )}
            %
          </strong>
          <small>מתוך סך החיובים</small>
        </div>
        <div className="card">
          <span>מע״מ עסקאות</span>
          <strong>{money(vatToReport)}</strong>
          <small>{overdueCount ? `${overdueCount} מסמכים באיחור` : "אין מסמכים באיחור"}</small>
        </div>
      </div>
      <ListToolbar
        query={query}
        onQuery={(value) => setFilters({ ...filters, invoiceQuery: value })}
        placeholder="חיפוש לפי מספר, לקוח או הערה"
        filter={
          <select
            value={status}
            onChange={(event) =>
              setFilters({ ...filters, invoiceStatus: event.target.value })
            }
            aria-label="סינון חשבוניות"
          >
            <option>הכול</option>
            <option>טיוטה</option>
            <option>נשלחה</option>
            <option>חוב פתוח</option>
            <option>שולמה</option>
            <option>באיחור</option>
            <option>מבוטלת</option>
          </select>
        }
        count={`${rows.length} חשבוניות`}
      />
      <article className="card table-card">
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>מסמך</th>
                <th>לקוח</th>
                <th>הפקה / תשלום</th>
                <th>סה״כ</th>
                <th>יתרה</th>
                <th>סטטוס</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((invoice) => {
                const displayStatus = invoiceDisplayStatus(invoice);
                return (
                  <tr key={invoice.id}>
                    <td>
                      <button
                        className="invoice-link"
                        onClick={() => onView(invoice.id)}
                      >
                        {invoice.id}
                      </button>
                      <span className="table-secondary">
                        {invoice.documentType} · {invoice.items.length} פריטים
                      </span>
                    </td>
                    <td>
                      <strong>
                        {invoice.clientName ||
                          clientById(invoice.clientId)?.name}
                      </strong>
                      <span className="table-secondary">
                        {invoice.clientTaxId || "ללא ח.פ / ת.ז"}
                      </span>
                    </td>
                    <td>
                      <span className="table-primary">
                        {friendlyDate(invoice.date)}
                      </span>
                      <span className="table-secondary">
                        עד {friendlyDate(invoice.dueDate)}
                      </span>
                    </td>
                    <td>
                      <strong>{money(invoiceTotal(invoice))}</strong>
                      <span className="table-secondary">
                        מע״מ {money(invoiceVat(invoice))}
                      </span>
                    </td>
                    <td>
                      <strong
                        className={
                          invoiceBalance(invoice) ? "text-danger" : ""
                        }
                      >
                        {money(invoiceBalance(invoice))}
                      </strong>
                      <span className="table-secondary">
                        שולם {money(invoice.paid)}
                      </span>
                    </td>
                    <td>
                      <Pill tone={statusTone[displayStatus]}>
                        {displayStatus}
                      </Pill>
                    </td>
                    <td>
                      <div className="row-actions invoice-row-actions">
                        <button onClick={() => onView(invoice.id)}>צפייה</button>
                        <button
                          className="invoice-row-print"
                          onClick={() => onPrint(invoice.id)}
                          aria-label={`הדפסת ${invoice.documentType} ${invoice.id}`}
                        >
                          ⎙ הדפסה / PDF
                        </button>
                        <button onClick={() => onEdit(invoice.id)}>עריכה</button>
                        {invoice.status === "טיוטה" ? (
                          <button onClick={() => onSend(invoice.id)}>
                            סמן כנשלחה
                          </button>
                        ) : null}
                        {invoice.status !== "שולמה" &&
                        invoice.status !== "מבוטלת" ? (
                          <button onClick={() => onPaid(invoice.id)}>שולם</button>
                        ) : null}
                        <button onClick={() => onDuplicate(invoice.id)}>
                          שכפול
                        </button>
                        {invoice.status !== "מבוטלת" ? (
                          <button
                            className="danger-link"
                            onClick={() => onCancel(invoice.id)}
                          >
                            ביטול
                          </button>
                        ) : (
                          <button
                            className="danger-link"
                            onClick={() => onDelete(invoice.id)}
                          >
                            מחיקה
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}

function InventoryPage({
  data,
  filters,
  setFilters,
  onAdd,
  onEdit,
  onDelete,
  onAdjust,
  onExport,
}: {
  data: AppData;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onAdjust: (id: string, delta: number) => void;
  onExport: () => void;
}) {
  const query = filters.stockQuery || "";
  const lowOnly = filters.stockLow === "yes";
  const rows = data.inventory.filter(
    (item) =>
      (!lowOnly || Number(item.stock) <= Number(item.minStock)) &&
      `${item.name} ${item.category} ${item.supplier}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const inventoryValue = data.inventory.reduce(
    (sum, item) => sum + item.stock * item.price,
    0,
  );
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="תפעול"
        title="מלאי וציוד"
        description="כמויות, מלאי מינימום, תוקף, ספקים ועלות מלאי."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button className="button button-primary" onClick={onAdd}>
              ＋ פריט חדש
            </button>
          </>
        }
      />
      <div className="inventory-banner card">
        <div>
          <span className="stat-icon stat-amber">□</span>
          <div>
            <strong>
              {
                data.inventory.filter(
                  (item) => Number(item.stock) <= Number(item.minStock),
                ).length
              }{" "}
              פריטים דורשים הזמנה
            </strong>
            <span>כדאי לרכז הזמנה מול הספקים עוד היום.</span>
          </div>
        </div>
        <div>
          <span>שווי מלאי</span>
          <strong>{money(inventoryValue)}</strong>
        </div>
      </div>
      <ListToolbar
        query={query}
        onQuery={(value) => setFilters({ ...filters, stockQuery: value })}
        placeholder="חיפוש פריט, קטגוריה או ספק"
        filter={
          <label className="checkbox-filter">
            <input
              type="checkbox"
              checked={lowOnly}
              onChange={(event) =>
                setFilters({
                  ...filters,
                  stockLow: event.target.checked ? "yes" : "",
                })
              }
            />
            מלאי נמוך בלבד
          </label>
        }
        count={`${rows.length} פריטים`}
      />
      <article className="card table-card">
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>פריט</th>
                <th>קטגוריה</th>
                <th>כמות</th>
                <th>מינימום</th>
                <th>ספק</th>
                <th>תוקף</th>
                <th>עלות</th>
                <th>סטטוס</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const low = Number(item.stock) <= Number(item.minStock);
                const expiring = item.expiry && item.expiry <= addDays(90);
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="table-person">
                        <span className="box-avatar">□</span>
                        <div>
                          <strong>{item.name}</strong>
                          <small>{item.unit}</small>
                        </div>
                      </div>
                    </td>
                    <td>{item.category}</td>
                    <td>
                      <div className="stock-stepper">
                        <button onClick={() => onAdjust(item.id, -1)}>−</button>
                        <strong>{item.stock}</strong>
                        <button onClick={() => onAdjust(item.id, 1)}>＋</button>
                      </div>
                    </td>
                    <td>{item.minStock}</td>
                    <td>{item.supplier}</td>
                    <td>
                      <span className={expiring ? "text-warning" : ""}>
                        {friendlyDate(item.expiry)}
                      </span>
                    </td>
                    <td>{money(item.price)}</td>
                    <td>
                      <Pill tone={low ? "danger" : "success"}>
                        {low ? "להזמין" : "תקין"}
                      </Pill>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button onClick={() => onEdit(item.id)}>עריכה</button>
                        <button
                          className="danger-link"
                          onClick={() => onDelete(item.id)}
                        >
                          מחיקה
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}

function TasksPage({
  data,
  filters,
  setFilters,
  petById,
  onAdd,
  onEdit,
  onDelete,
  onToggle,
  onExport,
}: {
  data: AppData;
  filters: Record<string, string>;
  setFilters: (filters: Record<string, string>) => void;
  petById: (id: string) => PetRecord | undefined;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  onExport: () => void;
}) {
  const query = filters.taskQuery || "";
  const status = filters.taskStatus || "הכול";
  const rows = data.tasks
    .filter(
      (task) =>
        (status === "הכול" || task.status === status) &&
        `${task.text} ${task.owner} ${petById(task.petId)?.name}`
          .toLowerCase()
          .includes(query.toLowerCase()),
    )
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="תיאום צוות"
        title="משימות ומעקב"
        description="משימות קליניות ותפעוליות, עדיפויות, אחריות ותאריכי יעד."
        actions={
          <>
            <button className="button button-secondary" onClick={onExport}>
              ↓ ייצוא
            </button>
            <button className="button button-primary" onClick={onAdd}>
              ＋ משימה חדשה
            </button>
          </>
        }
      />
      <ListToolbar
        query={query}
        onQuery={(value) => setFilters({ ...filters, taskQuery: value })}
        placeholder="חיפוש משימה, איש צוות או מטופל"
        filter={
          <select
            value={status}
            onChange={(event) =>
              setFilters({ ...filters, taskStatus: event.target.value })
            }
            aria-label="סינון משימות"
          >
            <option>הכול</option>
            <option>פתוחה</option>
            <option>בוצעה</option>
          </select>
        }
        count={`${rows.length} משימות`}
      />
      <div className="task-board">
        {(["דחופה", "גבוהה", "רגילה", "נמוכה"] as const).map((priority) => {
          const group = rows.filter((task) => task.priority === priority);
          return (
            <section className="card task-column" key={priority}>
              <CardHeader
                title={priority}
                action={<Pill tone={statusTone[priority]}>{group.length}</Pill>}
              />
              <div className="task-column-list">
                {group.map((task) => (
                  <article
                    className={`task-ticket ${
                      task.status === "בוצעה" ? "task-done" : ""
                    }`}
                    key={task.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`עריכת המשימה: ${task.text}`}
                    onClick={() => onEdit(task.id)}
                    onKeyDown={(event) => {
                      if (
                        event.currentTarget !== event.target ||
                        (event.key !== "Enter" && event.key !== " ")
                      ) {
                        return;
                      }
                      event.preventDefault();
                      onEdit(task.id);
                    }}
                  >
                    <div className="task-ticket-top">
                      <button
                        className="task-check"
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggle(task.id);
                        }}
                        aria-label="שינוי סטטוס משימה"
                      >
                        {task.status === "בוצעה" ? "✓" : ""}
                      </button>
                      <div className="row-actions">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            onEdit(task.id);
                          }}
                        >
                          עריכה
                        </button>
                        <button
                          className="danger-link"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDelete(task.id);
                          }}
                        >
                          מחיקה
                        </button>
                      </div>
                    </div>
                    <strong>{task.text}</strong>
                    {task.petId ? (
                      <Pill tone="mint">{petById(task.petId)?.name}</Pill>
                    ) : null}
                    {task.reminderAt ? (
                      <span className="reminder-badge">
                        ◷ {friendlyDateTime(task.reminderAt)}
                      </span>
                    ) : null}
                    <div className="task-ticket-meta">
                      <span>{friendlyDate(task.dueDate)}</span>
                      <span>{task.owner}</span>
                    </div>
                  </article>
                ))}
                {!group.length ? (
                  <div className="column-empty">אין משימות</div>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

type MarketingTab =
  | "overview"
  | "campaigns"
  | "planner"
  | "studio"
  | "audiences"
  | "analytics"
  | "connections";

function MarketingPage({
  data,
  setData,
  onToast,
}: {
  data: AppData;
  setData: React.Dispatch<React.SetStateAction<AppData>>;
  onToast: (message: string) => void;
}) {
  const [tab, setTab] = useState<MarketingTab>("overview");
  const [platform, setPlatform] = useState("הכול");
  const [campaignStatus, setCampaignStatus] = useState("הכול");
  const [campaignQuery, setCampaignQuery] = useState("");
  const [marketingNow] = useState(() => Date.now());
  const [campaignModal, setCampaignModal] = useState<string | null>(null);
  const [assetModal, setAssetModal] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const totalSpent = data.marketingCampaigns.reduce(
    (sum, campaign) => sum + campaign.spent,
    0,
  );
  const totalResults = data.marketingCampaigns.reduce(
    (sum, campaign) => sum + campaign.results,
    0,
  );
  const averageRoas =
    totalSpent > 0
      ? data.marketingCampaigns.reduce(
          (sum, campaign) => sum + campaign.roas * campaign.spent,
          0,
        ) / totalSpent
      : 0;
  const costPerResult = totalResults ? totalSpent / totalResults : 0;
  const totalLeads = data.marketingCampaigns.reduce(
    (sum, campaign) => sum + Number(campaign.leads || 0),
    0,
  );
  const totalAppointments = data.marketingCampaigns.reduce(
    (sum, campaign) => sum + Number(campaign.appointments || 0),
    0,
  );
  const totalRevenue = data.marketingCampaigns.reduce(
    (sum, campaign) => sum + Number(campaign.revenue || 0),
    0,
  );
  const leadToAppointmentRate = totalLeads
    ? (totalAppointments / totalLeads) * 100
    : 0;
  const activeCampaigns = data.marketingCampaigns.filter(
    (campaign) => campaign.status === "פעיל",
  );
  const platforms = [
    "הכול",
    ...Array.from(
      new Set(data.marketingCampaigns.map((campaign) => campaign.platform)),
    ),
  ];
  const visibleCampaigns = data.marketingCampaigns
    .filter(
      (campaign) => platform === "הכול" || campaign.platform === platform,
    )
    .filter(
      (campaign) =>
        campaignStatus === "הכול" || campaign.status === campaignStatus,
    )
    .filter((campaign) =>
      `${campaign.name} ${campaign.objective} ${campaign.audience} ${campaign.offer}`
        .toLowerCase()
        .includes(campaignQuery.toLowerCase()),
    );
  const scheduledActions = data.marketingCampaigns
    .filter((campaign) => campaign.nextAction && campaign.nextActionDate)
    .sort((a, b) =>
      String(a.nextActionDate).localeCompare(String(b.nextActionDate)),
    );
  const selectedCampaign = data.marketingCampaigns.find(
    (campaign) => campaign.id === detailId,
  );
  const editingCampaign = data.marketingCampaigns.find(
    (campaign) => campaign.id === campaignModal,
  );
  const editingAsset = data.marketingAssets.find(
    (asset) => asset.id === assetModal,
  );

  const tabs: Array<{ id: MarketingTab; label: string; count?: number }> = [
    { id: "overview", label: "תמונת מצב" },
    {
      id: "campaigns",
      label: "קמפיינים",
      count: data.marketingCampaigns.length,
    },
    {
      id: "planner",
      label: "תכנית עבודה",
      count: scheduledActions.length,
    },
    { id: "studio", label: "סטודיו תוכן", count: data.marketingAssets.length },
    { id: "audiences", label: "קהלים", count: data.marketingAudiences.length },
    { id: "analytics", label: "ביצועים" },
    { id: "connections", label: "חיבורים" },
  ];

  const platformStats = Array.from(
    data.marketingCampaigns.reduce<
      Map<string, { spent: number; results: number; revenue: number }>
    >((map, campaign) => {
      const current = map.get(campaign.platform) || {
        spent: 0,
        results: 0,
        revenue: 0,
      };
      map.set(campaign.platform, {
        spent: current.spent + campaign.spent,
        results: current.results + campaign.results,
        revenue: current.revenue + campaign.spent * campaign.roas,
      });
      return map;
    }, new Map()),
  )
    .map(([name, values]) => ({
      name,
      ...values,
      roas: values.spent ? values.revenue / values.spent : 0,
    }))
    .sort((a, b) => b.roas - a.roas);

  const saveCampaign = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const existing = editingCampaign;
    const campaign: MarketingCampaign = {
      id: existing?.id || uid("mc"),
      name: String(form.get("name") || ""),
      platform: String(form.get("platform") || "Meta"),
      objective: String(form.get("objective") || ""),
      audience: String(form.get("audience") || ""),
      offer: String(form.get("offer") || ""),
      landingPage: String(form.get("landingPage") || ""),
      keywords: String(form.get("keywords") || "")
        .split(",")
        .map((keyword) => keyword.trim())
        .filter(Boolean),
      budget: Number(form.get("budget") || 0),
      spent: Number(form.get("spent") || 0),
      results: Number(form.get("leads") || 0),
      status: String(form.get("status") || "טיוטה") as MarketingCampaignStatus,
      roas:
        Number(form.get("spent") || 0) > 0
          ? Number(form.get("revenue") || 0) /
            Number(form.get("spent") || 0)
          : 0,
      startDate: String(form.get("startDate") || localDateISO()),
      endDate: String(form.get("endDate") || addDays(30)),
      leads: Number(form.get("leads") || 0),
      appointments: Number(form.get("appointments") || 0),
      revenue: Number(form.get("revenue") || 0),
      nextAction: String(form.get("nextAction") || ""),
      nextActionDate: String(form.get("nextActionDate") || ""),
    };
    setData((current) => ({
      ...current,
      marketingCampaigns: existing
        ? current.marketingCampaigns.map((item) =>
            item.id === existing.id ? campaign : item,
          )
        : [campaign, ...current.marketingCampaigns],
    }));
    setCampaignModal(null);
    onToast(existing ? "הקמפיין עודכן" : "קמפיין חדש נשמר כטיוטה");
  };

  const saveAsset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const existing = editingAsset;
    const asset: MarketingAsset = {
      id: existing?.id || uid("ma"),
      campaignId: String(form.get("campaignId") || ""),
      type: String(form.get("type") || "פוסט"),
      title: String(form.get("title") || ""),
      format: String(form.get("format") || ""),
      status: String(form.get("status") || "טיוטה") as MarketingAsset["status"],
      tone: String(form.get("tone") || "mint") as MarketingAsset["tone"],
      headline: String(form.get("headline") || ""),
      body: String(form.get("body") || ""),
      cta: String(form.get("cta") || ""),
    };
    setData((current) => ({
      ...current,
      marketingAssets: existing
        ? current.marketingAssets.map((item) =>
            item.id === existing.id ? asset : item,
          )
        : [asset, ...current.marketingAssets],
    }));
    setAssetModal(null);
    onToast(existing ? "התוכן עודכן" : "פריט תוכן חדש נוסף לסטודיו");
  };

  const toggleCampaign = (id: string) => {
    setData((current) => ({
      ...current,
      marketingCampaigns: current.marketingCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              status: campaign.status === "פעיל" ? "מושהה" : "פעיל",
            }
          : campaign,
      ),
    }));
    onToast("סטטוס הקמפיין עודכן");
  };

  const completeCampaignAction = (id: string) => {
    setData((current) => ({
      ...current,
      marketingCampaigns: current.marketingCampaigns.map((campaign) =>
        campaign.id === id
          ? { ...campaign, nextAction: "", nextActionDate: "" }
          : campaign,
      ),
    }));
    onToast("הפעולה סומנה כבוצעה");
  };

  const duplicateCampaign = (campaign: MarketingCampaign) => {
    setData((current) => ({
      ...current,
      marketingCampaigns: [
        {
          ...campaign,
          id: uid("mc"),
          name: `${campaign.name} — עותק`,
          status: "טיוטה",
          spent: 0,
          results: 0,
          roas: 0,
          leads: 0,
          appointments: 0,
          revenue: 0,
          nextAction: "בדיקת קריאייטיב ומסרים",
          nextActionDate: addDays(2),
        },
        ...current.marketingCampaigns,
      ],
    }));
    onToast("עותק חדש נשמר כטיוטה");
  };

  const duplicateAsset = (asset: MarketingAsset) => {
    setData((current) => ({
      ...current,
      marketingAssets: [
        {
          ...asset,
          id: uid("ma"),
          title: `${asset.title} — עותק`,
          status: "טיוטה",
        },
        ...current.marketingAssets,
      ],
    }));
    onToast("התוכן שוכפל");
  };

  const exportMarketing = () => {
    const rows = [
      [
        "קמפיין",
        "ערוץ",
        "סטטוס",
        "תקציב יומי",
        "הוצאה",
        "לידים",
        "תורים",
        "הכנסה",
        "ROAS",
        "הפעולה הבאה",
        "תאריך פעולה",
      ],
      ...data.marketingCampaigns.map((campaign) => [
        campaign.name,
        campaign.platform,
        campaign.status,
        campaign.budget,
        campaign.spent,
        campaign.leads || 0,
        campaign.appointments || 0,
        campaign.revenue || 0,
        campaign.roas,
        campaign.nextAction || "",
        campaign.nextActionDate || "",
      ]),
    ];
    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `vetmanage-marketing-${localDateISO()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    onToast("דוח השיווק הורד");
  };

  const campaignPacing = (campaign: MarketingCampaign) => {
    const start = new Date(`${campaign.startDate}T12:00:00`).getTime();
    const end = Math.min(
      marketingNow,
      new Date(`${campaign.endDate}T12:00:00`).getTime(),
    );
    const activeDays = Math.max(
      1,
      Math.ceil((Math.max(start, end) - start) / 86_400_000) + 1,
    );
    const plannedSpend = Math.max(1, Number(campaign.budget) * activeDays);
    return Math.round((Number(campaign.spent) / plannedSpend) * 100);
  };

  const renderOverview = () => (
    <>
      <section className="marketing-hero card">
        <div className="marketing-hero-copy">
          <span className="marketing-ai-label">✦ המלצת הצמיחה של היום</span>
          <h3>להרחיב את קמפיין הסניורים לערוץ Meta</h3>
          <p>
            הקמפיין משיג תשואה של 4.8 בחיפוש. קהל דומה ללקוחות שכבר קבעו בדיקה
            עשוי להוסיף כ־18 תורים בחודש בתקציב יומי של 45 ₪.
          </p>
          <div className="marketing-hero-actions">
            <button
              className="button button-primary"
              onClick={() => {
                setTab("campaigns");
                setCampaignModal("new");
              }}
            >
              יצירת קמפיין משלים
            </button>
            <button
              className="button button-secondary"
              onClick={() => setTab("audiences")}
            >
              צפייה בקהל המומלץ
            </button>
          </div>
        </div>
        <div className="marketing-score">
          <span>ציון הזדמנות</span>
          <strong>92</strong>
          <small>גבוה מאוד</small>
        </div>
      </section>

      <div className="marketing-kpis">
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon mint">₪</span>
          <div>
            <small>הוצאה כוללת</small>
            <strong>{money(totalSpent)}</strong>
            <span>בכל הקמפיינים</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon purple">↗</span>
          <div>
            <small>תשואה ממוצעת</small>
            <strong>{averageRoas.toFixed(1)}×</strong>
            <span className="marketing-positive">+12% מהחודש הקודם</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon coral">✓</span>
          <div>
            <small>תוצאות</small>
            <strong>{totalResults}</strong>
            <span>לידים, הודעות ותורים</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon amber">◎</span>
          <div>
            <small>עלות לתוצאה</small>
            <strong>{money(costPerResult)}</strong>
            <span className="marketing-positive">יעיל ב־9% מהיעד</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon blue">◫</span>
          <div>
            <small>תורים משיווק</small>
            <strong>{totalAppointments}</strong>
            <span>{leadToAppointmentRate.toFixed(0)}% המרה מליד לתור</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon mint">↗</span>
          <div>
            <small>הכנסה מיוחסת</small>
            <strong>{money(totalRevenue)}</strong>
            <span>לפי נתוני הקמפיינים</span>
          </div>
        </article>
      </div>

      <div className="marketing-overview-grid">
        <article className="card marketing-performance-card">
          <CardHeader
            eyebrow="30 הימים האחרונים"
            title="ביצועים לאורך זמן"
            action={<Pill tone="success">{activeCampaigns.length} פעילים</Pill>}
          />
          <div className="marketing-chart">
            {[38, 54, 46, 66, 59, 78, 72, 91, 83, 100, 94, 112].map(
              (value, index) => (
                <i
                  key={`${value}-${index}`}
                  style={{ height: `${Math.max(18, value * 0.75)}px` }}
                  title={`${value} תוצאות`}
                />
              ),
            )}
          </div>
          <div className="marketing-chart-axis">
            <span>18 ביוני</span>
            <span>היום</span>
          </div>
        </article>
        <article className="card marketing-actions-card">
          <CardHeader eyebrow="מיקוד" title="הפעולות הבאות" />
          {scheduledActions.slice(0, 2).map((campaign, index) => (
            <button
              key={campaign.id}
              onClick={() => {
                setTab("planner");
                setDetailId(campaign.id);
              }}
            >
              <span className={`quick-icon ${index ? "mint" : "purple"}`}>
                {index ? "◎" : "✓"}
              </span>
              <div>
                <strong>{campaign.nextAction}</strong>
                <small>
                  {campaign.name} · {friendlyDate(campaign.nextActionDate || "")}
                </small>
              </div>
              <b>←</b>
            </button>
          ))}
          <button onClick={() => setTab("connections")}>
            <span className="quick-icon amber">@</span>
            <div>
              <strong>לחבר ערוץ Email</strong>
              <small>להרחבת תזכורות ותוכן רפואי</small>
            </div>
            <b>←</b>
          </button>
        </article>
      </div>

      <article className="card table-card marketing-campaigns-preview">
        <CardHeader
          eyebrow="פעילות חיה"
          title="קמפיינים מובילים"
          action={
            <button className="text-button" onClick={() => setTab("campaigns")}>
              לכל הקמפיינים ←
            </button>
          }
        />
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>קמפיין</th>
                <th>ערוץ</th>
                <th>סטטוס</th>
                <th>הוצאה</th>
                <th>תוצאות</th>
                <th>ROAS</th>
              </tr>
            </thead>
            <tbody>
              {data.marketingCampaigns.slice(0, 4).map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    <button
                      className="person-button"
                      onClick={() => setDetailId(campaign.id)}
                    >
                      <span className="table-primary">{campaign.name}</span>
                      <span className="table-secondary">{campaign.objective}</span>
                    </button>
                  </td>
                  <td>{campaign.platform}</td>
                  <td>
                    <Pill tone={statusTone[campaign.status]}>
                      {campaign.status}
                    </Pill>
                  </td>
                  <td>{money(campaign.spent)}</td>
                  <td>
                    <strong>{campaign.results}</strong>
                  </td>
                  <td>
                    <strong>{campaign.roas.toFixed(1)}×</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </>
  );

  const renderCampaigns = () => (
    <>
      <div className="marketing-filterbar card">
        <div className="marketing-filter-tools">
          <div className="search-box">
            <span>⌕</span>
            <input
              value={campaignQuery}
              onChange={(event) => setCampaignQuery(event.target.value)}
              placeholder="חיפוש קמפיין, קהל או הצעה"
              aria-label="חיפוש קמפיינים"
            />
          </div>
          <select
            value={campaignStatus}
            onChange={(event) => setCampaignStatus(event.target.value)}
            aria-label="סינון קמפיינים לפי סטטוס"
          >
            <option>הכול</option>
            <option>טיוטה</option>
            <option>בבדיקה</option>
            <option>פעיל</option>
            <option>מושהה</option>
          </select>
          <div className="marketing-platforms">
            {platforms.map((item) => (
              <button
                key={item}
                className={platform === item ? "active" : ""}
                onClick={() => setPlatform(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="marketing-filter-summary">
          <span>{visibleCampaigns.length} קמפיינים</span>
          <strong>{money(totalSpent)}</strong>
          <small>הוצאה מצטברת</small>
        </div>
      </div>
      <article className="card table-card">
        {visibleCampaigns.length ? (
          <div className="responsive-table">
            <table className="marketing-table">
              <thead>
                <tr>
                  <th>קמפיין</th>
                  <th>ערוץ</th>
                  <th>סטטוס</th>
                  <th>תקציב יומי</th>
                  <th>הוצאה</th>
                  <th>לידים / תורים</th>
                  <th>ROAS</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {visibleCampaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>
                      <button
                        className="person-button"
                        onClick={() => setDetailId(campaign.id)}
                      >
                        <span className="table-primary">{campaign.name}</span>
                        <span className="table-secondary">
                          {campaign.objective} · עד {friendlyDate(campaign.endDate)}
                        </span>
                      </button>
                    </td>
                    <td>
                      <span className="marketing-channel">
                        {campaign.platform.slice(0, 1)}
                      </span>
                      {campaign.platform}
                    </td>
                    <td>
                      <Pill tone={statusTone[campaign.status]}>
                        {campaign.status}
                      </Pill>
                    </td>
                    <td>
                      <strong>{money(campaign.budget)}</strong>
                      <span
                        className={`campaign-pacing ${
                          campaignPacing(campaign) > 115 ? "over" : ""
                        }`}
                      >
                        {campaignPacing(campaign)}% מקצב התקציב
                      </span>
                    </td>
                    <td>{money(campaign.spent)}</td>
                    <td>
                      <strong>
                        {campaign.leads || 0} / {campaign.appointments || 0}
                      </strong>
                    </td>
                    <td>
                      <strong>{campaign.roas.toFixed(1)}×</strong>
                    </td>
                    <td>
                      <div className="row-actions marketing-row-actions">
                        <button onClick={() => setDetailId(campaign.id)}>
                          פירוט
                        </button>
                        <button onClick={() => setCampaignModal(campaign.id)}>
                          עריכה
                        </button>
                        <button onClick={() => toggleCampaign(campaign.id)}>
                          {campaign.status === "פעיל" ? "השהיה" : "הפעלה"}
                        </button>
                        <button onClick={() => duplicateCampaign(campaign)}>
                          שכפול
                        </button>
                        <button
                          className="danger-link"
                          onClick={() => {
                            if (window.confirm("למחוק את הקמפיין?")) {
                              setData((current) => ({
                                ...current,
                                marketingCampaigns:
                                  current.marketingCampaigns.filter(
                                    (item) => item.id !== campaign.id,
                                  ),
                              }));
                              onToast("הקמפיין נמחק");
                            }
                          }}
                        >
                          מחיקה
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon="✦"
            title="אין קמפיינים בערוץ הזה"
            text="אפשר לפתוח קמפיין חדש ולבחור את הערוץ המתאים."
            action={
              <button
                className="button button-primary"
                onClick={() => setCampaignModal("new")}
              >
                קמפיין חדש
              </button>
            }
          />
        )}
      </article>
    </>
  );

  const renderPlanner = () => (
    <>
      <section className="marketing-plan-summary">
        <article className="card">
          <span>פעולות מתוזמנות</span>
          <strong>{scheduledActions.length}</strong>
          <small>הפעולות הבאות של צוות השיווק</small>
        </article>
        <article className="card">
          <span>קמפיינים פעילים</span>
          <strong>{activeCampaigns.length}</strong>
          <small>מתוך {data.marketingCampaigns.length} קמפיינים</small>
        </article>
        <article className="card">
          <span>המרה מליד לתור</span>
          <strong>{leadToAppointmentRate.toFixed(0)}%</strong>
          <small>{totalAppointments} תורים מתוך {totalLeads} לידים</small>
        </article>
      </section>
      <div className="marketing-planner-grid">
        <article className="card marketing-action-plan">
          <CardHeader
            eyebrow="סדר יום שיווקי"
            title="פעולות מתוזמנות"
            action={
              <button
                className="text-button"
                onClick={() => setCampaignModal("new")}
              >
                + קמפיין ופעולה
              </button>
            }
          />
          {scheduledActions.length ? (
            <div className="marketing-action-list">
              {scheduledActions.map((campaign) => {
                const overdue =
                  String(campaign.nextActionDate) < localDateISO();
                return (
                  <div
                    className={overdue ? "action-overdue" : ""}
                    key={campaign.id}
                  >
                    <span className="marketing-action-date">
                      <b>
                        {new Date(
                          `${campaign.nextActionDate}T12:00:00`,
                        ).getDate()}
                      </b>
                      <small>
                        {new Intl.DateTimeFormat("he-IL", {
                          month: "short",
                        }).format(
                          new Date(`${campaign.nextActionDate}T12:00:00`),
                        )}
                      </small>
                    </span>
                    <button
                      className="marketing-action-copy"
                      onClick={() => setDetailId(campaign.id)}
                    >
                      <strong>{campaign.nextAction}</strong>
                      <small>
                        {campaign.name} · {campaign.platform}
                      </small>
                    </button>
                    <Pill tone={overdue ? "danger" : "info"}>
                      {overdue ? "באיחור" : campaign.status}
                    </Pill>
                    <button
                      className="action-complete"
                      onClick={() => completeCampaignAction(campaign.id)}
                    >
                      ✓ בוצע
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon="✓"
              title="אין פעולות פתוחות"
              text="אפשר לערוך קמפיין ולהגדיר בו את הפעולה והתאריך הבאים."
            />
          )}
        </article>
        <article className="card marketing-roadmap">
          <CardHeader eyebrow="לוח קמפיינים" title="ציר זמן" />
          {[...data.marketingCampaigns]
            .sort((a, b) => a.startDate.localeCompare(b.startDate))
            .map((campaign) => {
              const start = new Date(`${campaign.startDate}T12:00:00`).getTime();
              const end = new Date(`${campaign.endDate}T12:00:00`).getTime();
              const today = marketingNow;
              const progress = Math.max(
                0,
                Math.min(100, ((today - start) / Math.max(1, end - start)) * 100),
              );
              return (
                <button
                  className="marketing-roadmap-row"
                  key={campaign.id}
                  onClick={() => setDetailId(campaign.id)}
                >
                  <span className="marketing-channel">
                    {campaign.platform.slice(0, 1)}
                  </span>
                  <span>
                    <strong>{campaign.name}</strong>
                    <small>
                      {friendlyDate(campaign.startDate)} —{" "}
                      {friendlyDate(campaign.endDate)}
                    </small>
                    <i>
                      <b style={{ width: `${progress}%` }} />
                    </i>
                  </span>
                  <Pill tone={statusTone[campaign.status]}>
                    {campaign.status}
                  </Pill>
                </button>
              );
            })}
        </article>
      </div>
    </>
  );

  const renderStudio = () => (
    <>
      <section className="studio-intro card">
        <div>
          <span className="eyebrow">קריאייטיב שמוכן לעבוד</span>
          <h3>כל המסרים של המרפאה במקום אחד</h3>
          <p>
            דפי נחיתה, מודעות, פוסטים והודעות — עם תצוגה מקדימה, סטטוס וחיבור
            לקמפיין.
          </p>
        </div>
        <button
          className="button button-primary"
          onClick={() => setAssetModal("new")}
        >
          + תוכן חדש
        </button>
      </section>
      <div className="asset-grid">
        {data.marketingAssets.map((asset) => {
          const campaign = data.marketingCampaigns.find(
            (item) => item.id === asset.campaignId,
          );
          return (
            <article className="card asset-card" key={asset.id}>
              <div className={`asset-preview asset-${asset.tone}`}>
                <span>{asset.type}</span>
                <strong>{asset.headline}</strong>
                <p>{asset.body}</p>
                <b>{asset.cta} ←</b>
              </div>
              <div className="asset-card-body">
                <div className="asset-meta">
                  <Pill tone={statusTone[asset.status]}>{asset.status}</Pill>
                  <span>{asset.format}</span>
                </div>
                <h3>{asset.title}</h3>
                <p>{campaign?.name || "ללא קמפיין משויך"}</p>
                <div className="asset-actions">
                  <button onClick={() => setAssetModal(asset.id)}>עריכה</button>
                  <button onClick={() => duplicateAsset(asset)}>שכפול</button>
                  <button
                    className="danger-link"
                    onClick={() => {
                      if (window.confirm("למחוק את פריט התוכן?")) {
                        setData((current) => ({
                          ...current,
                          marketingAssets: current.marketingAssets.filter(
                            (item) => item.id !== asset.id,
                          ),
                        }));
                        onToast("פריט התוכן נמחק");
                      }
                    }}
                  >
                    מחיקה
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );

  const renderAudiences = () => (
    <>
      <section className="audience-banner card">
        <div>
          <span className="eyebrow">פילוח חכם</span>
          <h3>קהלים שמחברים בין המידע הרפואי לצמיחה</h3>
          <p>
            המערכת מזהה הזדמנויות מתוך נתוני ההדגמה בלבד. לפני שליחה אמיתית יש
            לקבל הסכמה ולפעול לפי מדיניות הפרטיות של המרפאה.
          </p>
        </div>
        <div className="audience-total">
          <strong>
            {data.marketingAudiences
              .reduce((sum, audience) => sum + audience.size, 0)
              .toLocaleString("he-IL")}
          </strong>
          <span>אנשים בכל הקהלים</span>
        </div>
      </section>
      <div className="audience-grid">
        {data.marketingAudiences.map((audience, index) => (
          <article className="card audience-card" key={audience.id}>
            <div className="audience-card-top">
              <span className={`audience-orb tone-${index % 4}`}>◎</span>
              <div>
                <small>{audience.channel}</small>
                <h3>{audience.name}</h3>
              </div>
              <strong>{audience.size.toLocaleString("he-IL")}</strong>
            </div>
            <p>{audience.description}</p>
            <div className="criteria-list">
              {audience.criteria.map((criterion) => (
                <span key={criterion}>✓ {criterion}</span>
              ))}
            </div>
            <div className="audience-opportunity">
              <span>הזדמנות</span>
              <strong>{audience.opportunity}</strong>
            </div>
            <button
              className="button button-secondary"
              onClick={() => {
                setTab("campaigns");
                setCampaignModal("new");
              }}
            >
              יצירת קמפיין לקהל
            </button>
          </article>
        ))}
      </div>
    </>
  );

  const renderAnalytics = () => (
    <>
      <div className="marketing-kpis analytics-kpis">
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon mint">₪</span>
          <div>
            <small>השקעה</small>
            <strong>{money(totalSpent)}</strong>
            <span>מכל הערוצים</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon purple">×</span>
          <div>
            <small>ROAS</small>
            <strong>{averageRoas.toFixed(1)}×</strong>
            <span className="marketing-positive">מעל יעד 3.5×</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon coral">◎</span>
          <div>
            <small>תוצאות</small>
            <strong>{totalResults}</strong>
            <span>בכל תקופת המדידה</span>
          </div>
        </article>
        <article className="card marketing-kpi">
          <span className="marketing-kpi-icon amber">↓</span>
          <div>
            <small>עלות לתוצאה</small>
            <strong>{money(costPerResult)}</strong>
            <span className="marketing-positive">9% פחות מהיעד</span>
          </div>
        </article>
      </div>
      <div className="analytics-grid">
        <article className="card analytics-chart-card">
          <CardHeader eyebrow="מגמה שבועית" title="תוצאות מול הוצאה" />
          <div className="analytics-legend">
            <span><i className="legend-results" /> תוצאות</span>
            <span><i className="legend-spend" /> הוצאה</span>
          </div>
          <div className="analytics-columns">
            {[
              [44, 65],
              [62, 72],
              [52, 69],
              [75, 82],
              [68, 88],
              [91, 84],
              [84, 92],
              [100, 96],
            ].map(([result, spend], index) => (
              <div key={`${result}-${index}`}>
                <span style={{ height: `${result}%` }} />
                <i style={{ height: `${spend}%` }} />
                <small>ש׳ {index + 1}</small>
              </div>
            ))}
          </div>
        </article>
        <article className="card channel-performance">
          <CardHeader eyebrow="השוואה" title="ביצועים לפי ערוץ" />
          {platformStats.map((item) => (
            <div className="channel-performance-row" key={item.name}>
              <span className="marketing-channel">{item.name.slice(0, 1)}</span>
              <div>
                <strong>{item.name}</strong>
                <small>{item.results} תוצאות · {money(item.spent)}</small>
              </div>
              <div className="channel-meter">
                <i style={{ width: `${Math.min(100, item.roas * 17)}%` }} />
              </div>
              <b>{item.roas.toFixed(1)}×</b>
            </div>
          ))}
        </article>
      </div>
      <article className="card marketing-funnel">
        <CardHeader
          eyebrow="משפך שיווקי"
          title="מליד ועד הכנסה"
          action={
            <Pill tone="success">
              {leadToAppointmentRate.toFixed(0)}% המרה לתור
            </Pill>
          }
        />
        <div>
          <span>
            <small>לידים</small>
            <strong>{totalLeads}</strong>
            <i style={{ width: "100%" }} />
          </span>
          <span>
            <small>תורים שנקבעו</small>
            <strong>{totalAppointments}</strong>
            <i
              style={{
                width: `${Math.max(8, leadToAppointmentRate)}%`,
              }}
            />
          </span>
          <span>
            <small>הכנסה מיוחסת</small>
            <strong>{money(totalRevenue)}</strong>
            <i style={{ width: `${Math.min(100, averageRoas * 18)}%` }} />
          </span>
          <span>
            <small>תשואה</small>
            <strong>{averageRoas.toFixed(1)}×</strong>
            <i style={{ width: `${Math.min(100, averageRoas * 18)}%` }} />
          </span>
        </div>
      </article>
      <article className="card marketing-insight">
        <span className="marketing-ai-label">✦ תובנת ביצועים</span>
        <div>
          <h3>Google מביא את התוצאה היעילה ביותר, Meta מוכן להרחבה</h3>
          <p>
            מומלץ להעביר 15% מתקציב Instagram לקמפיין הסניורים ב־Google ולהשאיר
            את Meta כערוץ צמיחה לקהל חדש.
          </p>
        </div>
        <button className="button button-secondary" onClick={exportMarketing}>
          ↓ הורדת נתונים
        </button>
      </article>
    </>
  );

  const renderConnections = () => (
    <>
      <section className="connections-intro card">
        <div>
          <span className="eyebrow">ערוצים ואינטגרציות</span>
          <h3>חיבור כלי השיווק של המרפאה</h3>
          <p>
            החיבורים כאן מדמים את מצב החשבון ונשמרים בדפדפן. לא נשלח מידע לשירות
            חיצוני מתוך סביבת ההדגמה.
          </p>
        </div>
        <Pill tone="info">
          {data.marketingConnections.filter((item) => item.connected).length} מתוך{" "}
          {data.marketingConnections.length} מחוברים
        </Pill>
      </section>
      <div className="connections-grid">
        {data.marketingConnections.map((connection) => (
          <article className="card connection-card" key={connection.name}>
            <div className="connection-logo">{connection.icon}</div>
            <div className="connection-copy">
              <div>
                <h3>{connection.name}</h3>
                <Pill tone={connection.connected ? "success" : "neutral"}>
                  {connection.connected ? "מחובר" : "לא מחובר"}
                </Pill>
              </div>
              <p>
                {connection.connected
                  ? connection.account
                  : "חברו את החשבון כדי לרכז נתונים ופעילות."}
              </p>
              <small>סנכרון אחרון: {connection.lastSync}</small>
            </div>
            <div className="connection-actions">
              {connection.connected ? (
                <>
                  <button
                    className="button button-secondary"
                    onClick={() => {
                      setData((current) => ({
                        ...current,
                        marketingConnections: current.marketingConnections.map(
                          (item) =>
                            item.name === connection.name
                              ? { ...item, lastSync: "עכשיו" }
                              : item,
                        ),
                      }));
                      onToast(`${connection.name} סונכרן`);
                    }}
                  >
                    סנכרון
                  </button>
                  <button
                    className="danger-link"
                    onClick={() => {
                      setData((current) => ({
                        ...current,
                        marketingConnections: current.marketingConnections.map(
                          (item) =>
                            item.name === connection.name
                              ? {
                                  ...item,
                                  connected: false,
                                  account: "",
                                  lastSync: "מנותק",
                                }
                              : item,
                        ),
                      }));
                      onToast(`${connection.name} נותק`);
                    }}
                  >
                    ניתוק
                  </button>
                </>
              ) : (
                <button
                  className="button button-primary"
                  onClick={() => {
                    setData((current) => ({
                      ...current,
                      marketingConnections: current.marketingConnections.map(
                        (item) =>
                          item.name === connection.name
                            ? {
                                ...item,
                                connected: true,
                                account: "חשבון הדגמה",
                                lastSync: "עכשיו",
                              }
                            : item,
                      ),
                    }));
                    onToast(`${connection.name} חובר במצב הדגמה`);
                  }}
                >
                  חיבור חשבון
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </>
  );

  return (
    <div className="page-stack marketing-page">
      <PageHeading
        eyebrow="צמיחה ושימור"
        title="מרכז השיווק"
        description="קמפיינים, תוכן, קהלים וביצועים — מחוברים לפעילות של המרפאה."
        actions={
          <>
            <button className="button button-secondary" onClick={exportMarketing}>
              ↓ יצוא דוח
            </button>
            <button
              className="button button-primary"
              onClick={() => setCampaignModal("new")}
            >
              + קמפיין חדש
            </button>
          </>
        }
      />

      <nav className="marketing-tabs card" aria-label="כלי שיווק">
        {tabs.map((item) => (
          <button
            key={item.id}
            className={tab === item.id ? "active" : ""}
            onClick={() => setTab(item.id)}
          >
            {item.label}
            {typeof item.count === "number" ? <span>{item.count}</span> : null}
          </button>
        ))}
      </nav>

      {tab === "overview" ? renderOverview() : null}
      {tab === "campaigns" ? renderCampaigns() : null}
      {tab === "planner" ? renderPlanner() : null}
      {tab === "studio" ? renderStudio() : null}
      {tab === "audiences" ? renderAudiences() : null}
      {tab === "analytics" ? renderAnalytics() : null}
      {tab === "connections" ? renderConnections() : null}

      {campaignModal ? (
        <div className="modal-backdrop" onMouseDown={() => setCampaignModal(null)}>
          <section
            className="modal modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="campaign-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="modal-header">
              <div>
                <span className="eyebrow">ניהול קמפיין</span>
                <h2 id="campaign-modal-title">
                  {editingCampaign ? "עריכת קמפיין" : "קמפיין חדש"}
                </h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setCampaignModal(null)}
                aria-label="סגירה"
              >
                ×
              </button>
            </header>
            <div className="modal-body">
              <form
                id="marketing-campaign-form"
                className="form-grid"
                onSubmit={saveCampaign}
              >
                <Field label="שם הקמפיין" full>
                  <input
                    name="name"
                    defaultValue={editingCampaign?.name}
                    placeholder="למשל: חודש בריאות העור"
                    required
                  />
                </Field>
                <Field label="ערוץ">
                  <select
                    name="platform"
                    defaultValue={editingCampaign?.platform || "Meta"}
                  >
                    <option>Meta</option>
                    <option>Google</option>
                    <option>Instagram</option>
                    <option>WhatsApp</option>
                    <option>Email</option>
                    <option>YouTube</option>
                  </select>
                </Field>
                <Field label="מטרה">
                  <input
                    name="objective"
                    defaultValue={editingCampaign?.objective}
                    placeholder="קביעת תורים"
                    required
                  />
                </Field>
                <Field label="קהל" full>
                  <input
                    name="audience"
                    defaultValue={editingCampaign?.audience}
                    placeholder="תיאור הקהל המדויק"
                    required
                  />
                </Field>
                <Field label="הצעה" full>
                  <input
                    name="offer"
                    defaultValue={editingCampaign?.offer}
                    placeholder="המסר או ההטבה"
                  />
                </Field>
                <Field label="תקציב יומי">
                  <input
                    name="budget"
                    type="number"
                    min="0"
                    defaultValue={editingCampaign?.budget || 50}
                    required
                  />
                </Field>
                <Field label="סטטוס">
                  <select
                    name="status"
                    defaultValue={editingCampaign?.status || "טיוטה"}
                  >
                    <option>טיוטה</option>
                    <option>בבדיקה</option>
                    <option>פעיל</option>
                    <option>מושהה</option>
                  </select>
                </Field>
                <div className="marketing-form-section field-full">
                  <span>מדידה ומשפך המרה</span>
                  <small>
                    הנתונים מעדכנים את הדוחות, התשואה והמרת הלידים לתורים.
                  </small>
                </div>
                <Field label="הוצאה בפועל">
                  <input
                    name="spent"
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={editingCampaign?.spent || 0}
                  />
                </Field>
                <Field label="הכנסה מיוחסת">
                  <input
                    name="revenue"
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={editingCampaign?.revenue || 0}
                  />
                </Field>
                <Field label="לידים">
                  <input
                    name="leads"
                    type="number"
                    min="0"
                    defaultValue={editingCampaign?.leads || 0}
                  />
                </Field>
                <Field label="תורים שנקבעו">
                  <input
                    name="appointments"
                    type="number"
                    min="0"
                    defaultValue={editingCampaign?.appointments || 0}
                  />
                </Field>
                <Field label="תאריך התחלה">
                  <input
                    name="startDate"
                    type="date"
                    defaultValue={editingCampaign?.startDate || localDateISO()}
                  />
                </Field>
                <Field label="תאריך סיום">
                  <input
                    name="endDate"
                    type="date"
                    defaultValue={editingCampaign?.endDate || addDays(30)}
                  />
                </Field>
                <div className="marketing-form-section field-full">
                  <span>הפעולה הבאה</span>
                  <small>
                    הפעולה תופיע בתכנית העבודה ובמרכז תמונת המצב.
                  </small>
                </div>
                <Field label="מה צריך לעשות?">
                  <input
                    name="nextAction"
                    defaultValue={editingCampaign?.nextAction || ""}
                    placeholder="למשל: לאשר מודעה חדשה"
                  />
                </Field>
                <Field label="מועד לביצוע">
                  <input
                    name="nextActionDate"
                    type="date"
                    defaultValue={editingCampaign?.nextActionDate || ""}
                  />
                </Field>
                <Field label="דף נחיתה" full>
                  <input
                    name="landingPage"
                    defaultValue={editingCampaign?.landingPage}
                    placeholder="/campaigns/my-offer"
                    dir="ltr"
                  />
                </Field>
                <Field
                  label="מילות מפתח"
                  hint="הפרדה בפסיקים"
                  full
                >
                  <textarea
                    name="keywords"
                    defaultValue={editingCampaign?.keywords.join(", ")}
                    rows={3}
                  />
                </Field>
              </form>
            </div>
            <footer className="modal-footer">
              <button
                className="button button-secondary"
                onClick={() => setCampaignModal(null)}
              >
                ביטול
              </button>
              <button
                className="button button-primary"
                type="submit"
                form="marketing-campaign-form"
              >
                שמירת קמפיין
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {assetModal ? (
        <div className="modal-backdrop" onMouseDown={() => setAssetModal(null)}>
          <section
            className="modal modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="asset-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="modal-header">
              <div>
                <span className="eyebrow">סטודיו תוכן</span>
                <h2 id="asset-modal-title">
                  {editingAsset ? "עריכת תוכן" : "יצירת תוכן חדש"}
                </h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setAssetModal(null)}
                aria-label="סגירה"
              >
                ×
              </button>
            </header>
            <div className="modal-body">
              <form
                id="marketing-asset-form"
                className="form-grid"
                onSubmit={saveAsset}
              >
                <Field label="שם הפריט" full>
                  <input
                    name="title"
                    defaultValue={editingAsset?.title}
                    placeholder="שם פנימי לצוות"
                    required
                  />
                </Field>
                <Field label="סוג תוכן">
                  <select name="type" defaultValue={editingAsset?.type || "פוסט"}>
                    <option>פוסט Instagram</option>
                    <option>מודעת Google</option>
                    <option>הודעת WhatsApp</option>
                    <option>דף נחיתה</option>
                    <option>Email</option>
                    <option>סרטון קצר</option>
                  </select>
                </Field>
                <Field label="פורמט">
                  <input
                    name="format"
                    defaultValue={editingAsset?.format}
                    placeholder="1080×1080"
                  />
                </Field>
                <Field label="קמפיין משויך">
                  <select
                    name="campaignId"
                    defaultValue={editingAsset?.campaignId || ""}
                  >
                    <option value="">ללא שיוך</option>
                    {data.marketingCampaigns.map((campaign) => (
                      <option value={campaign.id} key={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="סטטוס">
                  <select
                    name="status"
                    defaultValue={editingAsset?.status || "טיוטה"}
                  >
                    <option>טיוטה</option>
                    <option>בבדיקה</option>
                    <option>מוכן</option>
                    <option>פורסם</option>
                  </select>
                </Field>
                <Field label="כותרת" full>
                  <input
                    name="headline"
                    defaultValue={editingAsset?.headline}
                    placeholder="הכותרת שהלקוחות יראו"
                    required
                  />
                </Field>
                <Field label="טקסט" full>
                  <textarea
                    name="body"
                    defaultValue={editingAsset?.body}
                    rows={4}
                    required
                  />
                </Field>
                <Field label="הנעה לפעולה">
                  <input
                    name="cta"
                    defaultValue={editingAsset?.cta}
                    placeholder="קביעת תור"
                  />
                </Field>
                <Field label="סגנון צבע">
                  <select name="tone" defaultValue={editingAsset?.tone || "mint"}>
                    <option value="mint">מנטה</option>
                    <option value="coral">קורל</option>
                    <option value="purple">סגול</option>
                    <option value="amber">ענבר</option>
                  </select>
                </Field>
              </form>
            </div>
            <footer className="modal-footer">
              <button
                className="button button-secondary"
                onClick={() => setAssetModal(null)}
              >
                ביטול
              </button>
              <button
                className="button button-primary"
                type="submit"
                form="marketing-asset-form"
              >
                שמירת תוכן
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {selectedCampaign ? (
        <div className="modal-backdrop" onMouseDown={() => setDetailId(null)}>
          <section
            className="modal modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="campaign-detail-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="modal-header">
              <div>
                <span className="eyebrow">
                  {selectedCampaign.platform} · {selectedCampaign.objective}
                </span>
                <h2 id="campaign-detail-title">{selectedCampaign.name}</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setDetailId(null)}
                aria-label="סגירה"
              >
                ×
              </button>
            </header>
            <div className="modal-body campaign-detail">
              <div className="campaign-detail-top">
                <Pill tone={statusTone[selectedCampaign.status]}>
                  {selectedCampaign.status}
                </Pill>
                <span>
                  {friendlyDate(selectedCampaign.startDate)} —{" "}
                  {friendlyDate(selectedCampaign.endDate)}
                </span>
              </div>
              <div className="campaign-detail-kpis">
                <div><span>הוצאה</span><strong>{money(selectedCampaign.spent)}</strong></div>
                <div><span>לידים</span><strong>{selectedCampaign.leads || 0}</strong></div>
                <div><span>תורים</span><strong>{selectedCampaign.appointments || 0}</strong></div>
                <div><span>ROAS</span><strong>{selectedCampaign.roas.toFixed(1)}×</strong></div>
              </div>
              <div className="campaign-detail-grid">
                <article>
                  <span>קהל יעד</span>
                  <strong>{selectedCampaign.audience}</strong>
                </article>
                <article>
                  <span>הצעה</span>
                  <strong>{selectedCampaign.offer}</strong>
                </article>
                <article>
                  <span>דף נחיתה</span>
                  <strong dir="ltr">
                    {selectedCampaign.landingPage || "ללא דף נחיתה"}
                  </strong>
                </article>
                <article>
                  <span>תקציב יומי</span>
                  <strong>{money(selectedCampaign.budget)}</strong>
                </article>
                <article>
                  <span>הכנסה מיוחסת</span>
                  <strong>{money(selectedCampaign.revenue || 0)}</strong>
                </article>
                <article>
                  <span>המרה מליד לתור</span>
                  <strong>
                    {selectedCampaign.leads
                      ? `${(
                          (Number(selectedCampaign.appointments || 0) /
                            selectedCampaign.leads) *
                          100
                        ).toFixed(0)}%`
                      : "—"}
                  </strong>
                </article>
              </div>
              {selectedCampaign.nextAction ? (
                <div className="campaign-next-action">
                  <span>הפעולה הבאה</span>
                  <div>
                    <strong>{selectedCampaign.nextAction}</strong>
                    <small>
                      לביצוע עד{" "}
                      {friendlyDate(selectedCampaign.nextActionDate || "")}
                    </small>
                  </div>
                  <button
                    className="button button-secondary"
                    onClick={() => completeCampaignAction(selectedCampaign.id)}
                  >
                    ✓ סימון כבוצע
                  </button>
                </div>
              ) : null}
              <div className="campaign-keywords">
                <span>מילות מפתח ומיקוד</span>
                <div>
                  {selectedCampaign.keywords.map((keyword) => (
                    <Pill key={keyword} tone="mint">{keyword}</Pill>
                  ))}
                </div>
              </div>
              <div className="campaign-assets-list">
                <CardHeader
                  eyebrow="קריאייטיב"
                  title="תוכן משויך לקמפיין"
                />
                {data.marketingAssets.filter(
                  (asset) => asset.campaignId === selectedCampaign.id,
                ).length ? (
                  data.marketingAssets
                    .filter((asset) => asset.campaignId === selectedCampaign.id)
                    .map((asset) => (
                      <div key={asset.id}>
                        <span className={`asset-dot asset-${asset.tone}`} />
                        <div>
                          <strong>{asset.title}</strong>
                          <small>{asset.type} · {asset.format}</small>
                        </div>
                        <Pill tone={statusTone[asset.status]}>{asset.status}</Pill>
                      </div>
                    ))
                ) : (
                  <p>עדיין אין תוכן משויך לקמפיין הזה.</p>
                )}
              </div>
            </div>
            <footer className="modal-footer">
              <button
                className="button button-secondary"
                onClick={() => toggleCampaign(selectedCampaign.id)}
              >
                {selectedCampaign.status === "פעיל" ? "השהיית קמפיין" : "הפעלת קמפיין"}
              </button>
              <button
                className="button button-primary"
                onClick={() => {
                  setDetailId(null);
                  setCampaignModal(selectedCampaign.id);
                }}
              >
                עריכת קמפיין
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function ReportsPage({
  data,
  petById,
}: {
  data: AppData;
  petById: (id: string) => PetRecord | undefined;
}) {
  const month = localDateISO().slice(0, 7);
  const monthAppointments = data.appointments.filter(
    (appointment) =>
      appointment.date.startsWith(month) && appointment.status !== "בוטל",
  );
  const paid = data.invoices
    .filter((invoice) => invoice.date.startsWith(month))
    .reduce((sum, invoice) => sum + invoice.paid, 0);
  const openDebt = data.invoices.reduce(
    (sum, invoice) => sum + invoiceBalance(invoice),
    0,
  );
  const treatmentCounts = Object.entries(
    monthAppointments.reduce<Record<string, number>>((acc, appointment) => {
      acc[appointment.type] = (acc[appointment.type] || 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const speciesCounts = Object.entries(
    monthAppointments.reduce<Record<string, number>>((acc, appointment) => {
      const species = petById(appointment.petId)?.species || "אחר";
      acc[species] = (acc[species] || 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const maxTreatments = Math.max(1, ...treatmentCounts.map((item) => item[1]));
  const maxSpecies = Math.max(1, ...speciesCounts.map((item) => item[1]));
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="נתונים ותובנות"
        title="דוחות המרפאה"
        description="מדדי פעילות, הכנסות, תמהיל טיפולים ונקודות שדורשות פעולה."
        actions={
          <button className="button button-secondary" onClick={() => window.print()}>
            ⎙ הדפסה
          </button>
        }
      />
      <div className="report-kpis">
        <article className="card">
          <span>הכנסות החודש</span>
          <strong>{money(paid)}</strong>
          <Pill tone="success">+8.4%</Pill>
        </article>
        <article className="card">
          <span>ביקורים החודש</span>
          <strong>{monthAppointments.length}</strong>
          <Pill tone="info">פעילות</Pill>
        </article>
        <article className="card">
          <span>חוב פתוח</span>
          <strong className="text-danger">{money(openDebt)}</strong>
          <Pill tone="danger">לגבייה</Pill>
        </article>
        <article className="card">
          <span>לקוחות פעילים</span>
          <strong>
            {data.clients.filter((client) => client.status !== "לא פעיל").length}
          </strong>
          <Pill tone="mint">קהילה</Pill>
        </article>
      </div>
      <div className="report-grid">
        <article className="card chart-card">
          <CardHeader eyebrow="פילוח פעילות" title="טיפולים לפי סוג" />
          <div className="bar-chart">
            {treatmentCounts.map(([label, value]) => (
              <div className="bar-chart-row" key={label}>
                <span>{label}</span>
                <div>
                  <i style={{ width: `${(value / maxTreatments) * 100}%` }} />
                </div>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </article>
        <article className="card chart-card">
          <CardHeader eyebrow="תמהיל מטופלים" title="ביקורים לפי סוג בעל חיים" />
          <div className="bar-chart bar-chart-coral">
            {speciesCounts.map(([label, value]) => (
              <div className="bar-chart-row" key={label}>
                <span>{label}</span>
                <div>
                  <i style={{ width: `${(value / maxSpecies) * 100}%` }} />
                </div>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </article>
        <article className="card action-report">
          <CardHeader eyebrow="גבייה" title="לקוחות עם יתרה פתוחה" />
          <div>
            {data.clients
              .map((client) => ({
                client,
                debt: data.invoices
                  .filter((invoice) => invoice.clientId === client.id)
                  .reduce((sum, invoice) => sum + invoiceBalance(invoice), 0),
              }))
              .filter((item) => item.debt > 0)
              .map(({ client, debt }) => (
                <div className="report-list-row" key={client.id}>
                  <span className="mini-avatar">{client.name.slice(0, 2)}</span>
                  <div>
                    <strong>{client.name}</strong>
                    <small>{client.phone}</small>
                  </div>
                  <Pill tone="danger">{money(debt)}</Pill>
                </div>
              ))}
          </div>
        </article>
        <article className="card action-report">
          <CardHeader eyebrow="רכש" title="פריטים להזמנה" />
          <div>
            {data.inventory
              .filter((item) => item.stock <= item.minStock)
              .map((item) => (
                <div className="report-list-row" key={item.id}>
                  <span className="mini-avatar box-avatar">□</span>
                  <div>
                    <strong>{item.name}</strong>
                    <small>
                      {item.supplier} · {item.stock} {item.unit}
                    </small>
                  </div>
                  <Pill tone="warning">מינ׳ {item.minStock}</Pill>
                </div>
              ))}
          </div>
        </article>
      </div>
    </div>
  );
}

function SettingsPage({
  data,
  setData,
  onBackup,
  onRestore,
  onReset,
  onClear,
  onToast,
}: {
  data: AppData;
  setData: React.Dispatch<React.SetStateAction<AppData>>;
  onBackup: () => void;
  onRestore: () => void;
  onReset: () => void;
  onClear: () => void;
  onToast: (message: string) => void;
}) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setData((current) => ({
      ...current,
      clinic: {
        name: String(form.get("name") || ""),
        doctor: String(form.get("doctor") || ""),
        phone: String(form.get("phone") || ""),
        email: String(form.get("email") || ""),
        address: String(form.get("address") || ""),
        defaultDoctor: String(form.get("defaultDoctor") || ""),
        reminderDays: Math.max(0, Number(form.get("reminderDays") || 14)),
        vat: Math.min(100, Math.max(0, Number(form.get("vat") || 18))),
        taxId: String(form.get("taxId") || ""),
        bank: String(form.get("bank") || ""),
        invoicePrefix: String(form.get("invoicePrefix") || "INV").trim(),
        nextInvoiceNumber: Math.max(
          1,
          Math.floor(Number(form.get("nextInvoiceNumber") || 1)),
        ),
      },
    }));
    onToast("הגדרות המרפאה נשמרו");
  };
  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="התאמה אישית"
        title="הגדרות מערכת"
        description="פרטי המרפאה, ברירות מחדל, גיבוי ושחזור נתונים."
      />
      <div className="settings-grid">
        <form className="card settings-form" onSubmit={submit}>
          <CardHeader eyebrow="זהות המרפאה" title="פרטים כלליים" />
          <div className="form-grid">
            <Field label="שם המרפאה">
              <input name="name" defaultValue={data.clinic.name} required />
            </Field>
            <Field label="וטרינרית ראשית">
              <input name="doctor" defaultValue={data.clinic.doctor} required />
            </Field>
            <Field label="טלפון">
              <input name="phone" defaultValue={data.clinic.phone} />
            </Field>
            <Field label="דוא״ל">
              <input name="email" type="email" defaultValue={data.clinic.email} />
            </Field>
            <Field label="כתובת" full>
              <input name="address" defaultValue={data.clinic.address} />
            </Field>
            <Field label="עוסק / ח.פ">
              <input
                name="taxId"
                defaultValue={data.clinic.taxId}
                inputMode="numeric"
              />
            </Field>
            <Field label="פרטי בנק לתשלום">
              <input name="bank" defaultValue={data.clinic.bank} />
            </Field>
            <Field label="רופא ברירת מחדל">
              <input
                name="defaultDoctor"
                defaultValue={data.clinic.defaultDoctor}
              />
            </Field>
            <Field label="תזכורת לפני חיסון">
              <div className="input-suffix">
                <input
                  name="reminderDays"
                  type="number"
                  min="0"
                  defaultValue={data.clinic.reminderDays}
                />
                <span>ימים</span>
              </div>
            </Field>
            <Field label="מע״מ">
              <div className="input-suffix">
                <input
                  name="vat"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  defaultValue={data.clinic.vat}
                />
                <span>%</span>
              </div>
            </Field>
            <Field label="קידומת חשבוניות">
              <input
                name="invoicePrefix"
                defaultValue={data.clinic.invoicePrefix}
                dir="ltr"
              />
            </Field>
            <Field label="מספר החשבונית הבא">
              <input
                name="nextInvoiceNumber"
                type="number"
                min="1"
                defaultValue={data.clinic.nextInvoiceNumber}
              />
            </Field>
          </div>
          <button className="button button-primary" type="submit">
            שמירת שינויים
          </button>
        </form>
        <div className="settings-side">
          <article className="card data-card">
            <CardHeader eyebrow="הנתונים שלך" title="גיבוי ושחזור" />
            <p>
              הנתונים נשמרים בדפדפן. מומלץ להוריד גיבוי תקופתי ולשמור אותו במקום
              בטוח.
            </p>
            <div className="data-actions">
              <button className="button button-secondary" onClick={onBackup}>
                ↓ הורדת גיבוי
              </button>
              <button className="button button-secondary" onClick={onRestore}>
                ↑ שחזור מקובץ
              </button>
            </div>
          </article>
          <article className="card data-card">
            <CardHeader eyebrow="סביבת הדגמה" title="תחזוקת המערכת" />
            <div className="maintenance-list">
              <div>
                <span className="quick-icon amber">↻</span>
                <div>
                  <strong>איפוס נתוני הדגמה</strong>
                  <p>טעינה מחדש של לקוחות, מטופלים ופעילות לדוגמה.</p>
                </div>
                <button onClick={onReset}>איפוס</button>
              </div>
              <div>
                <span className="quick-icon coral">×</span>
                <div>
                  <strong>מחיקת נתונים מקומיים</strong>
                  <p>מחיקה מלאה של המידע מהמכשיר ויציאה מהמערכת.</p>
                </div>
                <button className="danger-link" onClick={onClear}>
                  מחיקה
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

function ListToolbar({
  query,
  onQuery,
  placeholder,
  filter,
  count,
}: {
  query: string;
  onQuery: (value: string) => void;
  placeholder: string;
  filter: ReactNode;
  count: string;
}) {
  return (
    <div className="list-toolbar card">
      <div className="search-box">
        <span>⌕</span>
        <input
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
        />
      </div>
      <div className="toolbar-filter">{filter}</div>
      <span className="result-count">{count}</span>
    </div>
  );
}

function MedicalRecordEditor({
  data,
  current,
  presetPetId,
  setData,
  clientById,
  onSaved,
  onToast,
}: {
  data: AppData;
  current?: MedicalRecord;
  presetPetId?: string;
  setData: React.Dispatch<React.SetStateAction<AppData>>;
  clientById: (id: string) => ClientRecord | undefined;
  onSaved: (editing: boolean) => void;
  onToast: (message: string) => void;
}) {
  const [attachments, setAttachments] = useState<MedicalAttachment[]>(
    current?.attachments || [],
  );
  const [uploading, setUploading] = useState(false);
  const pendingUploadsRef = useRef(new Map<string, string>());
  const savedRef = useRef(false);

  useEffect(
    () => () => {
      if (savedRef.current) return;
      pendingUploadsRef.current.forEach((url) => {
        void fetch(url, { method: "DELETE" }).catch(() => undefined);
      });
      pendingUploadsRef.current.clear();
    },
    [],
  );

  const uploadImages = async (files: FileList | null) => {
    if (!files?.length) return;
    const availableSlots = Math.max(0, 8 - attachments.length);
    const selected = Array.from(files).slice(0, availableSlots);
    if (!selected.length) {
      onToast("אפשר לצרף עד 8 תמונות לרשומה");
      return;
    }
    setUploading(true);
    const uploaded: MedicalAttachment[] = [];
    let failedMessage = "";
    for (const file of selected) {
      try {
        const form = new FormData();
        form.append("file", file);
        const response = await fetch("/api/medical-images", {
          method: "POST",
          body: form,
        });
        const result = (await response.json()) as
          | MedicalAttachment
          | { error?: string };
        if (!response.ok || !("url" in result)) {
          throw new Error(
            "error" in result && result.error
              ? result.error
              : "העלאת התמונה נכשלה",
          );
        }
        uploaded.push(result);
        pendingUploadsRef.current.set(result.id, result.url);
      } catch (error) {
        failedMessage =
          error instanceof Error ? error.message : "העלאת התמונה נכשלה";
      }
    }
    if (uploaded.length) {
      setAttachments((items) => [...items, ...uploaded]);
      onToast(
        uploaded.length === 1
          ? "התמונה צורפה לרשומה"
          : `${uploaded.length} תמונות צורפו לרשומה`,
      );
    }
    if (failedMessage) onToast(failedMessage);
    setUploading(false);
  };

  const removeAttachment = (attachment: MedicalAttachment) => {
    setAttachments((items) =>
      items.filter((item) => item.id !== attachment.id),
    );
    const pendingUrl = pendingUploadsRef.current.get(attachment.id);
    if (pendingUrl) {
      pendingUploadsRef.current.delete(attachment.id);
      void fetch(pendingUrl, { method: "DELETE" }).catch(() => undefined);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (uploading) {
      onToast("יש להמתין לסיום העלאת התמונות");
      return;
    }
    const form = new FormData(event.currentTarget);
    const record: MedicalRecord = {
      id: current?.id || uid("r"),
      petId: String(form.get("petId") || ""),
      date: String(form.get("date") || localDateISO()),
      type: String(form.get("type") || ""),
      doctor: String(form.get("doctor") || ""),
      title: String(form.get("title") || ""),
      diagnosis: String(form.get("diagnosis") || ""),
      treatment: String(form.get("treatment") || ""),
      nextSteps: String(form.get("nextSteps") || ""),
      price: Number(form.get("price") || 0),
      attachments,
    };
    setData((state) => ({
      ...state,
      records: current
        ? state.records.map((item) =>
            item.id === current.id ? record : item,
          )
        : [record, ...state.records],
    }));
    current?.attachments
      .filter(
        (attachment) =>
          !attachments.some((item) => item.id === attachment.id),
      )
      .forEach((attachment) => {
        void fetch(attachment.url, { method: "DELETE" }).catch(
          () => undefined,
        );
      });
    savedRef.current = true;
    pendingUploadsRef.current.clear();
    onSaved(Boolean(current));
  };

  return (
    <form id="entity-form" className="medical-record-editor" onSubmit={submit}>
      <div className="form-grid">
        <Field label="מטופל">
          <select
            name="petId"
            defaultValue={current?.petId || presetPetId || data.pets[0]?.id}
          >
            {data.pets.map((pet) => (
              <option key={pet.id} value={pet.id}>
                {pet.name} · {clientById(pet.ownerId)?.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="תאריך">
          <input
            name="date"
            type="date"
            defaultValue={current?.date || localDateISO()}
          />
        </Field>
        <Field label="סוג רשומה">
          <input name="type" defaultValue={current?.type || "בדיקה"} />
        </Field>
        <Field label="רופא">
          <input
            name="doctor"
            defaultValue={current?.doctor || data.clinic.defaultDoctor}
          />
        </Field>
        <Field label="כותרת" full>
          <input name="title" defaultValue={current?.title} required />
        </Field>
        <Field label="אבחנה" full>
          <textarea
            name="diagnosis"
            defaultValue={current?.diagnosis}
            rows={3}
          />
        </Field>
        <Field label="טיפול" full>
          <textarea
            name="treatment"
            defaultValue={current?.treatment}
            rows={3}
          />
        </Field>
        <Field label="המשך מעקב" full>
          <textarea
            name="nextSteps"
            defaultValue={current?.nextSteps}
            rows={3}
          />
        </Field>
        <Field label="מחיר">
          <div className="input-suffix">
            <input
              name="price"
              type="number"
              defaultValue={current?.price || 0}
            />
            <span>₪</span>
          </div>
        </Field>
      </div>

      <section className="medical-attachments-editor">
        <div className="medical-attachments-heading">
          <div>
            <span className="eyebrow">תיעוד חזותי</span>
            <strong>תמונות מצורפות לרשומה</strong>
            <p>
              צילום פצע, בדיקת הדמיה, תוצאות מעבדה או מסמך רפואי. עד 8 תמונות,
              8MB לתמונה.
            </p>
          </div>
          <label className={`medical-upload-button ${uploading ? "is-uploading" : ""}`}>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              hidden
              disabled={uploading || attachments.length >= 8}
              onChange={(event) => {
                void uploadImages(event.target.files);
                event.currentTarget.value = "";
              }}
            />
            {uploading ? "מעלה תמונות…" : "＋ צירוף תמונות"}
          </label>
        </div>

        {attachments.length ? (
          <div className="medical-attachment-grid">
            {attachments.map((attachment) => (
              <article key={attachment.id}>
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                  title="פתיחת תמונה בגודל מלא"
                >
                  <MedicalThumbnail attachment={attachment} />
                </a>
                <div>
                  <strong>{attachment.name}</strong>
                  <small>
                    {(attachment.size / 1024 / 1024).toFixed(1)}MB ·{" "}
                    {friendlyDate(attachment.uploadedAt.slice(0, 10))}
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment)}
                  aria-label={`הסרת ${attachment.name}`}
                >
                  ×
                </button>
              </article>
            ))}
          </div>
        ) : (
          <label className="medical-upload-empty">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              hidden
              disabled={uploading}
              onChange={(event) => {
                void uploadImages(event.target.files);
                event.currentTarget.value = "";
              }}
            />
            <span>▧</span>
            <strong>{uploading ? "מעלה את התמונות…" : "לחצו לצירוף תמונות"}</strong>
            <small>JPG, PNG, WEBP או GIF</small>
          </label>
        )}
      </section>
    </form>
  );
}

function InvoiceComposer({
  data,
  current,
  presetClientId,
  setData,
  onSaved,
  onToast,
}: {
  data: AppData;
  current?: Invoice;
  presetClientId?: string;
  setData: React.Dispatch<React.SetStateAction<AppData>>;
  onSaved: (editing: boolean) => void;
  onToast: (message: string) => void;
}) {
  const initialClient =
    data.clients.find((client) => client.id === current?.clientId) ||
    data.clients.find((client) => client.id === presetClientId) ||
    data.clients[0];
  const nextInvoiceId =
    current?.id || nextInvoiceIdentity(data.clinic, data.invoices).id;
  const [clientId, setClientId] = useState(
    current?.clientId || presetClientId || initialClient?.id || "",
  );
  const [recipient, setRecipient] = useState({
    name: current?.clientName || initialClient?.name || "",
    email: current?.clientEmail || initialClient?.email || "",
    address:
      current?.clientAddress ||
      [initialClient?.address, initialClient?.city].filter(Boolean).join(", "),
    taxId: current?.clientTaxId || initialClient?.taxId || "",
  });
  const [appointmentId, setAppointmentId] = useState(
    current?.appointmentId || "",
  );
  const [documentType, setDocumentType] = useState<InvoiceDocumentType>(
    current?.documentType || "חשבונית מס",
  );
  const [date, setDate] = useState(current?.date || localDateISO());
  const [dueDate, setDueDate] = useState(
    current?.dueDate ||
      addDaysFrom(localDateISO(), initialClient?.paymentTerms ?? 30),
  );
  const [status, setStatus] = useState<InvoiceStatus>(
    current?.status || "טיוטה",
  );
  const [paid, setPaid] = useState(Number(current?.paid || 0));
  const [paymentMethod, setPaymentMethod] = useState(
    current?.paymentMethod || "",
  );
  const [notes, setNotes] = useState(current?.notes || "");
  const [vatExempt, setVatExempt] = useState(current?.vatExempt ?? false);
  const [items, setItems] = useState<Array<InvoiceItem & { id: string }>>(
    (current?.items?.length
      ? current.items
      : [{ desc: "בדיקה כללית", qty: 1, price: 250 }]
    ).map((item) => ({
      ...item,
      id: item.id || uid("line"),
    })),
  );

  const subtotal = roundMoney(
    items.reduce(
      (sum, item) => sum + Number(item.qty) * Number(item.price),
      0,
    ),
  );
  const vat = vatExempt
    ? 0
    : roundMoney(subtotal * (Number(data.clinic.vat || 0) / 100));
  const total = roundMoney(subtotal + vat);
  const unbilledAppointments = data.appointments.filter(
    (appointment) =>
      appointment.id === current?.appointmentId ||
      !data.invoices.some(
        (invoice) =>
          invoice.appointmentId === appointment.id &&
          invoice.status !== "מבוטלת",
      ),
  );

  const selectClient = (id: string) => {
    const client = data.clients.find((item) => item.id === id);
    setClientId(id);
    if (!client) return;
    setRecipient({
      name: client.name,
      email: client.email,
      address: [client.address, client.city].filter(Boolean).join(", "),
      taxId: client.taxId,
    });
    setDueDate(addDaysFrom(date, client.paymentTerms));
  };

  const selectAppointment = (id: string) => {
    setAppointmentId(id);
    const appointment = data.appointments.find((item) => item.id === id);
    if (!appointment) return;
    const client = data.clients.find(
      (item) => item.id === appointment.ownerId,
    );
    const pet = data.pets.find((item) => item.id === appointment.petId);
    selectClient(appointment.ownerId);
    setDueDate(
      addDaysFrom(date, client?.paymentTerms ?? initialClient?.paymentTerms ?? 30),
    );
    setItems([
      {
        id: uid("line"),
        desc: `${appointment.type}${pet ? ` — ${pet.name}` : ""}`,
        qty: 1,
        price: appointment.price,
      },
    ]);
  };

  const updateItem = (
    id: string,
    field: "desc" | "qty" | "price",
    value: string | number,
  ) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]:
                field === "desc" ? String(value) : Number(value || 0),
            }
          : item,
      ),
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !recipient.name.trim() ||
      !items.length ||
      items.some(
        (item) =>
          !item.desc.trim() ||
          Number(item.qty) <= 0 ||
          Number(item.price) < 0,
      )
    ) {
      onToast("יש להשלים לקוח ופריטי חיוב תקינים");
      return;
    }
    const finalStatus: InvoiceStatus =
      total > 0 && paid >= total
        ? "שולמה"
        : status === "שולמה"
          ? "חוב פתוח"
          : status;
    setData((state) => {
      const nextInvoice = nextInvoiceIdentity(state.clinic, state.invoices);
      const id = current?.id || nextInvoice.id;
      const record: Invoice = {
        id,
        date,
        dueDate,
        paidDate:
          finalStatus === "שולמה"
            ? current?.paidDate || localDateISO()
            : "",
        clientId,
        clientName: recipient.name.trim(),
        clientEmail: recipient.email.trim(),
        clientAddress: recipient.address.trim(),
        clientTaxId: recipient.taxId.trim(),
        appointmentId: appointmentId || undefined,
        status: finalStatus,
        documentType,
        items: items.map((item) => ({
          id: item.id,
          desc: item.desc.trim(),
          qty: Number(item.qty),
          price: Number(item.price),
        })),
        vatRate: Number(state.clinic.vat || 0) / 100,
        vatExempt,
        paid: Math.max(0, Number(paid || 0)),
        paymentMethod,
        notes: notes.trim(),
      };
      return {
        ...state,
        clinic: current
          ? state.clinic
          : {
              ...state.clinic,
              nextInvoiceNumber: nextInvoice.nextNumber,
            },
        invoices: current
          ? state.invoices.map((invoice) =>
              invoice.id === current.id ? record : invoice,
            )
          : [record, ...state.invoices],
      };
    });
    onSaved(Boolean(current));
  };

  return (
    <form id="entity-form" className="invoice-composer" onSubmit={submit}>
      <section className="invoice-composer-section">
        <div className="invoice-section-heading">
          <span>01</span>
          <div>
            <strong>מסמך ולקוח</strong>
            <small>פרטי המסמך והנמען כפי שיופיעו בהדפסה</small>
          </div>
        </div>
        <div className="form-grid invoice-header-grid">
          <Field label="סוג מסמך">
            <select
              value={documentType}
              onChange={(event) =>
                setDocumentType(event.target.value as InvoiceDocumentType)
              }
            >
              <option>חשבונית מס</option>
              <option>חשבונית מס קבלה</option>
              <option>קבלה</option>
              <option>דרישת תשלום</option>
              <option>זיכוי</option>
            </select>
          </Field>
          <Field label="לקוח מהמרפאה">
            <select
              value={clientId}
              onChange={(event) => selectClient(event.target.value)}
            >
              {data.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} · {client.phone}
                </option>
              ))}
            </select>
          </Field>
          <Field label="תור לחיוב" full>
            <select
              value={appointmentId}
              onChange={(event) => selectAppointment(event.target.value)}
            >
              <option value="">ללא שיוך לתור</option>
              {unbilledAppointments.map((appointment) => {
                const pet = data.pets.find(
                  (item) => item.id === appointment.petId,
                );
                return (
                  <option key={appointment.id} value={appointment.id}>
                    {friendlyDate(appointment.date)} · {appointment.type} ·{" "}
                    {pet?.name} · {money(appointment.price)}
                  </option>
                );
              })}
            </select>
          </Field>
          <Field label="שם הלקוח">
            <input
              value={recipient.name}
              onChange={(event) =>
                setRecipient({ ...recipient, name: event.target.value })
              }
              required
            />
          </Field>
          <Field label="ח.פ / ת.ז">
            <input
              value={recipient.taxId}
              onChange={(event) =>
                setRecipient({ ...recipient, taxId: event.target.value })
              }
              inputMode="numeric"
            />
          </Field>
          <Field label="דוא״ל">
            <input
              type="email"
              value={recipient.email}
              onChange={(event) =>
                setRecipient({ ...recipient, email: event.target.value })
              }
            />
          </Field>
          <Field label="כתובת">
            <input
              value={recipient.address}
              onChange={(event) =>
                setRecipient({ ...recipient, address: event.target.value })
              }
            />
          </Field>
          <Field label="תאריך הפקה">
            <input
              type="date"
              value={date}
              onChange={(event) => {
                const nextDate = event.target.value;
                const client = data.clients.find(
                  (item) => item.id === clientId,
                );
                setDate(nextDate);
                setDueDate(
                  addDaysFrom(nextDate, client?.paymentTerms ?? 30),
                );
              }}
              required
            />
          </Field>
          <Field label="לתשלום עד">
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              required
            />
          </Field>
        </div>
      </section>

      <section className="invoice-composer-section">
        <div className="invoice-section-heading">
          <span>02</span>
          <div>
            <strong>פריטי חיוב</strong>
            <small>טיפולים, תרופות ושירותים שניתנו בביקור</small>
          </div>
        </div>
        <div className="invoice-item-editor">
          <div className="invoice-item-head">
            <span>תיאור</span>
            <span>כמות</span>
            <span>מחיר יחידה</span>
            <span>סה״כ</span>
            <span />
          </div>
          {items.map((item) => (
            <div className="invoice-item-row" key={item.id}>
              <input
                value={item.desc}
                onChange={(event) =>
                  updateItem(item.id, "desc", event.target.value)
                }
                aria-label="תיאור פריט"
                required
              />
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={item.qty}
                onChange={(event) =>
                  updateItem(item.id, "qty", event.target.value)
                }
                aria-label="כמות"
                required
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={item.price}
                onChange={(event) =>
                  updateItem(item.id, "price", event.target.value)
                }
                aria-label="מחיר יחידה"
                required
              />
              <strong>{money(Number(item.qty) * Number(item.price))}</strong>
              <button
                type="button"
                className="invoice-remove-line"
                onClick={() =>
                  items.length > 1 &&
                  setItems((currentItems) =>
                    currentItems.filter(
                      (currentItem) => currentItem.id !== item.id,
                    ),
                  )
                }
                disabled={items.length === 1}
                aria-label="מחיקת פריט"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="invoice-add-line"
          onClick={() =>
            setItems((currentItems) => [
              ...currentItems,
              { id: uid("line"), desc: "", qty: 1, price: 0 },
            ])
          }
        >
          ＋ הוספת שורת חיוב
        </button>
      </section>

      <section className="invoice-composer-section invoice-payment-section">
        <div>
          <div className="invoice-section-heading">
            <span>03</span>
            <div>
              <strong>תשלום והערות</strong>
              <small>סטטוס, אמצעי תשלום ומידע ללקוח</small>
            </div>
          </div>
          <div className="form-grid">
            <Field label="סטטוס">
              <select
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as InvoiceStatus)
                }
              >
                <option>טיוטה</option>
                <option>נשלחה</option>
                <option>חוב פתוח</option>
                <option>שולמה</option>
                <option>מבוטלת</option>
              </select>
            </Field>
            <Field label="אמצעי תשלום">
              <select
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value)}
              >
                <option value="">טרם נבחר</option>
                <option>אשראי</option>
                <option>מזומן</option>
                <option>ביט</option>
                <option>פייבוקס</option>
                <option>העברה בנקאית</option>
                <option>צ׳ק</option>
                <option>אחר</option>
              </select>
            </Field>
            <Field label="סכום ששולם">
              <div className="input-suffix">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paid}
                  onChange={(event) => setPaid(Number(event.target.value || 0))}
                />
                <span>₪</span>
              </div>
            </Field>
            <Field label="מע״מ">
              <span className="invoice-vat-toggle">
                <input
                  type="checkbox"
                  checked={vatExempt}
                  onChange={(event) => setVatExempt(event.target.checked)}
                />
                ללא מע״מ / פטור
              </span>
            </Field>
            <Field label="הערות שיופיעו במסמך" full>
              <textarea
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="תנאי תשלום, הנחיות או תודה אישית"
              />
            </Field>
          </div>
        </div>
        <aside className="invoice-live-summary">
          <span className="eyebrow">סיכום בזמן אמת</span>
          <div>
            <span>סכום ביניים</span>
            <strong>{money(subtotal)}</strong>
          </div>
          <div>
            <span>מע״מ ({vatExempt ? 0 : data.clinic.vat}%)</span>
            <strong>{money(vat)}</strong>
          </div>
          <div className="invoice-summary-total">
            <span>סה״כ לתשלום</span>
            <strong>{money(total)}</strong>
          </div>
          <div>
            <span>יתרה לאחר תשלום</span>
            <strong className={Math.max(0, total - paid) ? "text-danger" : ""}>
              {money(Math.max(0, total - paid))}
            </strong>
          </div>
          <small>
            מספר המסמך ייקבע אוטומטית בשמירה:{" "}
            {nextInvoiceId}
          </small>
        </aside>
      </section>
    </form>
  );
}

function EntityModal({
  modal,
  data,
  setData,
  onClose,
  onToast,
  clientById,
  petById,
  onEdit,
}: {
  modal: NonNullable<ModalState>;
  data: AppData;
  setData: React.Dispatch<React.SetStateAction<AppData>>;
  onClose: () => void;
  onToast: (message: string) => void;
  clientById: (id: string) => ClientRecord | undefined;
  petById: (id: string) => PetRecord | undefined;
  onEdit: (modal: NonNullable<ModalState>) => void;
}) {
  const autoPrintedInvoiceRef = useRef<string | null>(null);

  useEffect(() => {
    if (modal.type !== "invoice-detail" || !modal.autoPrint) return;
    if (autoPrintedInvoiceRef.current === modal.id) return;
    autoPrintedInvoiceRef.current = modal.id;
    const timer = window.setTimeout(() => {
      const opened = printInvoiceDocument(
        `invoice-print-${modal.id}`,
        `מסמך ${modal.id}`,
      );
      if (!opened) {
        onToast("לא ניתן לפתוח את ההדפסה בדפדפן זה");
      }
    }, 120);
    return () => window.clearTimeout(timer);
  }, [modal, onToast]);

  const closeAndToast = (message: string) => {
    onClose();
    onToast(message);
  };
  let title = "";
  let subtitle = "";
  let body: ReactNode = null;

  if (modal.type === "client") {
    const current = modal.id ? clientById(modal.id) : undefined;
    title = current ? "עריכת לקוח" : "לקוח חדש";
    subtitle = "פרטי קשר, כתובת והעדפות תקשורת";
    body = (
      <form
        id="entity-form"
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: ClientRecord = {
            id: current?.id || uid("c"),
            name: String(form.get("name") || ""),
            phone: String(form.get("phone") || ""),
            email: String(form.get("email") || ""),
            city: String(form.get("city") || ""),
            address: String(form.get("address") || ""),
            status: String(form.get("status") || "פעיל") as ClientStatus,
            joinDate: String(form.get("joinDate") || localDateISO()),
            preferredContact: String(form.get("preferredContact") || "WhatsApp"),
            taxId: String(form.get("taxId") || ""),
            paymentTerms: Number(form.get("paymentTerms") || 30),
            notes: String(form.get("notes") || ""),
          };
          setData((state) => ({
            ...state,
            clients: current
              ? state.clients.map((item) => (item.id === current.id ? record : item))
              : [record, ...state.clients],
          }));
          closeAndToast(current ? "פרטי הלקוח עודכנו" : "לקוח חדש נוסף");
        }}
      >
        <Field label="שם מלא">
          <input name="name" defaultValue={current?.name} required autoFocus />
        </Field>
        <Field label="טלפון">
          <input name="phone" defaultValue={current?.phone} required />
        </Field>
        <Field label="דוא״ל">
          <input name="email" type="email" defaultValue={current?.email} />
        </Field>
        <Field label="עיר">
          <input name="city" defaultValue={current?.city} />
        </Field>
        <Field label="כתובת" full>
          <input name="address" defaultValue={current?.address} />
        </Field>
        <Field label="ח.פ / ת.ז">
          <input
            name="taxId"
            defaultValue={current?.taxId}
            inputMode="numeric"
          />
        </Field>
        <Field label="תנאי תשלום">
          <div className="input-suffix">
            <input
              name="paymentTerms"
              type="number"
              min="0"
              defaultValue={current?.paymentTerms ?? 30}
            />
            <span>ימים</span>
          </div>
        </Field>
        <Field label="סטטוס">
          <select name="status" defaultValue={current?.status || "פעיל"}>
            <option>פעיל</option>
            <option>חדש</option>
            <option>לא פעיל</option>
          </select>
        </Field>
        <Field label="אופן תקשורת מועדף">
          <select
            name="preferredContact"
            defaultValue={current?.preferredContact || "WhatsApp"}
          >
            <option>WhatsApp</option>
            <option>SMS</option>
            <option>טלפון</option>
            <option>דוא״ל</option>
          </select>
        </Field>
        <Field label="תאריך הצטרפות">
          <input
            name="joinDate"
            type="date"
            defaultValue={current?.joinDate || localDateISO()}
          />
        </Field>
        <Field label="הערות" full>
          <textarea name="notes" defaultValue={current?.notes} rows={4} />
        </Field>
      </form>
    );
  }

  if (modal.type === "pet") {
    const current = modal.id ? petById(modal.id) : undefined;
    title = current ? `עריכת ${current.name}` : "מטופל חדש";
    subtitle = "פרטים כלליים, שבב, רגישויות וחיסונים";
    body = (
      <form
        id="entity-form"
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: PetRecord = {
            id: current?.id || uid("p"),
            ownerId: String(form.get("ownerId") || ""),
            name: String(form.get("name") || ""),
            species: String(form.get("species") || ""),
            breed: String(form.get("breed") || ""),
            age: Number(form.get("age") || 0),
            sex: String(form.get("sex") || ""),
            chip: String(form.get("chip") || ""),
            weight: Number(form.get("weight") || 0),
            allergies: String(form.get("allergies") || "אין"),
            status: current?.status || "פעיל",
            lastVisit: current?.lastVisit || localDateISO(),
            nextVaccine: String(form.get("nextVaccine") || ""),
            notes: String(form.get("notes") || ""),
          };
          setData((state) => ({
            ...state,
            pets: current
              ? state.pets.map((item) => (item.id === current.id ? record : item))
              : [record, ...state.pets],
          }));
          closeAndToast(current ? "כרטיס המטופל עודכן" : "מטופל חדש נוסף");
        }}
      >
        <Field label="שם">
          <input name="name" defaultValue={current?.name} required autoFocus />
        </Field>
        <Field label="בעלים">
          <select name="ownerId" defaultValue={current?.ownerId || data.clients[0]?.id}>
            {data.clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name} · {client.phone}
              </option>
            ))}
          </select>
        </Field>
        <Field label="סוג">
          <select name="species" defaultValue={current?.species || "כלב"}>
            <option>כלב</option>
            <option>חתול</option>
            <option>ארנב</option>
            <option>ציפור</option>
            <option>אחר</option>
          </select>
        </Field>
        <Field label="גזע">
          <input name="breed" defaultValue={current?.breed} />
        </Field>
        <Field label="גיל">
          <input name="age" type="number" min="0" defaultValue={current?.age || 1} />
        </Field>
        <Field label="מין">
          <select name="sex" defaultValue={current?.sex || "זכר"}>
            <option>זכר</option>
            <option>נקבה</option>
          </select>
        </Field>
        <Field label="מספר שבב">
          <input name="chip" defaultValue={current?.chip} />
        </Field>
        <Field label="משקל (ק״ג)">
          <input
            name="weight"
            type="number"
            step="0.1"
            defaultValue={current?.weight}
          />
        </Field>
        <Field label="רגישויות / אלרגיות">
          <input name="allergies" defaultValue={current?.allergies || "אין"} />
        </Field>
        <Field label="חיסון הבא">
          <input
            name="nextVaccine"
            type="date"
            defaultValue={current?.nextVaccine || addDays(30)}
          />
        </Field>
        <Field label="הערות" full>
          <textarea name="notes" defaultValue={current?.notes} rows={4} />
        </Field>
      </form>
    );
  }

  if (modal.type === "appointment") {
    const current = modal.id
      ? data.appointments.find((item) => item.id === modal.id)
      : undefined;
    title = current ? "עריכת תור" : "קביעת תור חדש";
    subtitle = "בחרו מטופל, זמן, טיפול וחדר";
    body = (
      <form
        id="entity-form"
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const petId = String(form.get("petId") || "");
          const pet = petById(petId);
          if (!pet) return;
          const record: Appointment = {
            id: current?.id || uid("a"),
            date: String(form.get("date") || localDateISO()),
            time: String(form.get("time") || "09:00"),
            duration: Number(form.get("duration") || 30),
            type: String(form.get("type") || ""),
            petId,
            ownerId: pet.ownerId,
            doctor: String(form.get("doctor") || ""),
            room: String(form.get("room") || ""),
            status: String(form.get("status") || "ממתין") as AppointmentStatus,
            price: Number(form.get("price") || 0),
            notes: String(form.get("notes") || ""),
            reminderAt: String(form.get("reminderAt") || ""),
            reminderNote: String(form.get("reminderNote") || ""),
          };
          setData((state) => ({
            ...state,
            appointments: current
              ? state.appointments.map((item) =>
                  item.id === current.id ? record : item,
                )
              : [record, ...state.appointments],
          }));
          void syncReminder(
            "appointment",
            record.id,
            record.reminderAt || "",
            record.reminderNote || "",
          ).catch(() =>
            onToast("התור נשמר, אך לא ניתן היה לסנכרן את התזכורת"),
          );
          closeAndToast(current ? "התור עודכן" : "התור נקבע ביומן");
        }}
      >
        <Field label="מטופל">
          <select name="petId" defaultValue={current?.petId || data.pets[0]?.id}>
            {data.pets.map((pet) => (
              <option key={pet.id} value={pet.id}>
                {pet.name} · {clientById(pet.ownerId)?.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="סוג ביקור">
          <input
            name="type"
            defaultValue={current?.type || "בדיקה כללית"}
            required
          />
        </Field>
        <Field label="תאריך">
          <input
            name="date"
            type="date"
            defaultValue={current?.date || modal.date || localDateISO()}
          />
        </Field>
        <Field label="שעה">
          <input name="time" type="time" defaultValue={current?.time || "09:00"} />
        </Field>
        <Field label="משך">
          <div className="input-suffix">
            <input
              name="duration"
              type="number"
              defaultValue={current?.duration || 30}
            />
            <span>דקות</span>
          </div>
        </Field>
        <Field label="רופא">
          <select name="doctor" defaultValue={current?.doctor || data.clinic.defaultDoctor}>
            <option>{data.clinic.defaultDoctor}</option>
            <option>ד״ר רועי לוין</option>
          </select>
        </Field>
        <Field label="חדר">
          <select name="room" defaultValue={current?.room || "חדר 1"}>
            <option>חדר 1</option>
            <option>חדר 2</option>
            <option>מעבדה</option>
            <option>ניתוח</option>
          </select>
        </Field>
        <Field label="סטטוס">
          <select name="status" defaultValue={current?.status || "ממתין"}>
            {["ממתין", "הגיע", "בטיפול", "הושלם", "בוטל"].map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </Field>
        <Field label="מחיר">
          <div className="input-suffix">
            <input name="price" type="number" defaultValue={current?.price || 250} />
            <span>₪</span>
          </div>
        </Field>
        <Field
          label="תזכורת עתידית"
          hint="התזכורת תופיע במרכז ההתראות כשהמועד יגיע"
        >
          <input
            name="reminderAt"
            type="datetime-local"
            min={
              !current?.reminderAt ||
              current.reminderAt >= localDateTimeInput()
                ? localDateTimeInput()
                : undefined
            }
            defaultValue={current?.reminderAt || ""}
          />
        </Field>
        <Field label="הודעת תזכורת">
          <input
            name="reminderNote"
            defaultValue={current?.reminderNote || ""}
            placeholder="למשל: להתקשר לאישור הגעה"
            maxLength={500}
          />
        </Field>
        <Field label="הערות" full>
          <textarea name="notes" defaultValue={current?.notes} rows={4} />
        </Field>
      </form>
    );
  }

  if (modal.type === "record") {
    const current = modal.id
      ? data.records.find((item) => item.id === modal.id)
      : undefined;
    title = current ? "עריכת רשומה רפואית" : "רשומה רפואית חדשה";
    subtitle = "תיעוד הביקור, המשך המעקב ותמונות רפואיות";
    body = (
      <MedicalRecordEditor
        data={data}
        current={current}
        presetPetId={modal.petId}
        setData={setData}
        clientById={clientById}
        onToast={onToast}
        onSaved={(editing) =>
          closeAndToast(
            editing ? "הרשומה עודכנה" : "הרשומה נשמרה בתיק הרפואי",
          )
        }
      />
    );
  }

  if (modal.type === "invoice") {
    const current = modal.id
      ? data.invoices.find((item) => item.id === modal.id)
      : undefined;
    title = current ? `עריכת ${current.id}` : "הפקת מסמך חדש";
    subtitle = "לקוח, תור, פריטי חיוב, מע״מ ותנאי תשלום";
    body = (
      <InvoiceComposer
        data={data}
        current={current}
        presetClientId={modal.clientId}
        setData={setData}
        onToast={onToast}
        onSaved={(editing) =>
          closeAndToast(editing ? "המסמך עודכן" : "המסמך הופק ונשמר")
        }
      />
    );
  }

  if (modal.type === "inventory") {
    const current = modal.id
      ? data.inventory.find((item) => item.id === modal.id)
      : undefined;
    title = current ? "עריכת פריט מלאי" : "פריט מלאי חדש";
    subtitle = "כמות, מינימום, ספק ותאריך תוקף";
    body = (
      <form
        id="entity-form"
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: InventoryItem = {
            id: current?.id || uid("m"),
            name: String(form.get("name") || ""),
            category: String(form.get("category") || ""),
            stock: Number(form.get("stock") || 0),
            minStock: Number(form.get("minStock") || 0),
            unit: String(form.get("unit") || ""),
            expiry: String(form.get("expiry") || ""),
            supplier: String(form.get("supplier") || ""),
            price: Number(form.get("price") || 0),
          };
          setData((state) => ({
            ...state,
            inventory: current
              ? state.inventory.map((item) =>
                  item.id === current.id ? record : item,
                )
              : [record, ...state.inventory],
          }));
          closeAndToast(current ? "פריט המלאי עודכן" : "פריט חדש נוסף למלאי");
        }}
      >
        <Field label="שם הפריט">
          <input name="name" defaultValue={current?.name} required autoFocus />
        </Field>
        <Field label="קטגוריה">
          <input name="category" defaultValue={current?.category || "תרופות"} />
        </Field>
        <Field label="כמות במלאי">
          <input name="stock" type="number" defaultValue={current?.stock || 0} />
        </Field>
        <Field label="מלאי מינימום">
          <input
            name="minStock"
            type="number"
            defaultValue={current?.minStock || 5}
          />
        </Field>
        <Field label="יחידת מידה">
          <input name="unit" defaultValue={current?.unit || "יחידות"} />
        </Field>
        <Field label="תוקף">
          <input name="expiry" type="date" defaultValue={current?.expiry} />
        </Field>
        <Field label="ספק">
          <input name="supplier" defaultValue={current?.supplier} />
        </Field>
        <Field label="עלות ליחידה">
          <div className="input-suffix">
            <input
              name="price"
              type="number"
              step="0.1"
              defaultValue={current?.price || 0}
            />
            <span>₪</span>
          </div>
        </Field>
      </form>
    );
  }

  if (modal.type === "task") {
    const current = modal.id
      ? data.tasks.find((item) => item.id === modal.id)
      : undefined;
    title = current ? "עריכת משימה" : "משימה חדשה";
    subtitle = "תאריך יעד, עדיפות ואחריות";
    body = (
      <form
        id="entity-form"
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const record: ClinicTask = {
            id: current?.id || uid("t"),
            text: String(form.get("text") || ""),
            dueDate: String(form.get("dueDate") || localDateISO()),
            priority: String(form.get("priority") || "רגילה") as ClinicTask["priority"],
            owner: String(form.get("owner") || ""),
            status: String(form.get("status") || "פתוחה") as TaskStatus,
            petId: String(form.get("petId") || ""),
            reminderAt: String(form.get("reminderAt") || ""),
            reminderNote: String(form.get("reminderNote") || ""),
          };
          setData((state) => ({
            ...state,
            tasks: current
              ? state.tasks.map((item) => (item.id === current.id ? record : item))
              : [record, ...state.tasks],
          }));
          void syncReminder(
            "task",
            record.id,
            record.reminderAt || "",
            record.reminderNote || "",
          ).catch(() =>
            onToast("המשימה נשמרה, אך לא ניתן היה לסנכרן את התזכורת"),
          );
          closeAndToast(current ? "המשימה עודכנה" : "משימה חדשה נוספה");
        }}
      >
        <Field label="משימה" full>
          <input name="text" defaultValue={current?.text} required autoFocus />
        </Field>
        <Field label="תאריך יעד">
          <input
            name="dueDate"
            type="date"
            defaultValue={current?.dueDate || localDateISO()}
          />
        </Field>
        <Field label="עדיפות">
          <select name="priority" defaultValue={current?.priority || "רגילה"}>
            <option>דחופה</option>
            <option>גבוהה</option>
            <option>רגילה</option>
            <option>נמוכה</option>
          </select>
        </Field>
        <Field label="אחראי">
          <input name="owner" defaultValue={current?.owner || "מזכירות"} />
        </Field>
        <Field label="סטטוס">
          <select name="status" defaultValue={current?.status || "פתוחה"}>
            <option>פתוחה</option>
            <option>בוצעה</option>
          </select>
        </Field>
        <Field
          label="תזכורת עתידית"
          hint="התזכורת תופיע במרכז ההתראות כשהמועד יגיע"
        >
          <input
            name="reminderAt"
            type="datetime-local"
            min={
              !current?.reminderAt ||
              current.reminderAt >= localDateTimeInput()
                ? localDateTimeInput()
                : undefined
            }
            defaultValue={current?.reminderAt || ""}
          />
        </Field>
        <Field label="הודעת תזכורת">
          <input
            name="reminderNote"
            defaultValue={current?.reminderNote || ""}
            placeholder="למשל: לעדכן את מנהלת המרפאה"
            maxLength={500}
          />
        </Field>
        <Field label="מטופל משויך" full>
          <select name="petId" defaultValue={current?.petId || ""}>
            <option value="">ללא שיוך</option>
            {data.pets.map((pet) => (
              <option key={pet.id} value={pet.id}>
                {pet.name} · {clientById(pet.ownerId)?.name}
              </option>
            ))}
          </select>
        </Field>
      </form>
    );
  }

  if (modal.type === "client-detail") {
    const client = clientById(modal.id);
    if (!client) return null;
    const pets = data.pets.filter((pet) => pet.ownerId === client.id);
    const invoices = data.invoices.filter(
      (invoice) => invoice.clientId === client.id,
    );
    const debt = invoices.reduce(
      (sum, invoice) => sum + invoiceBalance(invoice),
      0,
    );
    title = client.name;
    subtitle = `${client.phone} · ${client.email}`;
    body = (
      <div className="detail-stack">
        <div className="detail-hero">
          <span className="detail-avatar">{client.name.slice(0, 2)}</span>
          <div>
            <Pill tone={statusTone[client.status]}>{client.status}</Pill>
            <h3>{client.name}</h3>
            <p>
              {client.address}, {client.city}
            </p>
          </div>
          <div className="detail-hero-actions">
            <button
              className="button button-primary"
              onClick={() =>
                onEdit({ type: "invoice", clientId: client.id })
              }
            >
              ＋ הפקת חשבונית
            </button>
            <button
              className="button button-secondary"
              onClick={() => onEdit({ type: "client", id: client.id })}
            >
              עריכת פרטים
            </button>
          </div>
        </div>
        <div className="detail-kpis">
          <div>
            <span>מטופלים</span>
            <strong>{pets.length}</strong>
          </div>
          <div>
            <span>יתרה פתוחה</span>
            <strong className={debt ? "text-danger" : ""}>{money(debt)}</strong>
          </div>
          <div>
            <span>תקשורת מועדפת</span>
            <strong>{client.preferredContact}</strong>
          </div>
        </div>
        <section className="detail-section">
          <h4>בעלי חיים</h4>
          <div className="detail-list">
            {pets.map((pet) => (
              <button
                key={pet.id}
                onClick={() => onEdit({ type: "pet-detail", id: pet.id })}
              >
                <span className="pet-avatar">{pet.name.slice(0, 1)}</span>
                <div>
                  <strong>{pet.name}</strong>
                  <small>
                    {pet.species} · {pet.breed}
                  </small>
                </div>
                <b>←</b>
              </button>
            ))}
          </div>
        </section>
        <section className="detail-section">
          <h4>חיובים אחרונים</h4>
          <div className="detail-list">
            {invoices.map((invoice) => (
              <button
                key={invoice.id}
                onClick={() => onEdit({ type: "invoice-detail", id: invoice.id })}
              >
                <span className="invoice-mini">₪</span>
                <div>
                  <strong>{invoice.id}</strong>
                  <small>{friendlyDate(invoice.date)}</small>
                </div>
                <Pill tone={statusTone[invoice.status]}>{money(invoiceTotal(invoice))}</Pill>
              </button>
            ))}
          </div>
        </section>
        {client.notes ? <div className="detail-note">{client.notes}</div> : null}
      </div>
    );
  }

  if (modal.type === "pet-detail") {
    const pet = petById(modal.id);
    if (!pet) return null;
    const owner = clientById(pet.ownerId);
    const records = data.records
      .filter((record) => record.petId === pet.id)
      .sort((a, b) => b.date.localeCompare(a.date));
    title = `תיק רפואי · ${pet.name}`;
    subtitle = `${pet.species} · ${pet.breed} · ${owner?.name}`;
    body = (
      <div className="detail-stack">
        <div className="pet-detail-hero">
          <span className="large-pet-avatar">{pet.name.slice(0, 1)}</span>
          <div>
            <Pill tone={pet.nextVaccine < localDateISO() ? "danger" : "success"}>
              {pet.nextVaccine < localDateISO() ? "חיסון באיחור" : "מעקב פעיל"}
            </Pill>
            <h3>{pet.name}</h3>
            <p>
              {pet.sex} · {pet.age} שנים · {pet.weight} ק״ג
            </p>
          </div>
          <button
            className="button button-secondary"
            onClick={() => onEdit({ type: "pet", id: pet.id })}
          >
            עריכת כרטיס
          </button>
        </div>
        <div className="detail-kpis detail-kpis-four">
          <div>
            <span>בעלים</span>
            <strong>{owner?.name}</strong>
          </div>
          <div>
            <span>שבב</span>
            <strong>{pet.chip || "ללא"}</strong>
          </div>
          <div>
            <span>חיסון הבא</span>
            <strong>{friendlyDate(pet.nextVaccine)}</strong>
          </div>
          <div>
            <span>רגישויות</span>
            <strong className={pet.allergies !== "אין" ? "text-danger" : ""}>
              {pet.allergies}
            </strong>
          </div>
        </div>
        <section className="detail-section">
          <div className="section-title-row">
            <h4>היסטוריה רפואית</h4>
            <button
              className="button button-small button-primary"
              onClick={() => onEdit({ type: "record", petId: pet.id })}
            >
              ＋ רשומה
            </button>
          </div>
          <div className="medical-timeline">
            {records.map((record) => (
              <div key={record.id}>
                <span />
                <section>
                  <div>
                    <Pill tone="purple">{record.type}</Pill>
                    <small>{friendlyDate(record.date)}</small>
                  </div>
                  <strong>{record.title}</strong>
                  <p>{record.diagnosis}</p>
                   <small>
                     {record.treatment} · {record.doctor}
                   </small>
                   {record.attachments.length ? (
                     <div className="timeline-attachments">
                       {record.attachments.slice(0, 4).map((attachment) => (
                         <a
                           href={attachment.url}
                           target="_blank"
                           rel="noreferrer"
                           key={attachment.id}
                           title={attachment.name}
                         >
                           <MedicalThumbnail attachment={attachment} />
                         </a>
                       ))}
                       <span>{record.attachments.length} תמונות</span>
                     </div>
                   ) : null}
                 </section>
              </div>
            ))}
            {!records.length ? (
              <EmptyState
                title="אין עדיין רשומות"
                text="הוסיפו את הביקור הראשון לתיק הרפואי."
              />
            ) : null}
          </div>
        </section>
        {pet.notes ? <div className="detail-note">{pet.notes}</div> : null}
      </div>
    );
  }

  if (modal.type === "invoice-detail") {
    const invoice = data.invoices.find((item) => item.id === modal.id);
    if (!invoice) return null;
    const client = clientById(invoice.clientId);
    const displayStatus = invoiceDisplayStatus(invoice);
    const recipientName = invoice.clientName || client?.name || "";
    const setInvoiceStatus = (status: InvoiceStatus) => {
      setData((state) => ({
        ...state,
        invoices: state.invoices.map((item) =>
          item.id === invoice.id
            ? {
                ...item,
                status,
                paid:
                  status === "שולמה" ? invoiceTotal(item) : item.paid,
                paidDate: status === "שולמה" ? localDateISO() : item.paidDate,
                paymentMethod:
                  status === "שולמה"
                    ? item.paymentMethod || "אשראי"
                    : item.paymentMethod,
              }
            : item,
        ),
      }));
      onToast(
        status === "שולמה"
          ? "המסמך סומן כשולם"
          : status === "מבוטלת"
            ? "המסמך בוטל ונשמר בהיסטוריה"
            : "המסמך סומן כנשלח",
      );
    };
    const duplicate = () => {
      setData((state) => {
        const nextInvoice = nextInvoiceIdentity(
          state.clinic,
          state.invoices,
        );
        const duplicateInvoice: Invoice = {
          ...invoice,
          id: nextInvoice.id,
          date: localDateISO(),
          dueDate: addDaysFrom(
            localDateISO(),
            client?.paymentTerms ?? 30,
          ),
          paidDate: "",
          status: "טיוטה",
          paid: 0,
          notes: invoice.notes
            ? `${invoice.notes} — שוכפל ממסמך ${invoice.id}`
            : `שוכפל ממסמך ${invoice.id}`,
          items: invoice.items.map((item) => ({
            ...item,
            id: uid("line"),
          })),
        };
        return {
          ...state,
          clinic: {
            ...state.clinic,
            nextInvoiceNumber: nextInvoice.nextNumber,
          },
          invoices: [duplicateInvoice, ...state.invoices],
        };
      });
      closeAndToast("עותק חדש נשמר כטיוטה");
    };
    const reminderText = `שלום ${recipientName},

תזכורת לתשלום עבור ${invoice.documentType} ${invoice.id} בסך ${money(
      invoiceTotal(invoice),
    )}.
תאריך לתשלום: ${friendlyDate(invoice.dueDate)}.

פרטי תשלום: ${data.clinic.bank}

תודה,
${data.clinic.name}`;
    title = `${invoice.documentType} ${invoice.id}`;
    subtitle = `${recipientName} · ${friendlyDate(invoice.date)}`;
    body = (
      <div className="invoice-detail-stack">
        <div className="invoice-document-actions">
          <button
            className="button invoice-print-button"
            onClick={() => {
              const opened = printInvoiceDocument(
                `invoice-print-${invoice.id}`,
                `${invoice.documentType} ${invoice.id}`,
              );
              if (!opened) {
                onToast("לא ניתן לפתוח את ההדפסה בדפדפן זה");
              }
            }}
          >
            ⎙ הדפסה / שמירה כ־PDF
          </button>
          {invoice.status !== "שולמה" && invoice.status !== "מבוטלת" ? (
            <button
              className="button button-primary"
              onClick={() => setInvoiceStatus("שולמה")}
            >
              ✓ סמן כשולם
            </button>
          ) : null}
          {invoice.status === "טיוטה" ? (
            <button
              className="button button-secondary"
              onClick={() => setInvoiceStatus("נשלחה")}
            >
              ↗ סמן כנשלחה
            </button>
          ) : null}
          <button
            className="button button-secondary"
            onClick={async () => {
              await navigator.clipboard?.writeText(reminderText);
              onToast("נוסח תזכורת התשלום הועתק");
            }}
          >
            ◇ העתק תזכורת
          </button>
          <button className="button button-secondary" onClick={duplicate}>
            ⧉ שכפול
          </button>
          <button
            className="button button-secondary"
            onClick={() => onEdit({ type: "invoice", id: invoice.id })}
          >
            עריכה
          </button>
          {invoice.status !== "מבוטלת" ? (
            <button
              className="button invoice-cancel-button"
              onClick={() => {
                if (window.confirm("לבטל את המסמך ולשמור אותו בהיסטוריה?")) {
                  setInvoiceStatus("מבוטלת");
                }
              }}
            >
              ביטול מסמך
            </button>
          ) : null}
        </div>
        <div
          className="invoice-preview invoice-document"
          id={`invoice-print-${invoice.id}`}
        >
          {invoice.status === "שולמה" ? (
            <span className="invoice-stamp invoice-stamp-paid">שולם</span>
          ) : null}
          {invoice.status === "מבוטלת" ? (
            <span className="invoice-stamp invoice-stamp-cancelled">מבוטל</span>
          ) : null}
          <div className="invoice-preview-head">
            <div className="invoice-document-title">
              <span className="brand-mark">V+</span>
              <div>
                <small>מסמך מספר {invoice.id}</small>
                <strong>{invoice.documentType}</strong>
              </div>
            </div>
            <div className="invoice-business">
              <strong>{data.clinic.name}</strong>
              <small>עוסק / ח.פ: {data.clinic.taxId || "—"}</small>
              <small>{data.clinic.address}</small>
              <small>
                {data.clinic.phone} · {data.clinic.email}
              </small>
            </div>
          </div>
          <div className="invoice-document-status">
            <Pill tone={statusTone[displayStatus]}>{displayStatus}</Pill>
          </div>
          <div className="invoice-meta invoice-document-meta">
            <div>
              <span>לכבוד</span>
              <strong>{recipientName}</strong>
              <small>
                {invoice.clientTaxId
                  ? `ח.פ / ת.ז: ${invoice.clientTaxId}`
                  : "ללא ח.פ / ת.ז"}
              </small>
              <small>{invoice.clientAddress || client?.address}</small>
              <small>{invoice.clientEmail || client?.email}</small>
            </div>
            <div>
              <span>פרטי המסמך</span>
              <strong>הופק: {friendlyDate(invoice.date)}</strong>
              <small>לתשלום עד: {friendlyDate(invoice.dueDate)}</small>
              <small>
                אמצעי תשלום: {invoice.paymentMethod || "טרם נבחר"}
              </small>
              {invoice.paidDate ? (
                <small>שולם בתאריך: {friendlyDate(invoice.paidDate)}</small>
              ) : null}
            </div>
          </div>
          <div className="invoice-lines">
            <div>
              <strong>תיאור</strong>
              <strong>כמות</strong>
              <strong>מחיר יחידה</strong>
              <strong>סה״כ</strong>
            </div>
            {invoice.items.map((item, index) => (
              <div key={item.id || index}>
                <span>{item.desc}</span>
                <span>{item.qty}</span>
                <span>{money(item.price)}</span>
                <strong>{money(item.qty * item.price)}</strong>
              </div>
            ))}
          </div>
          <div className="invoice-totals invoice-document-totals">
            <div>
              <span>סכום לפני מע״מ</span>
              <strong>{money(invoiceSubtotal(invoice))}</strong>
            </div>
            <div>
              <span>
                מע״מ ({invoice.vatExempt ? 0 : invoice.vatRate * 100}%)
              </span>
              <strong>{money(invoiceVat(invoice))}</strong>
            </div>
            <div className="invoice-total-line">
              <span>סה״כ לתשלום</span>
              <strong>{money(invoiceTotal(invoice))}</strong>
            </div>
            <div>
              <span>שולם</span>
              <strong>{money(invoice.paid)}</strong>
            </div>
            <div>
              <span>יתרה</span>
              <strong
                className={invoiceBalance(invoice) ? "text-danger" : ""}
              >
                {money(invoiceBalance(invoice))}
              </strong>
            </div>
          </div>
          {invoice.notes ? (
            <div className="invoice-document-notes">
              <strong>הערות</strong>
              <p>{invoice.notes}</p>
            </div>
          ) : null}
          <footer className="invoice-document-footer">
            <strong>פרטי תשלום: {data.clinic.bank || "לא הוגדרו"}</strong>
            <span>
              לשאלות לגבי המסמך: {data.clinic.email} · {data.clinic.phone}
            </span>
            <small>
              המסמך הופק במערכת VetManage Pro ונשמר בנתוני המרפאה.
            </small>
          </footer>
        </div>
      </div>
    );
  }

  const isDetail = modal.type.endsWith("-detail");
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section
        className={`modal ${
          isDetail || modal.type === "invoice" || modal.type === "record"
            ? "modal-wide"
            : ""
        } ${
          modal.type === "invoice"
            ? "invoice-composer-modal"
            : modal.type === "invoice-detail"
              ? "invoice-document-modal"
              : modal.type === "record"
                ? "medical-record-modal"
              : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">{subtitle}</span>
            <h2 id="modal-title">{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="סגירה">
            ×
          </button>
        </header>
        <div className="modal-body">{body}</div>
        <footer className="modal-footer">
          {isDetail ? (
            <>
              {modal.type === "invoice-detail" ? (
                <button
                  className="button button-secondary"
                  onClick={() => {
                    const opened = printInvoiceDocument(
                      `invoice-print-${modal.id}`,
                      `מסמך ${modal.id}`,
                    );
                    if (!opened) {
                      onToast("לא ניתן לפתוח את ההדפסה בדפדפן זה");
                    }
                  }}
                >
                  ⎙ הדפסה / PDF
                </button>
              ) : null}
              <button className="button button-primary" onClick={onClose}>
                סגירה
              </button>
            </>
          ) : (
            <>
              <button className="button button-secondary" onClick={onClose}>
                ביטול
              </button>
              <button className="button button-primary" type="submit" form="entity-form">
                {modal.type === "invoice" ? "הפקת מסמך" : "שמירה"}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>
  );
}

function SearchOverlay({
  query,
  setQuery,
  results,
  onClose,
  onSelect,
}: {
  query: string;
  setQuery: (query: string) => void;
  results: Array<{
    id: string;
    type: string;
    title: string;
    subtitle: string;
    page: Page;
    modal?: ModalState;
  }>;
  onClose: () => void;
  onSelect: (result: {
    id: string;
    type: string;
    title: string;
    subtitle: string;
    page: Page;
    modal?: ModalState;
  }) => void;
}) {
  return (
    <div className="search-overlay" onMouseDown={onClose}>
      <section className="command-menu" onMouseDown={(event) => event.stopPropagation()}>
        <div className="command-input">
          <span>⌕</span>
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="חיפוש לקוח, מטופל, טיפול או חשבונית..."
            aria-label="חיפוש גלובלי"
          />
          <kbd>ESC</kbd>
        </div>
        <div className="command-results">
          {!query ? (
            <div className="search-suggestions">
              <span>חיפושים מומלצים</span>
              <div>
                {["חיסון", "רקס", "חוב פתוח", "כהן"].map((item) => (
                  <button key={item} onClick={() => setQuery(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ) : results.length ? (
            results.map((result) => (
              <button key={`${result.type}-${result.id}`} onClick={() => onSelect(result)}>
                <span className="result-type">{result.type.slice(0, 1)}</span>
                <span>
                  <strong>{result.title}</strong>
                  <small>{result.subtitle}</small>
                </span>
                <Pill tone="neutral">{result.type}</Pill>
                <b>←</b>
              </button>
            ))
          ) : (
            <EmptyState
              icon="⌕"
              title="לא נמצאו תוצאות"
              text="נסו שם, מספר טלפון, שבב או מספר חשבונית."
            />
          )}
        </div>
        <div className="command-footer">
          <span>↵ לפתיחה</span>
          <span>ESC לסגירה</span>
        </div>
      </section>
    </div>
  );
}

function NotificationDrawer({
  open,
  alerts,
  onClose,
  onNavigate,
}: {
  open: boolean;
  alerts: Array<{
    id: string;
    tone: string;
    title: string;
    text: string;
    page: Page;
  }>;
  onClose: () => void;
  onNavigate: (page: Page) => void;
}) {
  return (
    <>
      {open ? <button className="drawer-scrim" onClick={onClose} aria-label="סגירה" /> : null}
      <aside className={`notification-drawer ${open ? "drawer-open" : ""}`}>
        <header>
          <div>
            <span className="eyebrow">מרכז תשומת לב</span>
            <h2>התראות</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="סגירה">
            ×
          </button>
        </header>
        <div className="drawer-summary">
          <div>
            <strong>{alerts.length}</strong>
            <span>פתוחות</span>
          </div>
          <div>
            <strong>{alerts.filter((alert) => alert.tone === "danger").length}</strong>
            <span>דחופות</span>
          </div>
        </div>
        <div className="drawer-alerts">
          {alerts.map((alert) => (
            <button
              key={alert.id}
              onClick={() => {
                onNavigate(alert.page);
                onClose();
              }}
            >
              <span className={`alert-mark alert-${alert.tone}`}>!</span>
              <span>
                <strong>{alert.title}</strong>
                <small>{alert.text}</small>
              </span>
              <b>←</b>
            </button>
          ))}
          {!alerts.length ? (
            <EmptyState title="הכול שקט" text="אין התראות שדורשות טיפול." />
          ) : null}
        </div>
      </aside>
    </>
  );
}
